import { Role } from '@/Enums/Users/Role.enum';
import {
  type ExportSection,
  buildSpreadsheetXml,
  buildStackedCsv,
  downloadFile,
} from '@/pages/ReportingV2/lib/reportExport';
import { useAppSelector } from '@/store/hooks';
import { selectIsSuperAdmin } from '@/store/selectors/me.selectors';
import { postReport } from '@/store/slices/reportingV2/reportingV2.api';
import type { ReportRow } from '@/store/slices/reportingV2/reportingV2.api';
import {
  AddRounded,
  CloseRounded,
  ContentCopyRounded,
  DragIndicatorRounded,
  EditOutlined,
  ExpandLessRounded,
  ExpandMoreRounded,
  FileDownloadOutlined,
  FullscreenRounded,
  MoreHorizRounded,
  PrintOutlined,
  RefreshRounded,
  SettingsRounded,
  ShareRounded,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  FormControlLabel,
  IconButton,
  Menu,
  MenuItem,
  Tooltip as MuiTooltip,
  Paper,
  Select,
  Skeleton,
  Snackbar,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Tooltip,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { type ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';
import { DragDropContext, Draggable, type DropResult, Droppable } from 'react-beautiful-dnd';
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import CityChoropleth from '../../ReportingV2/components/CityChoropleth';
import DmaChoropleth from '../../ReportingV2/components/DmaChoropleth';
import MultiSelectFilter from '../../ReportingV2/components/MultiSelectFilter';
import StateChoropleth from '../../ReportingV2/components/StateChoropleth';
import ZipChoropleth from '../../ReportingV2/components/ZipChoropleth';
import { genericChannelLabel, num, sharePercent } from '../../ReportingV2/format';
import type { OptionsScope } from '../../ReportingV2/hooks/optionsBatcher';
import useFilterOptions, { labelCache } from '../../ReportingV2/hooks/useFilterOptions';
import {
  type CustomDashboard,
  clearTenantFilterDefaults,
  getDashboard,
  getTenantFilterDefaults,
  setTenantFilterDefaults,
  updateDashboard,
} from '../api';
import {
  CANONICAL_METRICS,
  METRIC_AVAILABILITY_LABELS,
  getMetricDef,
  isChannelSpecificMetric,
  isConversionMetric,
  isMetricSupported,
  isNonAdditiveMetric,
  isReachMetric,
} from '../lib/canonicalMetrics';
import {
  type AxisOverrides,
  MAX_CHART_METRICS,
  buildScales,
  buildSeries,
  normalizeSeriesValues,
  resolveChartMetrics,
  tooltipLabelCallback,
} from '../lib/chartSeries';
import {
  CONVERSION_TYPE_LABELS,
  type ConversionType,
  availableConversionTypes,
  buildConversionsByGroup,
  conversionsLabel,
  isConversionTypeSplittable,
  resolveConversionType,
  sumConversionsForType,
} from '../lib/conversionType';
import {
  type DashboardBranding,
  type DashboardFilters,
  type FilterBarConfig,
  type MetricSuppression,
  SUPPRESSION_NOTES,
  type SavedView,
  type WidgetFilterKey,
  type WidgetFilters,
  applyDashboardFilters,
  availableWidgetFilterKeys,
  buildConfig,
  conversionsVisible,
  defaultDashboardFilters,
  defaultFilterBarConfig,
  enabledFilterKeys,
  filtersSignature,
  inheritedCount,
  metricSuppression,
  migrateEntityFilters,
  migrateProductDimension,
  migrateProductFilter,
  overrideCount,
  priorRange,
  priorYearRange,
  reachVisible,
  readActiveViewId,
  readBranding,
  readSavedViews,
  refreshRelativeRange,
  resolveFilterBarConfig,
  resolveWidgetFilters,
  splitLayout,
  stripDeniedFilterConditions,
  vanityMetricsVisible,
  widgetEntityScope,
  withEntityCascade,
  withWidgetOverride,
} from '../lib/dashboardFilters';
import {
  comparisonWindowEnd,
  dataThroughLabel,
  recordFreshness,
  useDashboardFreshness,
} from '../lib/dashboardFreshness';
import {
  type DashboardPage,
  addPage,
  backfillWidgetPages,
  readPages,
  removePage,
  renamePage,
  reorderWithinPage,
  widgetsForPage,
  writePages,
} from '../lib/dashboardPages';
import {
  isPermissionDeniedMetric,
  maskPermissionDeniedValues,
  readMetricValue,
  recordRestrictedFields,
  stripDeniedWidgetFields,
  useDashboardPermissions,
} from '../lib/dashboardPermissions';
import { dimensionUnavailableDetail } from '../lib/dimensionAvailability';
import { formatMetricValue, metricLabel, notApplicableReason } from '../lib/metricFormat';
import { type PptxChartImage, type WidgetExportSnapshot, buildDashboardPptx, pptxFileName } from '../lib/pptxExport';
import { computeTotalsRow, maskNeverTotalValues } from '../lib/tableTotals';
import {
  type CalculatedMetricDef,
  EMPTY_TENANT_FIELDS,
  KNOWN_METRIC_KEYS,
  type TenantFields,
  aggregateByCustomDimension,
  applyTenantFields,
  mergeMetricOptions,
  readTenantFields,
  validateFormula,
  writeTenantFields,
} from '../lib/tenantFields';
import {
  ROW_SPAN_OPTIONS,
  type RowSpan,
  clampRowSpan,
  nextRowSpanFromDrag,
  widgetBodyMinHeight,
  widgetChartHeight,
  widgetMapHeight,
} from '../lib/widgetSizing';
import ConversionBreakdownModal from './ConversionBreakdownModal';
import DashboardExportDialog, { type DashboardExportFormat } from './DashboardExportDialog';
import DashboardFilterBar from './DashboardFilterBar';
import RenameDashboardDialog from './RenameDashboardDialog';
import TenantFieldsDialog from './TenantFieldsDialog';

ChartJS.register(
  CategoryScale,
  LinearScale,
  LineElement,
  BarElement,
  ArcElement,
  PointElement,
  Filler,
  Legend,
  Tooltip,
  ChartDataLabels,
);

// Slice colours for the Donut widget (composition by dimension).
const DONUT_PALETTE = ['#3b82f6', '#f59e0b', '#10b981', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316'];

// ─── widget catalogue (matches Lovable exactly) ──────────────────────────────

// Categories mirror the Lovable "Add a widget" drawer groupings.
const WIDGET_CATEGORIES = [
  'KPI & Scorecards',
  'Performance',
  'Charts',
  'Tables & Pivots',
  'Geo',
  'Layout & Media',
] as const;

type WidgetCategory = (typeof WIDGET_CATEGORIES)[number];

const WIDGET_TYPES: Array<{
  type: string;
  label: string;
  description: string;
  category: WidgetCategory;
}> = [
  {
    type: 'kpi',
    label: 'KPI Card',
    description: 'A single big number with optional change vs prior period.',
    category: 'KPI & Scorecards',
  },
  {
    type: 'scorecard',
    label: 'Scorecard Grid',
    description: 'N-up KPI cards, one per metric, sharing the same filters.',
    category: 'KPI & Scorecards',
  },
  {
    type: 'pacing',
    label: 'Pacing',
    description: 'Actual vs. expected delivery for a metric over the window.',
    category: 'Performance',
  },
  {
    type: 'goal',
    label: 'Goal Tracker',
    description: 'Circular progress ring showing actual vs. a target.',
    category: 'Performance',
  },
  {
    type: 'funnel',
    label: 'Funnel',
    description: 'Ordered metrics with per-stage conversion rates.',
    category: 'Performance',
  },
  {
    type: 'line',
    label: 'Line Chart',
    description: 'Metric trend over time. Add up to 3 metrics to compare series.',
    category: 'Charts',
  },
  {
    type: 'bar',
    label: 'Bar Chart',
    description: 'Top-N breakdown of one or more metrics by a dimension.',
    category: 'Charts',
  },
  {
    type: 'area',
    label: 'Area Chart',
    description: 'Stacked area over time, optional breakdown.',
    category: 'Charts',
  },
  {
    type: 'donut',
    label: 'Donut Chart',
    description: 'Composition by dimension.',
    category: 'Charts',
  },
  {
    type: 'table',
    label: 'Data Table',
    description: 'Rows per dimension with multiple metric columns.',
    category: 'Tables & Pivots',
  },
  {
    type: 'pivot',
    label: 'Pivot Table',
    description: 'Row × column heatmap for a single metric.',
    category: 'Tables & Pivots',
  },
  {
    type: 'geo',
    label: 'Geo Map',
    description: 'Choropleth map at state / market level.',
    category: 'Geo',
  },
  {
    type: 'text',
    label: 'Text Block',
    description: 'Static markdown — notes, context, dividers.',
    category: 'Layout & Media',
  },
  {
    type: 'image',
    label: 'Image / Logo',
    description: 'Upload an image — client logo, divider, screenshot.',
    category: 'Layout & Media',
  },
  {
    type: 'divider',
    label: 'Divider',
    description: 'A horizontal rule, with an optional label, to break up dashboard sections.',
    category: 'Layout & Media',
  },
  {
    type: 'callout',
    label: 'Callout',
    description: 'A highlighted box for commentary or an insight — not tied to any metric.',
    category: 'Layout & Media',
  },
];

// Canonical metric catalog drives the pickers (value = api key, label = display).
const METRIC_OPTIONS = CANONICAL_METRICS.map(m => ({ value: m.key, label: m.label }));

// Legacy dashboards stored friendly metric LABELS ("Impressions"); the catalog
// uses keys ("impressions"). Accept either so saved configs keep working.
const LABEL_TO_KEY: Record<string, string> = Object.fromEntries(CANONICAL_METRICS.map(m => [m.label, m.key]));
const toMetricKey = (m: string): string => LABEL_TO_KEY[m] ?? m;

// Metric picker <MenuItem>: badges non-GA metrics and disables any not yet wired
// to postReport, so users can't build a widget that will render empty.
const renderMetricOption = (
  opt: { value: string; label: string; custom?: boolean; formula?: string },
  extraDisabled = false,
) => {
  const def = getMetricDef(opt.value);
  // A tenant metric has no catalog entry — it is computed here from canonical
  // metrics, so it stands or falls on what its formula reads. Checking the
  // formula keeps the existing promise of this picker: never offer a metric
  // that would render empty.
  if (opt.custom) {
    const refs = opt.formula ? validateFormula(opt.formula).references : [];
    const blocked = refs.filter(ref => !isMetricSupported(ref));
    return (
      <MenuItem key={opt.value} value={opt.value} disabled={extraDisabled || blocked.length > 0}>
        {opt.label}
        {blocked.length ? ` — needs ${blocked.join(', ')}` : ''}
      </MenuItem>
    );
  }
  const availLabel = def?.availability && def.availability !== 'ga' ? METRIC_AVAILABILITY_LABELS[def.availability] : '';
  const unsupported = !isMetricSupported(opt.value);
  const suffix = availLabel ? ` — ${availLabel}` : unsupported ? ' — not available' : '';
  return (
    <MenuItem key={opt.value} value={opt.value} disabled={extraDisabled || unsupported}>
      {opt.label}
      {suffix}
    </MenuItem>
  );
};

// Widget types that issue a report query (and therefore honour filters).
const WIDGET_DATA_TYPES = new Set([
  'kpi',
  'line',
  'area',
  'bar',
  'donut',
  'table',
  'scorecard',
  'goal',
  'funnel',
  'pacing',
  'geo',
  'pivot',
]);

// Sensible starting metrics per totals widget.
const DEFAULT_TOTALS_METRICS: Record<string, string[]> = {
  scorecard: ['impressions', 'clicks', 'spend', 'ctr'],
  funnel: ['impressions', 'clicks', 'conversions'],
};

// Widgets whose data is a single row of totals for N metrics (no dimensions).
const WIDGET_TOTALS_TYPES = new Set(['scorecard', 'goal', 'funnel', 'pacing']);

// Widgets that display exactly one metric (widget.metric), as opposed to a list.
const SINGLE_METRIC_WIDGETS = new Set(['kpi', 'donut', 'geo', 'pivot']);

// Default titles describe what the widget shows rather than naming the chart
// type, so a freshly built dashboard doesn't read as a pile of placeholders.
// Sentence case matches the flank-20 design source.
const DEFAULT_WIDGET_TITLES: Record<string, string> = {
  kpi: 'Impressions',
  scorecard: 'Headline KPIs',
  pacing: 'Spend pacing',
  goal: 'Goal tracker',
  funnel: 'Conversion funnel',
  line: 'Delivery over time',
  area: 'Delivery over time',
  bar: 'Top channels',
  donut: 'Mix by channel',
  table: 'Performance table',
  pivot: 'Channel × Product',
  geo: 'Delivery by state',
  text: 'Notes',
  image: 'Logo',
  divider: 'Section',
  callout: 'Insight',
};

// Text Block size/weight presets (#4433) — covers the ticket's page
// title/section header/subheader/body/caption list without a rich-text
// toolbar. A Divider's optional label reuses 'subheader'.
const TEXT_STYLE_VARIANTS: Record<
  NonNullable<WidgetConfig['textStyle']>,
  'h5' | 'h6' | 'subtitle1' | 'body2' | 'caption'
> = {
  title: 'h5',
  heading: 'h6',
  subheader: 'subtitle1',
  body: 'body2',
  caption: 'caption',
};

// Group-by dimensions for the Table widget (friendly label → canonical dimension key).
const TABLE_DIMENSIONS: Record<string, string> = {
  DSP: 'dsp',
  Channel: 'channel',
  // Product removed (flank#5100) — delivery not launched through Flank has
  // no product, so grouping by it puts the bulk of delivery under
  // "Unmapped" (87% on one account). Unified Reporting dropped the same
  // breakdown for the same reason (#4109).
  Device: 'device',
  Advertiser: 'advertiser_name',
  Campaign: 'campaign_name',
  'Line Item': 'line_item_name',
  Day: 'day',
  // Bonneville asked to group by these and there was no way to select them
  // here at all — only Unified Reporting offered them (Earl, FB-003/FB-006).
  //
  // Daypart is carried on every canonical fact table, so it combines with any
  // filter. ZIP is not: geography lives only on the GEOGRAPHY fact, which has
  // no Device or Publisher column, so those two filters cannot be applied to a
  // ZIP widget. The API reports that back as `ignored_filters` and the widget
  // says so rather than showing an unfiltered number as though it were
  // filtered.
  // Labelled, so a row reads "Primetime (7 PM - 11 PM)". Earl couldn't find the
  // definitions here: the Unified Reporting tooltip doesn't exist in Custom
  // Dashboards, and FB-003 asks for them when BUILDING reports as well as
  // reading them — including in exports and PDFs, where a tooltip can't reach.
  Daypart: 'daypart_label',
  ZIP: 'geo_zip_label',
  // Same GEOGRAPHY-only caveat as ZIP above (FB-009): no Device/Publisher
  // filter support, reported back as `ignored_filters`.
  City: 'geo_city',
};

// Geo Map widget granularity → canonical dimension key. State, Market, City,
// and ZIP all draw a real Mapbox GL choropleth (StateChoropleth /
// DmaChoropleth / CityChoropleth / ZipChoropleth) — chartjs-plugin-zoom
// doesn't support chartjs-chart-geo's projection scale, so these switched to
// the same library and boundary sources the legacy Simplifi/GAM reporting
// tab already ships in production, to get real zoom/pan (flank#5184 ported
// State last, after Market/City/ZIP).
// ZIP groups by the raw code alone, not the "ZIP | City, State" label
// (flank#4811, flank#4812): grouping by the label split any ZIP with more
// than one historical city/state spelling across multiple rows — one
// silently overwrote another's value in ZipChoropleth, and the split rows ate
// multiple slots of the widget's row cap, so an advertiser with 100+ real
// ZIPs of delivery could see far fewer on the map. ZipChoropleth's tooltip
// falls back to the bare code here (no label dimension is requested) — this
// map has no ranked list alongside it the way Unified Reporting's does, so
// there's no separate, decoupled label query to maintain.
export const GEO_LEVEL_DIMENSIONS: Record<'state' | 'dma' | 'zip' | 'city', string> = {
  state: 'geo_state',
  dma: 'geo_dma',
  zip: 'geo_zip',
  city: 'geo_city',
};

// Paired identifier dimension a mapped level's boundary lookup joins on — the
// display name collides too often to use alone (metros and cities both repeat
// across markets/states). ZIP needs no entry here any more: GEO_LEVEL_DIMENSIONS.zip
// is already the raw code the boundary lookup joins on.
export const GEO_LEVEL_CODE_DIMENSIONS: Partial<Record<'state' | 'dma' | 'zip' | 'city', string>> = {
  dma: 'geo_dma_code',
};

// City has no usable code dimension (canonical's `geo_city_code` is a
// per-DSP passthrough with no relationship to the boundary table — see
// CityChoropleth), so it resolves boundaries by name+state instead.
const GEO_LEVEL_STATE_DIMENSIONS: Partial<Record<'state' | 'dma' | 'zip' | 'city', string>> = {
  city: 'geo_state_code',
};

// flank#4813 — ZIP and City alone can collide with a non-US postcode/city of
// the same code/name; requesting `geo_country` here lets ZipChoropleth/
// CityChoropleth drop a positively-identified non-US row before it reaches
// boundary-matching. State/Market boundary sources are US-only already and
// no leakage was observed in the ticket's own data, so they're left alone.
const GEO_LEVEL_COUNTRY_DIMENSIONS: Partial<Record<'state' | 'dma' | 'zip' | 'city', string>> = {
  zip: 'geo_country',
  city: 'geo_country',
};

// Empty-state copy for the ranked-bar branch ("No {label}-level delivery...").
const GEO_LEVEL_LABELS: Record<'state' | 'dma' | 'zip' | 'city', string> = {
  state: 'state',
  dma: 'market',
  zip: 'ZIP',
  city: 'city',
};

/** Filters the API cannot apply once a widget groups by geography. */
const FILTER_LABELS: Record<string, string> = {
  device_ids: 'Device',
  publishers: 'Publisher',
  ribeye_campaign_ids: 'Campaign',
  ribeye_line_item_ids: 'Line Item',
};

const ENTITY_FILTERS_NOTICE = 'Campaign/Line Item filters on this dashboard were cleared — reselect them.';

const PRODUCT_FILTER_NOTICE =
  'The Product filter was removed — it only covered delivery launched through Flank. This dashboard’s Product selection was cleared.';

const DENIED_FIELDS_NOTICE =
  'A widget on this dashboard uses a metric or dimension you don’t have access to — it’s hidden from your view only, not removed from the dashboard.';
const PRODUCT_DIMENSION_NOTICE =
  'A Product breakdown or filter selection was removed from this dashboard — delivery not launched through Flank has no product.';
const DENIED_FILTER_NOTICE =
  'A DSP or Publisher filter on this dashboard was cleared — you don’t have permission to view it.';

// ─── helpers ─────────────────────────────────────────────────────────────────

/** Shared no-op for props a read-only widget instance never calls (#4434 expand dialog). */
const NOOP = () => {
  // intentionally empty
};

const ymd = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * Prefer a raw numeric value over its display-string counterpart wherever one
 * was captured. table.rows/PptxMetricRow.value always carry
 * formatMetricValue's display string ("$1,234.56", "45.2%") for PPTX/on-screen
 * rendering; CSV/Excel need a real number to SUM or sort a metric column
 * instead of reading it as text (flank#4458/#4553, CodeRabbit). `raw` is keyed
 * the same as `display`; a key with no raw entry (the dimension column, which
 * is never numeric) keeps its display value.
 */
export const preferRawValues = (
  display: Record<string, unknown>,
  raw: Record<string, number | null> | undefined,
): Record<string, string | number | null> => {
  const out: Record<string, string | number | null> = {};
  for (const [k, v] of Object.entries(display)) {
    const r = raw?.[k];
    out[k] = r !== undefined ? r : v == null ? null : String(v);
  }
  return out;
};

/**
 * A fetched value read positionally by index (`allRowsTotal`'s
 * `tableMetricKeys[i]` pairing) is only safe to render against the CURRENT
 * key list if it was fetched for that same list. Changing a widget's metric
 * selection re-renders with the new keys before the matching new fetch
 * resolves — without this guard, a stale value briefly renders under the
 * new column headers (CodeRabbit, flank#5054: e.g. a prior spend total
 * appearing under a newly-added client_spend column).
 */
export const staleGuard = <T,>(stored: { sig: string; values: T } | null, currentSig: string): T | null =>
  stored?.sig === currentSig ? stored.values : null;

/**
 * Resolves a group-by value to the same display name Unified Reporting shows
 * for it — a per-whitelabel Directus rename (#4469, #4914) or the GAM
 * "GAM Display"/"GAM Video" → "Display"/"Video" mask (#4741) — instead of the
 * raw warehouse value dashboards printed everywhere until now (flank#5062).
 *
 * Reads `labelCache`, the module-level cache `useFilterOptions` itself
 * populates on every fetch (keyed by dimension) — `DashboardFilterBar`'s own
 * `useFilterOptions('dsp'/'channel')` calls keep it warm, so this needs no
 * hook of its own and works from a plain row value. A dimension this cache
 * doesn't cover (Campaign, Advertiser, a custom dimension, …) passes through
 * unchanged, same as an unrenamed raw dsp/channel value falling back to
 * itself.
 *
 * `canViewPlatform` is threaded through from the caller's own
 * `useDashboardPermissions` rather than hardcoded, so every call site here
 * masks correctly for the permissions value it's given. Every call site,
 * Bar/Donut included, recomputes it fresh on every render straight from live
 * `permissions.canViewPlatform` — `chartRowLabel` does this for a Bar/Donut
 * row from its stored `raw` warehouse value. This used to be baked into
 * `chartRows` state inside the data-fetch effect instead, so a widget that
 * fetched before `canViewPlatform` resolved (the Oct 23 production retest's
 * "By channel" donut, confirmed live) kept showing the raw "GAM Display"/
 * "GAM Video" until something else triggered a refetch — fixed by storing
 * the raw value and relabelling at render instead (Lucas, PR review #5105).
 *
 * A blank group-by value reads with the same wording Unified Reporting uses
 * (flank#5055) rather than a bare "—"/"Unclassified", which looked like a
 * rendering glitch: "Unmapped" for a category dimension (Channel, Product, …,
 * matching `dimensionLabel`'s own convention) or "Unnamed <kind>" for an
 * entity dimension (Advertiser/Campaign/Line Item, matching `entityLabel`'s
 * convention). Unlike Unified Reporting's `entityLabel`, there's no id
 * dimension fetched alongside the name here — a table widget only ever groups
 * by the one field named in TABLE_DIMENSIONS — so this can reuse the wording
 * but not the id suffix (e.g. "Unnamed item (GAM0)"); it reads "Unnamed
 * campaign" with no id. Getting the id too would mean grouping by an id
 * dimension and looking up the name separately, a query-shape change bigger
 * than this label fix.
 */
const ENTITY_DIMENSION_KINDS: Record<string, string> = {
  advertiser_name: 'advertiser',
  campaign_name: 'campaign',
  line_item_name: 'line item',
};

export const dashboardDimensionLabel = (dimensionKey: string, value: string, canViewPlatform: boolean): string => {
  if (!value) {
    const kind = ENTITY_DIMENSION_KINDS[dimensionKey];
    return kind ? `Unnamed ${kind}` : 'Unmapped';
  }
  if (dimensionKey === 'dsp') return labelCache('dsp').get(value) ?? value;
  if (dimensionKey === 'channel')
    return genericChannelLabel(labelCache('channel').get(value) ?? value, canViewPlatform);
  return value;
};

// A table widget's "All <plural>" total row (flank#5051) — `dimension` is the
// friendly TABLE_DIMENSIONS key (widget.dimension), not the canonical field,
// since that's what's already on hand at both call sites. DSP keeps the term
// this app already uses for it elsewhere ("Platform") rather than the
// unfamiliar "DSPs"; every other key here pluralizes with a plain "s".
const DIMENSION_PLURAL_OVERRIDES: Record<string, string> = { DSP: 'platforms' };
export const dashboardDimensionPlural = (dimension: string): string =>
  DIMENSION_PLURAL_OVERRIDES[dimension] ?? `${dimension.toLowerCase()}s`;

// Appends " (2)", " (3)"... to a repeated label so buildSpreadsheetXml's
// worksheet names (#4458) stay unique — widget titles are user-edited and
// two widgets can share a default or renamed title, unlike Unified
// Reporting's fixed section labels (CodeRabbit, flank#4553). A lone label
// keeps its title exactly as written; only a real collision gets a suffix.
export const uniqueExportLabels = (labels: string[]): string[] => {
  const counts = new Map<string, number>();
  return labels.map(label => {
    const seen = (counts.get(label) ?? 0) + 1;
    counts.set(label, seen);
    return seen === 1 ? label : `${label} (${seen})`;
  });
};

// A delimiter-joined string key (`${rk}||${ck}`) can collide when a row or
// column value itself contains that delimiter — a real risk for a pivot's
// row/column keys, which are arbitrary data (campaign/product names), unlike
// a fixed vocabulary. JSON.stringify of the tuple can't collide the same way.
// Shared by the rendered crosstab and its export snapshot so both stay in
// sync (CodeRabbit, flank#4458 follow-up).
const pivotCellKey = (rowKey: string, colKey: string): string => JSON.stringify([rowKey, colKey]);

// The reporting backend only exposes a `day` grain, so Week/Month are bucketed
// client-side from daily rows. Count/currency metrics sum; ratio/percent metrics
// average (summing a rate across days would be meaningless).
export const bucketRows = (
  rows: Array<Record<string, unknown>>,
  metricKeys: string[],
  grain: 'day' | 'week' | 'month',
  calculated: CalculatedMetricDef[] = [],
): Array<{ label: string; values: number[] }> => {
  const calculatedFormats = new Map(calculated.map(m => [m.key, m.format]));
  const isAvg = metricKeys.map(mk => {
    // A canonical metric's own non-additive classification (CodeRabbit,
    // flank#5065) — cpm/cpc/client_cpm are `format: 'currency'`, same as
    // genuinely additive Spend, so a format-only check summed them across a
    // bucketed week/month the same way it would sum Spend. A tenant's own
    // calculated metric has no canonical key to classify by, so its own
    // declared format is still the only signal available for it.
    if (isNonAdditiveMetric(mk)) return true;
    const f = calculatedFormats.get(mk);
    return f === 'percent' || f === 'ratio' || f === 'multiple';
  });
  const acc = new Map<string, { label: string; sums: number[]; count: number }>();
  for (const r of rows) {
    const iso = String(r.day ?? '').slice(0, 10);
    if (!iso) continue;
    const d = new Date(`${iso}T00:00:00`);
    let key: string;
    let label: string;
    if (grain === 'day') {
      key = iso;
      label = `${MONTHS[d.getMonth()]} ${d.getDate()}`;
    } else if (grain === 'month') {
      key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      label = `${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
    } else {
      const offset = (d.getDay() + 6) % 7; // 0 = Monday
      const monday = new Date(d);
      monday.setDate(d.getDate() - offset);
      key = ymd(monday);
      label = `${MONTHS[monday.getMonth()]} ${monday.getDate()}`;
    }
    const b = acc.get(key);
    if (b) {
      for (let i = 0; i < metricKeys.length; i++) b.sums[i] += Number(r[metricKeys[i]] ?? 0);
      b.count += 1;
    } else {
      acc.set(key, { label, sums: metricKeys.map(mk => Number(r[mk] ?? 0)), count: 1 });
    }
  }
  return [...acc.entries()]
    .sort((a, b) => (a[0] < b[0] ? -1 : 1))
    .map(([, b]) => ({
      label: b.label,
      values: b.sums.map((s, i) => (isAvg[i] ? s / b.count : s)),
    }));
};

// ─── types ───────────────────────────────────────────────────────────────────

// Declared as a type alias rather than an interface on purpose: only type
// aliases get an implicit index signature, which is what makes a WidgetConfig
// assignable to the API client's `DashboardWidget` (Record<string, unknown>)
// without casting through `unknown` on every save.
type WidgetConfig = {
  id: string;
  type: string;
  title: string;
  metric?: string;
  showDelta?: boolean;
  compareTo?: string;
  text?: string;
  /** Text Block: size/weight preset (#4433). Also the label on a Divider. */
  textStyle?: 'title' | 'heading' | 'subheader' | 'body' | 'caption';
  /** Text Block alignment (#4433). */
  textAlign?: 'left' | 'center' | 'right';
  /** Callout widget color, reusing MUI Alert's severities (#4433). */
  calloutTone?: 'info' | 'success' | 'warning' | 'error';
  // Table widget config:
  dimension?: string; // friendly key into TABLE_DIMENSIONS (group-by)
  metrics?: string[]; // canonical metric keys (legacy configs may hold labels — normalized on read)
  rowLimit?: number;
  // Chart widget config:
  timeGrain?: 'day' | 'week' | 'month'; // line/area bucketing
  sortOrder?: 'desc' | 'asc'; // bar top-N direction
  orientation?: 'horizontal' | 'vertical'; // bar layout
  /** Line/area: plot every series as % of its own max instead of raw units (#4408). */
  normalizeChartAxes?: boolean;
  /** Line/area: manual per-metric left/right axis assignment, overriding the auto format-split (#4408). */
  axisOverrides?: AxisOverrides;
  /** Table: show an aggregate row below the data (#4409). Defaults to shown. */
  showTotalsRow?: boolean;
  /** Goal Tracker / Pacing target for the selected window. */
  goalTarget?: number;
  /** How many of the 3 grid columns this widget spans (1-3). */
  span?: 1 | 2 | 3;
  /** Height in the same 1-3 units as `span`. Unset renders at the original size. */
  rowSpan?: RowSpan;
  /** Page this widget belongs to. Unset on dashboards saved before pages existed. */
  pageId?: string;
  /** Image / Logo widget. */
  imageUrl?: string;
  imageFit?: 'contain' | 'cover';
  /** Geo Map granularity + Pivot column dimension. */
  geoLevel?: 'state' | 'dma' | 'zip' | 'city';
  columnDimension?: string;
  /** Per-widget filter overrides; unset dimensions inherit the dashboard's. */
  widgetFilters?: WidgetFilters;
  /** Which conversion type a widget showing Conversions displays (flank#5098)
   * — Web/Listen-through/Foot traffic can't be summed into one meaningful
   * total, so the viewer picks one, the same as Unified Reporting. Unset
   * resolves to the first type the widget's own data actually has. */
  conversionType?: ConversionType;
};

interface Props {
  dashboardId: number;
  onBack: () => void;
  onSaved: () => void;
}

// ─── Widget card ─────────────────────────────────────────────────────────────

/**
 * Apply tenant fields to a widget's rows. Grouping by a tenant dimension has to
 * re-aggregate — bucketing collapses several base values into one label, so the
 * rows must be summed or the widget repeats the bucket with un-added metrics.
 */
const applyWidgetTenantFields = (
  rows: Array<Record<string, unknown>>,
  widget: WidgetConfig,
  fields: TenantFields,
  extraGroupBy: string[] = [],
): Array<Record<string, unknown>> => {
  const custom = fields.dimensions.find(d => d.key === widget.dimension);
  if (!custom) return applyTenantFields(rows, fields);
  // A single-metric widget carries `metric`, not `metrics`; defaulting to
  // impressions there aggregated a column the widget never asked for.
  const metricKeys = widget.metrics?.length
    ? widget.metrics.map(toMetricKey)
    : [toMetricKey(widget.metric ?? 'impressions')];
  return aggregateByCustomDimension(rows, custom, metricKeys, fields.metrics, extraGroupBy);
};

/**
 * Datalabels shown only in the exported image, never on the live chart: the
 * on-screen dashboard deliberately leaves them off (`datalabels: { display:
 * false }` on every chart below) and relies on hover tooltips for the actual
 * numbers instead — reasonable for an interactive page, but a static PPTX
 * slide has no hover state, so a doughnut/bar exported as-is shows colour and
 * proportion with no number anywhere (found live testing #4414's own PR: a
 * client-facing deck with unreadable charts). Percentage-of-total for
 * doughnuts matches how a pie/donut is conventionally labelled; the formatted
 * value for bars, since a bar's own axis already gives scale, a percentage
 * would not. Left off for line charts — too many points for per-point labels
 * to stay legible.
 */
/** Below this share of the total, a doughnut slice is too thin for a
 * two-line label to fit inside it without spilling into its neighbours —
 * found live testing #4414's own preview (a 1% and a 0% slice sitting next
 * to a 7% one collided into an unreadable stack). Suppressed rather than
 * shrunk further: the legend already names every slice by colour, so a
 * label that can't fit is redundant, not lost information. */
const MIN_SLICE_PERCENT_FOR_LABEL = 3;

/**
 * What a chart slide's subtitle should say, beneath a title that's often just
 * whatever the dashboard's author named the widget ("Denver") — which says
 * nothing about metric or dimension once it's a static image on a slide with
 * no tooltip to fall back on (Josh, live testing #4414's own preview: "what
 * is 'Denver' associated with?", and a normalized time series with "no
 * numbers so it's hard to know what the percentage is referencing").
 */
export function chartExportSubtitle(
  widgetType: string,
  dimension: string | undefined,
  metricLabels: string[],
  normalized: boolean,
): string {
  const metrics = metricLabels.length > 0 ? metricLabels.join(', ') : 'Impressions';
  if (widgetType === 'donut' || widgetType === 'bar') {
    return dimension ? `${metrics} by ${dimension}` : metrics;
  }
  // line / area — normalizeSeriesValues scales each SERIES to its own peak
  // across all periods, not each period to its own peak (review, flank#4414).
  return normalized ? `${metrics} — % of each series' peak` : metrics;
}

export function exportDatalabelsFor(chartType: string | undefined, indexAxis?: string, metricKeys: string[] = []) {
  // Which metric a given Chart.js dataset plots — bar/line datasets are one
  // per metric in `chartMetricKeys` order (see buildSeries), so datasetIndex
  // maps directly; a doughnut has one dataset for its single metric, so this
  // just falls back to metricKeys[0] there.
  const metricFor = (datasetIndex: number | undefined) =>
    metricKeys[datasetIndex ?? 0] ?? metricKeys[0] ?? 'impressions';
  if (chartType === 'doughnut' || chartType === 'pie') {
    return {
      display: true,
      color: '#fff',
      font: { weight: 'bold' as const, size: 9 },
      // Two lines, not "28% (930.4K)" crammed onto one — narrow slices have
      // little width, and a single long string is the first thing to
      // overlap its neighbours (review, flank#4414: percentage alone isn't
      // enough, but the raw number needs to stay compact to not clutter).
      formatter: (
        value: number,
        ctx: { chart: { data: { datasets: Array<{ data: unknown[] }> } }; datasetIndex?: number },
      ) => {
        const dataset = ctx.chart.data.datasets[0]?.data ?? [];
        const total = dataset.reduce((sum: number, v) => sum + (Number(v) || 0), 0);
        if (total <= 0) return '';
        const percent = Math.round((value / total) * 100);
        // A currency/percent/ratio metric shown here with plain count
        // formatting reads as the wrong number, not just an ugly one
        // (review, flank#4414) — formatMetricValue picks the metric's own
        // format, same as every other value shown for this widget.
        return percent < MIN_SLICE_PERCENT_FOR_LABEL
          ? ''
          : [`${percent}%`, formatMetricValue(metricFor(ctx.datasetIndex), value)];
      },
    };
  }
  if (chartType === 'bar') {
    // Chart.js datalabels' anchor/align are relative to the bar's own value
    // axis, not screen "up" — 'top' only means "past the bar's end" for a
    // VERTICAL bar. Applied to a horizontal bar (indexAxis: 'y', the default
    // for every ranked-list widget here — Top Products, Top Publishers,
    // etc.) it collapsed every row's label toward the same corner instead of
    // past each bar's own tip, an unreadable stack regardless of how long
    // the bar actually was (found live testing this PR's own preview).
    const horizontal = indexAxis === 'y';
    return {
      display: true,
      color: '#111111',
      anchor: 'end' as const,
      align: horizontal ? ('end' as const) : ('top' as const),
      clamp: true,
      font: { weight: 'bold' as const, size: 10 },
      formatter: (value: number, ctx?: { datasetIndex?: number }) =>
        Number.isFinite(value) ? formatMetricValue(metricFor(ctx?.datasetIndex), value) : '',
    };
  }
  return { display: false };
}

/**
 * Captures a chart at export quality, not the on-screen canvas's own
 * resolution — a dashboard tile is often a few hundred pixels wide, and
 * exporting that directly onto a full slide upscaled it into a soft, blurry
 * image (Earl, flank#4414). Chart.js's canvas backing store tracks
 * `options.devicePixelRatio`; bumping it and resizing (then restoring both
 * right after the capture) redraws at a higher pixel density without
 * changing the chart's on-screen CSS size, so the live dashboard is
 * unaffected. `chart.canvas.width/height` afterward are the exact pixel
 * dimensions PNG-encoded into the capture, so the slide can fit it without
 * stretching (see pptxExport.ts `fitWithinBox`).
 *
 * Also flips on datalabels (off by default on the live chart) for the
 * duration of the capture only — see exportDatalabelsFor.
 */
function captureChartImage(chart: ChartJS | null | undefined, metricKeys: string[] = []): PptxChartImage | null {
  if (!chart) return null;
  const originalDpr = chart.options.devicePixelRatio;
  const originalDatalabels = chart.options.plugins?.datalabels;
  chart.options.devicePixelRatio = Math.max(4, window.devicePixelRatio || 1) * 2;
  if (chart.options.plugins) {
    chart.options.plugins.datalabels = exportDatalabelsFor(
      (chart.config as { type?: string }).type,
      chart.options.indexAxis,
      metricKeys,
    );
  }
  chart.resize();
  chart.update();
  const data = chart.toBase64Image();
  const { width, height } = chart.canvas;
  chart.options.devicePixelRatio = originalDpr;
  if (chart.options.plugins) {
    chart.options.plugins.datalabels = originalDatalabels;
  }
  chart.resize();
  chart.update();
  return { data, width, height };
}

const WidgetCard = ({
  widget,
  editing,
  filters,
  accent,
  dragHandleProps,
  onConfigure,
  onDuplicate,
  onRemove,
  onResize,
  onResizeRows,
  onConversionTypeChange,
  refreshNonce,
  tenantFields,
  hideExpand,
  onExportReady,
}: {
  widget: WidgetConfig;
  editing: boolean;
  filters: DashboardFilters;
  tenantFields: TenantFields;
  /** Bumped by the Refresh button to re-run this widget's query. */
  refreshNonce: number;
  accent?: string;
  dragHandleProps?: Record<string, unknown>;
  onConfigure: () => void;
  onDuplicate: () => void;
  onRemove: () => void;
  onResize?: (span: 1 | 2 | 3) => void;
  onResizeRows?: (rowSpan: RowSpan) => void;
  /** flank#5098 — persists the viewer's Web/Listen-through/Foot traffic pick. */
  onConversionTypeChange: (type: ConversionType) => void;
  /** Set on the instance rendered inside the expand Dialog itself (#4434), so it doesn't offer to expand again. */
  hideExpand?: boolean;
  /** Reports this widget's current data as an export-ready snapshot (PPTX export, #4414). */
  onExportReady?: (id: string, snapshot: WidgetExportSnapshot) => void;
}) => {
  const [expandOpen, setExpandOpen] = useState(false);
  // react-chartjs-2's ref type differs per chart kind (Line/Bar/Doughnut); this
  // ref is shared across all three and only ever calls the one method they share.
  const chartRef = useRef<any>(null);
  const [kpiValue, setKpiValue] = useState<number | null>(null);
  const [kpiDelta, setKpiDelta] = useState<number | null>(null);
  // Which conversion types this widget's own data actually has (flank#5098)
  // — feeds the Web/Listen-through/Foot traffic toggle; always at least
  // ['web'] so the control is never empty before the fetch resolves.
  const [conversionTypesSeen, setConversionTypesSeen] = useState<ConversionType[]>(['web']);
  // flank#5098 (CodeRabbit) — the Table's Conversions column is only actually
  // split by type when the overlay fetch ran and succeeded (it's skipped for
  // ZIP/City dimensions, or can fail). Gates the column header's typed label
  // so combined conversions never get presented as one type.
  const [tableConversionsSplit, setTableConversionsSplit] = useState(false);
  // `raw` is only set by the Bar/Donut fetch (the dimension value as it came
  // back from the warehouse, e.g. "gam_display") — Line/Area's own `label` is
  // a date bucket, never a dimension value, so it has nothing to re-mask and
  // leaves `raw` undefined. Keeping the raw value instead of baking the
  // masked label in at fetch time means the label is recomputed at render
  // from the CURRENT permissions.canViewPlatform (Lucas, PR review #5105):
  // Table/Pivot already do this; this makes Bar/Donut match instead of
  // needing a refetch every time the permission signal changes mid-session.
  const [chartRows, setChartRows] = useState<Array<{ label: string; raw?: string; values: number[] }>>([]);
  // Unified Reporting's own explanation for its channel breakdown when some
  // delivery has no channel attached (BreakdownCard.tsx, unmappedNote prop) —
  // a bar/donut grouped by Channel got the "Unclassified" slice with no such
  // note (flank#5055 AC3), reading as an unexplained gap rather than "this
  // delivery is real, it's just unmapped".
  const [channelUnmappedNote, setChannelUnmappedNote] = useState<string | null>(null);
  const [tableRows, setTableRows] = useState<Array<Record<string, unknown>>>([]);
  // Filters the API could not apply to this widget's fact table. Left unsaid,
  // the widget shows an UNFILTERED number that looks filtered — the one failure
  // a reader can't spot by looking at it.
  const [ignoredFilters, setIgnoredFilters] = useState<string[]>([]);
  // Totals for scorecard / goal / funnel / pacing (one row, N metrics) + priors.
  const [totals, setTotals] = useState<(number | null)[] | null>(null);
  const [priorTotals, setPriorTotals] = useState<(number | null)[] | null>(null);
  // flank#5049 — the scorecard delta's own "current" half, fetched under the
  // freshness-clamped end date so it covers the same number of days as
  // `priorTotals` below; `totals` above stays unclamped and keeps being what
  // the card's big number displays (see the WIDGET_TOTALS_TYPES effect).
  const [clampedTotals, setClampedTotals] = useState<(number | null)[] | null>(null);
  // A table widget only ever fetches its own `rowLimit` rows (10 by default),
  // so `computeTotalsRow(tableRows, …)` only ever totals what's on screen —
  // read as the dashboard's whole total, which it isn't once a campaign/line
  // item count exceeds the cap (flank#5051). Fetched the same way the
  // scorecard/goal/funnel/pacing branch above gets its one-row aggregate
  // (`dimensions: []`, same filters and metrics, `limit: 1`) so the two stay
  // in agreement by construction rather than by coincidence.
  //
  // Paired with the `tableMetricsSig` it was fetched for (CodeRabbit,
  // flank#5054): changing the widget's metric selection re-renders with the
  // new `tableMetricKeys` before the matching new fetch resolves, and this
  // state is read positionally by index — without the signature check, the
  // old values briefly render under the new column headers (e.g. a prior
  // spend total appearing under a newly-added client_spend column).
  const [allRowsTotal, setAllRowsTotal] = useState<{ sig: string; values: (number | null)[] } | null>(null);
  const [dataLoading, setDataLoading] = useState(false);
  // Tracks a failed fetch so PPTX export (#4414) can skip a stale snapshot
  // rather than re-exporting whatever the widget last successfully showed.
  const [dataFailed, setDataFailed] = useState(false);
  // Conversion-action drill-down (FB-008) — only offered on the raw
  // `conversions` KPI, not derived ratios like CPA/ROAS.
  const [conversionModalOpen, setConversionModalOpen] = useState(false);

  // Widget inherits the dashboard filters; any dimension it sets itself wins.
  const effectiveFilters = resolveWidgetFilters(filters, widget.widgetFilters, tenantFields.dimensions);
  const filtersSig = filtersSignature(effectiveFilters);

  // flank#5054 — #4442/#4446 permissions, same signal Unified Reporting reads
  // via restrictedFields, accumulated across every widget's own responses
  // (see dashboardPermissions.ts for why there's no single page-wide signal
  // widget here).
  const isRibeyeAdmin = useAppSelector(selectIsSuperAdmin);
  const permissions = useDashboardPermissions(isRibeyeAdmin);
  // flank#5049 — same accumulated-across-widgets pattern as permissions above.
  const freshnessMap = useDashboardFreshness();
  // Computed at render, not inside the fetch effect below (CodeRabbit,
  // flank#5065): `freshnessMap`'s object identity changes on every widget's
  // response, most of which don't move this widget's own dsps' cutoff date —
  // putting the map itself in the effect's deps would refetch on every one of
  // those irrelevant updates, so only this derived STRING goes in the deps.
  // "Prior year" shifts the comparison window's year, not its length, so it
  // isn't freshness-clamped — same reasoning as the KPI branch below.
  // Totals-type widgets never set `compareTo` to 'Prior year' (no such option
  // in their own config panel), so this one formula covers both branches that
  // read it further down.
  const comparisonEnd =
    widget.compareTo === 'Prior year'
      ? effectiveFilters.dateEnd
      : comparisonWindowEnd(effectiveFilters.dateStart, effectiveFilters.dateEnd, effectiveFilters.dsps, freshnessMap);
  // Only the KPI branch and a scorecard's delta read `comparisonEnd` (see the
  // fetch effect's KPI/totals-type branches below) — every other widget type
  // was refetching on freshness updates it never uses, doubling warehouse
  // query load on first load as each widget's own response moved the cutoff
  // (CodeRabbit, flank#5065).
  const usesComparisonEnd = widget.type === 'kpi' || (widget.type === 'scorecard' && widget.showDelta !== false);
  const comparisonEndDep = usesComparisonEnd ? comparisonEnd : null;

  // Metrics are suppressed against THIS widget's effective filters, so a
  // widget-level DSP or Channel override can unlock them even when the dashboard
  // as a whole spans several. Three rules (see metricSuppression): conversions
  // need one DSP; rate metrics need one channel; Reach/Frequency need one DSP
  // (flank#4460 — same double-counting failure mode as conversions). A metric
  // the viewer has no PERMISSION to see at all (flank#5054) is folded into the
  // same "suppressed" concept so the existing allSuppressed/keepShown skip
  // logic below covers it for free — but kept out of suppressedReasons/
  // suppressionNote (below) so it never shows one of SUPPRESSION_NOTES'
  // actionable hints for something the viewer can't fix by changing a filter.
  // `dims` is the widget's own active breakdown dimension (flank#5052) —
  // ONLY a real group-by, e.g. a Table/Pivot grouped by DSP, where each row
  // genuinely carries one platform. A KPI/scorecard/line-chart has no
  // breakdown at all (every metric is one aggregate number or a time series,
  // not a per-dsp/per-channel row), so passing `tableDimKey` there would
  // falsely claim a DSP breakdown from its own "default to DSP" fallback —
  // call sites with no real dimension omit `dims` (defaults to none).
  const suppressionOf = (key: string, dims: string[] = []) =>
    metricSuppression(key, effectiveFilters, tenantFields.dimensions, dims);
  const keepShown = (keys: string[], dims: string[] = []) =>
    keys.filter(k => !suppressionOf(k, dims) && !isPermissionDeniedMetric(k, permissions));

  // A tenant dimension has no column in the warehouse — query the canonical
  // field it buckets and collapse the rows after they come back.
  const tenantFieldsSignature = useMemo(() => JSON.stringify(tenantFields), [tenantFields]);
  const customDim = tenantFields.dimensions.find(d => d.key === widget.dimension);
  const tableDimKey = customDim?.field ?? TABLE_DIMENSIONS[widget.dimension ?? 'DSP'] ?? 'dsp';
  // A custom dimension's `field` is the underlying column it BUCKETS from
  // (e.g. "dsp" for a custom grouping whose buckets happen to be defined over
  // raw dsp values), not a real canonical "group by this" breakdown — passing
  // it to keepShown/suppressionOf would make metric suppression (e.g.
  // "Conversions need one DSP picked") fire for a table that isn't actually
  // grouped by DSP at all, just by a custom bucket label (CodeRabbit,
  // flank#5065). Empty, not tableDimKey, whenever a custom dimension is in
  // play; the real canonical key only when it genuinely is one.
  const realTableDims = customDim ? [] : [tableDimKey];
  const tableMetricKeysAll = (widget.metrics?.length ? widget.metrics : ['impressions']).map(toMetricKey);
  const tableMetricKeys = keepShown(tableMetricKeysAll, realTableDims);
  const tableMetricsSig = tableMetricKeys.join(',');
  const safeAllRowsTotal = staleGuard(allRowsTotal, tableMetricsSig);

  // Charts (line/area) plot up to 3 metric series, dual-axis when formats mix.
  const chartMetricKeysAll = resolveChartMetrics(widget).map(toMetricKey);
  const chartMetricKeys = keepShown(chartMetricKeysAll);
  const chartSeries = buildSeries(chartMetricKeys, accent, widget.axisOverrides);
  const chartMetricsSig = chartMetricKeys.join(',');
  // Precomputed once per render (not once per series) when the normalize
  // toggle is on (#4408) — null otherwise, so the chart plots raw values.
  const normalizedChartRows = widget.normalizeChartAxes ? normalizeSeriesValues(chartRows, chartSeries) : null;

  // Scorecard/Funnel plot several metrics; Goal/Pacing track one.
  const totalsMetricKeysAll = WIDGET_TOTALS_TYPES.has(widget.type)
    ? widget.type === 'goal' || widget.type === 'pacing'
      ? [toMetricKey(widget.metric ?? 'impressions')]
      : (widget.metrics?.length ? widget.metrics : (DEFAULT_TOTALS_METRICS[widget.type] ?? ['impressions'])).map(
          toMetricKey,
        )
    : [];
  const totalsMetricKeys = keepShown(totalsMetricKeysAll);
  const totalsMetricsSig = totalsMetricKeys.join(',');

  // Pivot: rows = `dimension`, columns = `columnDimension`.
  const pivotColKey = TABLE_DIMENSIONS[widget.columnDimension ?? 'Channel'] ?? 'channel';

  // Bar/Donut group by a canonical dimension (defaults to Channel).
  const chartDimKey = customDim?.field ?? TABLE_DIMENSIONS[widget.dimension ?? 'Channel'] ?? 'channel';
  // Matches the fetch effect's own `labelKey` below exactly — a custom
  // dimension's label lookup key is `customDim.key`, not `chartDimKey`
  // (which already embeds `customDim.field` instead).
  const chartLabelKey = customDim ? customDim.key : chartDimKey;
  // Re-derives a Bar/Donut row's display label from its `raw` warehouse value
  // on every render, straight from the current `permissions.canViewPlatform`
  // (Lucas, PR review #5105) — same pattern dashboardDimensionLabel's
  // Table/Pivot call sites already use, so a permission change relabels
  // instantly with no refetch. A Line/Area row has no `raw` (its `label` is a
  // date bucket, nothing to re-mask), so it falls through unchanged.
  const chartRowLabel = (r: { label: string; raw?: string }): string =>
    r.raw != null ? dashboardDimensionLabel(chartLabelKey, r.raw, permissions.canViewPlatform) : r.label;
  const timeGrain = widget.timeGrain ?? 'day';

  // Every metric this widget would display, before the conversion gate — used to
  // tell "all suppressed" (show the explanation instead of an empty widget) from
  // "some suppressed" (render the rest, caption the omission).
  const singleMetricKey = toMetricKey(widget.metric ?? 'impressions');
  const shownMetricKeysAll = SINGLE_METRIC_WIDGETS.has(widget.type)
    ? [singleMetricKey]
    : widget.type === 'bar' || widget.type === 'line' || widget.type === 'area'
      ? chartMetricKeysAll
      : widget.type === 'table'
        ? tableMetricKeysAll
        : WIDGET_TOTALS_TYPES.has(widget.type)
          ? totalsMetricKeysAll
          : [];
  // Same dims rule as tableMetricKeys above: only a real Table/Pivot
  // breakdown passes its group-by dimension through, and never a custom
  // dimension's underlying field (realTableDims is already empty for one).
  const shownDims = widget.type === 'table' ? realTableDims : [];
  const suppressedReasons = shownMetricKeysAll
    .map(k => suppressionOf(k, shownDims))
    .filter(Boolean) as MetricSuppression[];
  // Permission denials count toward allSuppressed/someSuppressed (so a KPI
  // card whose only metric is denied skips its fetch entirely, same as a
  // filter-suppressed one) but never into suppressedReasons — see the comment
  // on keepShown above for why no note is shown for these.
  const permissionDeniedCount = shownMetricKeysAll.filter(k => isPermissionDeniedMetric(k, permissions)).length;
  const suppressedCount = suppressedReasons.length + permissionDeniedCount;
  const allSuppressed = suppressedCount > 0 && suppressedCount === shownMetricKeysAll.length;
  const someSuppressed = suppressedCount > 0 && !allSuppressed;
  // If both rules fired, show both explanations — otherwise the user fixes one
  // and the widget is still blank with no reason given.
  const suppressionNote = [...new Set(suppressedReasons)].map(r => SUPPRESSION_NOTES[r]).join(' ');

  useEffect(() => {
    // Widgets fetch in edit mode too. Previously `editing` short-circuited this
    // effect, so a widget added from the picker — which can only happen while
    // editing — rendered an empty shell until the user happened to click Done.
    // The data was one unprompted click away, which reads as "I added a widget
    // and it shows nothing".
    //
    // `allSuppressed` still skips the request: clear any previously fetched
    // value so widening the DSP filter can't leave a stale conversion number on
    // screen, and skip the request entirely since we would discard the result.
    if (allSuppressed) {
      // The prior request's own `.finally` may have already been skipped by
      // its `cancelled` guard (a filter change superseded it), so this branch
      // must reset loading/failed state itself or a widget suppressed mid-fetch
      // stays on its loading skeleton forever (review, flank#4414).
      setDataLoading(false);
      setDataFailed(false);
      setKpiValue(null);
      setKpiDelta(null);
      setChartRows([]);
      setTableRows([]);
      setTotals(null);
      setPriorTotals(null);
      setClampedTotals(null);
      setAllRowsTotal(null);
      setChannelUnmappedNote(null);
      return;
    }
    const apiMetric = toMetricKey(widget.metric ?? 'impressions');
    const controller = new AbortController();
    // Aborting a superseded request still rejects its promise (axios doesn't
    // swallow AbortError), so without this guard a stale request's .then/
    // .catch/.finally could fire AFTER a newer request already reset this
    // state, overwriting it — e.g. a late-arriving abort-rejection setting
    // dataFailed(true) on a request the CURRENT one had already superseded,
    // with nothing in the current request's own .then clearing it back to
    // false, so export could skip a widget that actually loaded fine
    // (review, flank#4414). Set once per effect run, so only the closure
    // belonging to the LATEST request ever reaches its callbacks with
    // cancelled still false.
    let cancelled = false;

    setDataLoading(true);
    setDataFailed(false);

    if (widget.type === 'kpi') {
      // "Compare to: Prior year" (flank#5050) shifts the comparison window's
      // YEAR, not its length. Year-ago data is long since finalized, so
      // unlike the default comparison below it isn't freshness-clamped —
      // Unified Reporting's own reference implementation for this ticket
      // (usePriorPeriod.ts) has no year-over-year case to match, and a
      // year-old window running past "today minus a year" isn't the
      // incomplete-trailing-days problem flank#5049 is about.
      const isPriorYear = widget.compareTo === 'Prior year';
      // flank#5049: a window that runs past the data's actual last complete
      // day (e.g. "Last 30 Days" when the warehouse is 2 days behind) compared
      // a current period padded with near-zero incomplete days against a
      // fully-elapsed prior period — clamp the comparison's own end date to
      // the freshest day shared by the dsps THIS widget is scoped to
      // (effectiveFilters.dsps already carries a widget-level DSP override
      // over the dashboard's own, per resolveWidgetFilters). `comparisonEnd`
      // is computed at render (see above, CodeRabbit) rather than recomputed
      // here from `freshnessMap` directly.
      const clampedEnd = comparisonEnd;
      const prior = isPriorYear
        ? priorYearRange(effectiveFilters.dateStart, effectiveFilters.dateEnd)
        : priorRange(effectiveFilters.dateStart, clampedEnd);
      // flank#5098 — Conversions can't be summed across Web/Listen-through/Foot
      // traffic into one meaningful number, so this requests conversion_type
      // as a dimension (fanning the single row into one per type actually
      // present) and the widget picks one, same as Unified Reporting. Limit
      // bumped from 1 to fit every type in one page — there are at most 3.
      const isConversionsKpi = apiMetric === 'conversions';
      const kpiBase = {
        dimensions: isConversionsKpi ? ['conversion_type'] : [],
        metrics: [apiMetric],
        pagination: { limit: isConversionsKpi ? 10 : 1, offset: 0 },
      };
      const conversionsForSelectedType = (rows: ReportRow[]): number =>
        sumConversionsForType(rows, resolveConversionType(widget.conversionType, availableConversionTypes(rows)));
      // allSettled, not all: these are independent Snowflake queries and the
      // prior-period one is frequently the slower. Under Promise.all a single
      // failing/timing-out comparison rejected the pair, the catch below
      // swallowed it, and the card rendered a bare em-dash even though the
      // current-period number had come back fine. The headline value must not
      // depend on the delta.
      Promise.allSettled([
        postReport(applyDashboardFilters(kpiBase, effectiveFilters, tenantFields.dimensions), controller.signal),
        // The headline figure above stays on the raw, unclamped dateEnd (the
        // new "Data through" label next to the date range already discloses
        // when it runs past the last complete day — flank#5049's own open
        // questions explicitly leave clamping the primary window/total to a
        // separate ticket, #4850). Only the DELTA needs an equal-length,
        // equally-complete pair of windows, so its "current" half is a
        // second, clamped-end fetch — skipped for Prior year, which reuses
        // the headline value directly (see below).
        isPriorYear
          ? Promise.resolve(null)
          : postReport(
              {
                ...applyDashboardFilters(kpiBase, effectiveFilters, tenantFields.dimensions),
                date_range: { start: effectiveFilters.dateStart, end: clampedEnd },
              },
              controller.signal,
            ),
        postReport(
          { ...applyDashboardFilters(kpiBase, effectiveFilters, tenantFields.dimensions), date_range: prior },
          controller.signal,
        ),
      ])
        .then(([curRes, curClampedRes, priorRes]) => {
          if (cancelled) return;
          recordRestrictedFields(curRes.status === 'fulfilled' ? curRes.value.restricted_fields : undefined);
          recordFreshness(curRes.status === 'fulfilled' ? curRes.value.freshness : undefined);
          // Always assign both, never early-return: this effect re-runs on every
          // filter change, so bailing out left the PREVIOUS filter's number or
          // delta on screen next to the new one. A prior window with zero
          // impressions was enough to strand a stale "+15.2%".
          const curRows = curRes.status === 'fulfilled' ? curRes.value.data.rows : [];
          if (isConversionsKpi && curRes.status === 'fulfilled')
            setConversionTypesSeen(availableConversionTypes(curRows));
          // readMetricValue, not a bare `?? 0`: Client Spend/CPM must stay
          // null (blank) when unpriced rather than read as a true zero
          // (flank#5054 AC4). Conversions reads the selected type's own sum
          // instead — see conversionsForSelectedType above.
          const v = isConversionsKpi
            ? curRes.status === 'fulfilled'
              ? conversionsForSelectedType(curRows)
              : null
            : readMetricValue(curRows[0] ?? null, apiMetric);
          setKpiValue(v);
          // Promise.allSettled never rejects, so a failed CURRENT request
          // reaches here as curRes.status === 'rejected', not the .catch
          // below — without this, export could publish an empty KPI snapshot
          // instead of skipping the widget (review, flank#4414).
          if (curRes.status !== 'fulfilled') setDataFailed(true);

          const deltaCur = isPriorYear
            ? v
            : curClampedRes.status === 'fulfilled' && curClampedRes.value
              ? isConversionsKpi
                ? conversionsForSelectedType(curClampedRes.value.data.rows)
                : readMetricValue(curClampedRes.value.data.rows[0], apiMetric)
              : null;
          const pv =
            priorRes.status === 'fulfilled'
              ? isConversionsKpi
                ? conversionsForSelectedType(priorRes.value.data.rows)
                : Number(priorRes.value.data.rows[0]?.[apiMetric] ?? 0)
              : 0;
          setKpiDelta(deltaCur !== null && pv > 0 ? ((deltaCur - pv) / pv) * 100 : null);
        })
        .catch(() => {
          /* widget data is best-effort; leave the card empty on error */
          if (!cancelled) setDataFailed(true);
        })
        .finally(() => {
          if (!cancelled) setDataLoading(false);
        });
    } else if (widget.type === 'line' || widget.type === 'area') {
      postReport(
        applyDashboardFilters(
          {
            dimensions: ['day'],
            metrics: chartMetricKeys,
            sort: [{ field: 'day', dir: 'asc' }],
            pagination: { limit: 1000, offset: 0 },
          },
          effectiveFilters,
          tenantFields.dimensions,
        ),
        controller.signal,
      )
        .then(resp => {
          if (cancelled) return;
          recordRestrictedFields(resp.restricted_fields);
          recordFreshness(resp.freshness);
          setIgnoredFilters(resp.ignored_filters ?? []);
          setChartRows(bucketRows(resp.data.rows, chartMetricKeys, timeGrain, tenantFields.metrics));
        })
        .catch(() => {
          /* widget data is best-effort; leave the card empty on error */
          if (!cancelled) setDataFailed(true);
        })
        .finally(() => {
          if (!cancelled) setDataLoading(false);
        });
    } else if (widget.type === 'bar' || widget.type === 'donut') {
      const barMetrics = widget.type === 'donut' ? [apiMetric] : chartMetricKeys;
      const barLimit = widget.rowLimit ?? 10;
      postReport(
        applyDashboardFilters(
          {
            dimensions: [chartDimKey],
            metrics: barMetrics,
            sort: [{ field: barMetrics[0], dir: widget.sortOrder ?? 'desc' }],
            // A tenant dimension is bucketed after the query, so a server-side
            // top-N would rank the underlying values and then collapse them —
            // giving the wrong bars and the wrong totals. Take a wide slice and
            // rank the buckets here instead.
            pagination: { limit: customDim ? 500 : barLimit, offset: 0 },
          },
          effectiveFilters,
          tenantFields.dimensions,
        ),
        controller.signal,
      )
        .then(resp => {
          if (cancelled) return;
          recordRestrictedFields(resp.restricted_fields);
          recordFreshness(resp.freshness);
          setIgnoredFilters(resp.ignored_filters ?? []);
          const rows = customDim
            ? aggregateByCustomDimension(resp.data.rows, customDim, barMetrics, tenantFields.metrics)
            : resp.data.rows;
          const labelKey = customDim ? customDim.key : chartDimKey;
          const ranked = customDim
            ? [...rows].sort((a, b) => {
                const delta = Number(b[barMetrics[0]] ?? 0) - Number(a[barMetrics[0]] ?? 0);
                return (widget.sortOrder ?? 'desc') === 'desc' ? delta : -delta;
              })
            : rows;
          if (labelKey === 'channel') {
            const grandTotal = rows.reduce((sum, r) => sum + Number(r[barMetrics[0]] ?? 0), 0);
            const unmappedTotal = rows
              .filter(r => !String(r[labelKey] ?? ''))
              .reduce((sum, r) => sum + Number(r[barMetrics[0]] ?? 0), 0);
            const mappedTotal = grandTotal - unmappedTotal;
            setChannelUnmappedNote(
              unmappedTotal > 0
                ? `Shares are of the ${sharePercent(mappedTotal, grandTotal)} of ${metricLabel(barMetrics[0]).toLowerCase()} that carry a channel. ${num(unmappedTotal)} (${sharePercent(unmappedTotal, grandTotal)}) aren’t counted here. Channel is mapped per line item, so platforms that aren't mapped yet sit outside this breakdown. The delivery is real, it just has no channel attached.`
                : null,
            );
          } else {
            setChannelUnmappedNote(null);
          }
          setChartRows(
            ranked.slice(0, barLimit).map(r => {
              const raw = String(r[labelKey] ?? '');
              return {
                // Masked at RENDER time (see chartRowLabel below), not here —
                // this `label` is only a fallback for a row with no `raw` to
                // re-derive it from, which never happens for a Bar/Donut row.
                label: dashboardDimensionLabel(labelKey, raw, permissions.canViewPlatform),
                raw,
                values: barMetrics.map(mk => Number(r[mk] ?? 0)),
              };
            }),
          );
        })
        .catch(() => {
          /* widget data is best-effort; leave the card empty on error */
          if (!cancelled) setDataFailed(true);
        })
        .finally(() => {
          if (!cancelled) setDataLoading(false);
        });
    } else if (WIDGET_TOTALS_TYPES.has(widget.type)) {
      const base = {
        dimensions: [],
        metrics: totalsMetricKeys,
        pagination: { limit: 1, offset: 0 },
      };
      const wantPrior = widget.type === 'scorecard' && widget.showDelta !== false;
      // flank#5098 — `conversions` rides alongside other metrics in `base`
      // above, so it can't take `conversion_type` as a dimension there
      // without fanning spend/impressions/etc. across types too. A separate,
      // conversions-only overlay fetch (same convention as the current/
      // clamped/prior split below) resolves the type split without touching
      // how any other metric is read.
      const conversionsIdx = totalsMetricKeys.indexOf('conversions');
      const wantConversionOverlay = conversionsIdx !== -1;
      const overlayBase = {
        dimensions: ['conversion_type'],
        metrics: ['conversions'],
        pagination: { limit: 10, offset: 0 },
      };
      // flank#5049 — same freshness clamp as the KPI branch above, scoped to
      // this widget's own effective dsps. `totals` (headline, unclamped) and
      // the delta's own current/prior pair are two independent concerns, same
      // split as the KPI branch. Totals-type widgets never set `compareTo` to
      // 'Prior year', so `comparisonEnd` (computed at render, see above) is
      // always the clamped value here.
      const clampedEnd = comparisonEnd;
      const prior = priorRange(effectiveFilters.dateStart, clampedEnd);
      // allSettled for the same reason as the KPI branch above: a failing
      // comparison query must not blank the current-period totals.
      Promise.allSettled([
        postReport(applyDashboardFilters(base, effectiveFilters, tenantFields.dimensions), controller.signal),
        wantPrior
          ? postReport(
              {
                ...applyDashboardFilters(base, effectiveFilters, tenantFields.dimensions),
                date_range: { start: effectiveFilters.dateStart, end: clampedEnd },
              },
              controller.signal,
            )
          : Promise.resolve(null),
        wantPrior
          ? postReport(
              { ...applyDashboardFilters(base, effectiveFilters, tenantFields.dimensions), date_range: prior },
              controller.signal,
            )
          : Promise.resolve(null),
        wantConversionOverlay
          ? postReport(applyDashboardFilters(overlayBase, effectiveFilters, tenantFields.dimensions), controller.signal)
          : Promise.resolve(null),
        wantConversionOverlay && wantPrior
          ? postReport(
              {
                ...applyDashboardFilters(overlayBase, effectiveFilters, tenantFields.dimensions),
                date_range: { start: effectiveFilters.dateStart, end: clampedEnd },
              },
              controller.signal,
            )
          : Promise.resolve(null),
        wantConversionOverlay && wantPrior
          ? postReport(
              { ...applyDashboardFilters(overlayBase, effectiveFilters, tenantFields.dimensions), date_range: prior },
              controller.signal,
            )
          : Promise.resolve(null),
      ])
        .then(([curRes, curClampedRes, prevRes, convRes, convClampedRes, convPriorRes]) => {
          if (cancelled) return;
          if (curRes.status !== 'fulfilled') {
            setDataFailed(true);
            return;
          }
          recordRestrictedFields(curRes.value.restricted_fields);
          recordFreshness(curRes.value.freshness);
          if (wantConversionOverlay && convRes.status === 'fulfilled' && convRes.value) {
            setConversionTypesSeen(availableConversionTypes(convRes.value.data.rows));
          }
          // Patches just the conversions entry with the selected type's own
          // sum — every other metric in `values` came from `base`'s own
          // fetch and is untouched.
          const patchConversions = (
            values: Array<number | null>,
            overlayRows: ReportRow[] | undefined,
          ): Array<number | null> => {
            if (!wantConversionOverlay) return values;
            const patched = [...values];
            patched[conversionsIdx] = overlayRows
              ? sumConversionsForType(
                  overlayRows,
                  resolveConversionType(widget.conversionType, availableConversionTypes(overlayRows)),
                )
              : null;
            return patched;
          };
          const row = curRes.value.data.rows[0];
          // readMetricValue, not a bare `?? 0`: Client Spend/CPM must stay
          // null (blank) when unpriced rather than read as a true zero
          // (flank#5054 AC4) — every render site below already tolerates a
          // null entry (`totals?.[i] ?? null`, formatMetricValue(mk, null)).
          setTotals(
            row
              ? patchConversions(
                  totalsMetricKeys.map(mk => readMetricValue(row, mk)),
                  convRes.status === 'fulfilled' ? convRes.value?.data.rows : undefined,
                )
              : null,
          );
          const crow = curClampedRes.status === 'fulfilled' ? curClampedRes.value?.data.rows[0] : undefined;
          setClampedTotals(
            crow
              ? patchConversions(
                  totalsMetricKeys.map(mk => readMetricValue(crow, mk)),
                  convClampedRes.status === 'fulfilled' ? convClampedRes.value?.data.rows : undefined,
                )
              : null,
          );
          const prow = prevRes.status === 'fulfilled' ? prevRes.value?.data.rows[0] : undefined;
          setPriorTotals(
            prow
              ? patchConversions(
                  totalsMetricKeys.map(mk => readMetricValue(prow, mk)),
                  convPriorRes.status === 'fulfilled' ? convPriorRes.value?.data.rows : undefined,
                )
              : null,
          );
        })
        .catch(() => {
          /* widget data is best-effort; leave the card empty on error */
          if (!cancelled) setDataFailed(true);
        })
        .finally(() => {
          if (!cancelled) setDataLoading(false);
        });
    } else if (widget.type === 'geo') {
      const geoDim = GEO_LEVEL_DIMENSIONS[widget.geoLevel ?? 'state'];
      // Mapped levels also group by whatever extra dimension their boundary
      // lookup joins on — a code for Market, name+state for City (see
      // GEO_LEVEL_CODE_DIMENSIONS / GEO_LEVEL_STATE_DIMENSIONS above).
      const geoCodeDim = GEO_LEVEL_CODE_DIMENSIONS[widget.geoLevel ?? 'state'];
      const geoStateDim = GEO_LEVEL_STATE_DIMENSIONS[widget.geoLevel ?? 'state'];
      const geoCountryDim = GEO_LEVEL_COUNTRY_DIMENSIONS[widget.geoLevel ?? 'state'];
      const geoDimensions = [geoDim, geoCodeDim, geoStateDim, geoCountryDim].filter((d): d is string => Boolean(d));
      postReport(
        applyDashboardFilters(
          {
            dimensions: geoDimensions,
            metrics: [apiMetric],
            sort: [{ field: apiMetric, dir: 'desc' }],
            pagination: { limit: 500, offset: 0 },
          },
          effectiveFilters,
          tenantFields.dimensions,
        ),
        controller.signal,
      )
        .then(resp => {
          if (cancelled) return;
          recordRestrictedFields(resp.restricted_fields);
          recordFreshness(resp.freshness);
          setIgnoredFilters(resp.ignored_filters ?? []);
          setTableRows(applyWidgetTenantFields(resp.data.rows, widget, tenantFields));
        })
        .catch(() => {
          /* widget data is best-effort; leave the card empty on error */
          if (!cancelled) setDataFailed(true);
        })
        .finally(() => {
          if (!cancelled) setDataLoading(false);
        });
    } else if (widget.type === 'pivot') {
      // flank#5098 — a pivot is single-metric, so when that metric IS
      // conversions, `conversion_type` can be added as a 3rd dimension
      // directly (no other column to fragment, unlike Table/Totals below).
      const isConversionsPivot = apiMetric === 'conversions' && isConversionTypeSplittable([tableDimKey, pivotColKey]);
      postReport(
        applyDashboardFilters(
          {
            dimensions: isConversionsPivot ? [tableDimKey, pivotColKey, 'conversion_type'] : [tableDimKey, pivotColKey],
            metrics: [apiMetric],
            sort: [{ field: apiMetric, dir: 'desc' }],
            pagination: { limit: isConversionsPivot ? 1500 : 500, offset: 0 },
          },
          effectiveFilters,
          tenantFields.dimensions,
        ),
        controller.signal,
      )
        .then(resp => {
          if (cancelled) return;
          recordRestrictedFields(resp.restricted_fields);
          recordFreshness(resp.freshness);
          setIgnoredFilters(resp.ignored_filters ?? []);
          let rows = resp.data.rows;
          if (isConversionsPivot) {
            setConversionTypesSeen(availableConversionTypes(rows));
            const selected = resolveConversionType(widget.conversionType, availableConversionTypes(rows));
            rows = rows.filter(r => r.conversion_type === selected);
          }
          // A pivot groups by row *and* column dimension; collapsing on the
          // bucket alone would flatten every column into one row.
          setTableRows(applyWidgetTenantFields(rows, widget, tenantFields, [pivotColKey]));
        })
        .catch(() => {
          /* widget data is best-effort; leave the card empty on error */
          if (!cancelled) setDataFailed(true);
        })
        .finally(() => {
          if (!cancelled) setDataLoading(false);
        });
    } else if (widget.type === 'table') {
      const wantAllRowsTotal = widget.showTotalsRow !== false;
      // flank#5098 — `conversions` rides alongside other metric columns in
      // `tableMetricKeys`, so it can't take `conversion_type` as a dimension
      // on the main fetch without fragmenting every other column's rows by
      // type too (and the pivot/geo-only dims this widget can group by don't
      // all carry `conversion_type` — see isConversionTypeSplittable). A
      // separate, conversions-only overlay fetch patches just that column
      // after the fact, same convention as the Totals branch above.
      const conversionsIdx = tableMetricKeys.indexOf('conversions');
      const wantConversionOverlay = conversionsIdx !== -1 && isConversionTypeSplittable([tableDimKey]);
      // allSettled, not a plain .then chain: a failed all-rows aggregate must
      // not blank out the rows that DID come back (same reasoning as the
      // scorecard branch's current/prior pair above).
      Promise.allSettled([
        postReport(
          applyDashboardFilters(
            {
              dimensions: [tableDimKey],
              metrics: tableMetricKeys,
              sort: [{ field: tableMetricKeys[0], dir: 'desc' }],
              pagination: { limit: widget.rowLimit ?? 10, offset: 0 },
            },
            effectiveFilters,
            tenantFields.dimensions,
          ),
          controller.signal,
        ),
        wantAllRowsTotal
          ? postReport(
              applyDashboardFilters(
                { dimensions: [], metrics: tableMetricKeys, pagination: { limit: 1, offset: 0 } },
                effectiveFilters,
                tenantFields.dimensions,
              ),
              controller.signal,
            )
          : Promise.resolve(null),
        wantConversionOverlay
          ? postReport(
              applyDashboardFilters(
                {
                  dimensions: [tableDimKey, 'conversion_type'],
                  metrics: ['conversions'],
                  pagination: { limit: 500, offset: 0 },
                },
                effectiveFilters,
                tenantFields.dimensions,
              ),
              controller.signal,
            )
          : Promise.resolve(null),
      ])
        .then(([rowsRes, allRes, convRes]) => {
          if (cancelled) return;
          if (rowsRes.status !== 'fulfilled') {
            setDataFailed(true);
            return;
          }
          recordRestrictedFields(rowsRes.value.restricted_fields);
          recordFreshness(rowsRes.value.freshness);
          setIgnoredFilters(rowsRes.value.ignored_filters ?? []);
          let rows = applyWidgetTenantFields(rowsRes.value.data.rows, widget, tenantFields);
          setTableConversionsSplit(wantConversionOverlay && convRes.status === 'fulfilled' && !!convRes.value);
          if (wantConversionOverlay && convRes.status === 'fulfilled' && convRes.value) {
            const overlayRows = convRes.value.data.rows;
            setConversionTypesSeen(availableConversionTypes(overlayRows));
            // CodeRabbit (PR #5111) — for a custom dimension, `tableDimKey` is
            // `customDim.field` and the main rows are already bucketed into
            // labels by applyWidgetTenantFields, but the overlay rows still
            // carry raw values. Bucket the overlay the same way, keeping
            // conversion_type as a group key so the per-type split survives.
            const byGroup = buildConversionsByGroup(
              customDim
                ? (aggregateByCustomDimension(
                    overlayRows,
                    customDim,
                    ['conversions'],
                    [],
                    ['conversion_type'],
                  ) as ReportRow[])
                : overlayRows,
              resolveConversionType(widget.conversionType, availableConversionTypes(overlayRows)),
              tableDimKey,
            );
            // A group with no row at all for this type genuinely has 0 of it
            // (AdsWizz reporting 0 web conversions is as real as the 962
            // listen-throughs it does report) — `?? 0`, not `—`.
            rows = rows.map(r => ({ ...r, conversions: byGroup.get(String(r[tableDimKey] ?? '')) ?? 0 }));
          }
          setTableRows(rows);
          const allRow = allRes.status === 'fulfilled' ? allRes.value?.data.rows[0] : undefined;
          // flank#5051: this is an ungrouped, multi-platform aggregate — Reach,
          // Frequency, Conversions, CPA and ROAS can't be answered honestly
          // across platforms (same reasoning as computeTotalsRow's
          // NEVER_TOTAL_KEYS), so mask them here too rather than trust
          // whatever number the API happens to answer with for them.
          // flank#5054: same for a permission-denied Client Spend/CPM from a
          // saved widget config predating the permission being revoked.
          setAllRowsTotal(
            allRow
              ? {
                  sig: tableMetricsSig,
                  values: maskPermissionDeniedValues(
                    tableMetricKeys,
                    maskNeverTotalValues(tableMetricKeys, mk => readMetricValue(allRow, mk)),
                    permissions,
                  ),
                }
              : null,
          );
        })
        .catch(() => {
          /* widget data is best-effort; leave the card empty on error */
          if (!cancelled) setDataFailed(true);
        })
        .finally(() => {
          if (!cancelled) setDataLoading(false);
        });
    } else {
      setDataLoading(false);
    }

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [
    widget.type,
    widget.metric,
    widget.dimension,
    widget.rowLimit,
    widget.sortOrder,
    tableDimKey,
    tableMetricsSig,
    chartMetricsSig,
    chartDimKey,
    timeGrain,
    filtersSig,
    totalsMetricsSig,
    widget.showDelta,
    widget.geoLevel,
    // flank#5098 — KPI, Pivot, Table and Totals all resolve conversion_type
    // from widget.conversionType inside this effect; without it here,
    // toggling Web/Listen-through relabels the header but never re-fetches.
    widget.conversionType,
    pivotColKey,
    allSuppressed,
    refreshNonce,
    comparisonEndDep,
    // Serialised: the object identity changes on every render, but the widget
    // only needs to re-run when a definition actually differs.
    tenantFieldsSignature,
    // flank#5062: canViewPlatform is deliberately NOT a dependency here — a
    // Bar/Donut row keeps its raw warehouse value (`raw`) and relabels fresh
    // on every render via chartRowLabel, so a permission change relabels
    // instantly with no refetch (Lucas, PR review #5105).
  ]);

  // Report an export-ready snapshot of whatever this widget is currently
  // showing (#4414 PPTX export) — same "export what's on screen" rule as the
  // Unified Reporting CSV/Excel export. Chart widgets hand back a getter
  // rather than a base64 string so the image is only rendered when someone
  // actually exports, not on every data refresh.
  useEffect(() => {
    if (!onExportReady) return;
    const title = widget.title || WIDGET_TYPES.find(t => t.type === widget.type)?.label || widget.type;
    if (dataLoading || dataFailed) {
      // Don't re-export whatever this widget last successfully showed — a
      // request in flight or a failed refetch must not silently export data
      // from before the filter/definition change that triggered it. Distinct
      // reasons: a failed widget won't resolve on its own the way a loading
      // one will, so CSV/Excel's completeness check (#4458) needs to tell
      // "still waiting" apart from "this one is stuck" rather than showing a
      // "waiting to load" message forever (CodeRabbit).
      onExportReady(widget.id, {
        kind: 'skip',
        id: widget.id,
        title,
        reason: dataFailed ? 'This widget failed to load.' : 'Not ready to export yet.',
      });
      return;
    }
    if (widget.type === 'text' || widget.type === 'callout') {
      onExportReady(widget.id, { kind: 'text', id: widget.id, title, text: widget.text ?? '' });
    } else if (widget.type === 'kpi') {
      const mk = toMetricKey(widget.metric ?? 'impressions');
      onExportReady(widget.id, {
        kind: 'metrics',
        id: widget.id,
        title,
        metrics:
          kpiValue == null ? [] : [{ label: metricLabel(mk), value: formatMetricValue(mk, kpiValue), raw: kpiValue }],
      });
    } else if (WIDGET_TOTALS_TYPES.has(widget.type)) {
      onExportReady(widget.id, {
        kind: 'metrics',
        id: widget.id,
        title,
        metrics: totals
          ? totalsMetricKeys.map((mk, i) => ({
              label: metricLabel(mk),
              value: formatMetricValue(mk, totals[i]),
              raw: totals[i],
            }))
          : [],
      });
    } else if (widget.type === 'pivot') {
      // Same rows × columns crosstab showPivot renders below — including its
      // row/column caps (rowLimit rows, top 6 columns) — so the file matches
      // what's on screen instead of dumping every raw row (flank#4458 follow-up:
      // pivot fell through to 'skip' with no export, unlike every other data
      // widget type, and a dashboard with only a pivot and a KPI produced a
      // near-empty file with no indication anything was left out).
      const metricKey = toMetricKey(widget.metric ?? 'impressions');
      const rowKeys: string[] = [];
      const colKeys: string[] = [];
      const cells = new Map<string, number>();
      for (const r of tableRows) {
        const rk = String(r[tableDimKey] ?? '');
        const ck = String(r[pivotColKey] ?? '');
        if (!rowKeys.includes(rk)) rowKeys.push(rk);
        if (!colKeys.includes(ck)) colKeys.push(ck);
        const key = pivotCellKey(rk, ck);
        cells.set(key, (cells.get(key) ?? 0) + Number(r[metricKey] ?? 0));
      }
      const shownRows = rowKeys.slice(0, widget.rowLimit ?? 10);
      const shownCols = colKeys.slice(0, 6);
      const dimLabel = widget.dimension ?? 'DSP';
      // A pivot's columns are data values (e.g. product names), unlike a plain
      // table's fixed metric-label columns, so one can coincidentally match
      // the row dimension's own label (a product literally named "Channel"
      // next to a "Channel" row dimension) — sharing that object key would
      // silently overwrite the row label with the cell value (CodeRabbit).
      // Checked against the DISPLAY label (flank#5062), not the raw value —
      // that's what actually gets printed as the header, so that's the
      // collision that matters. `shownCols[i]` (raw, for `cells` lookups) and
      // `columnKeys[i]` (display, for the printed header/object key) stay
      // index-aligned throughout.
      const columnKeys = shownCols
        .map(ck => dashboardDimensionLabel(pivotColKey, ck, permissions.canViewPlatform))
        .map(ck => (ck === dimLabel ? `${ck} (2)` : ck));
      const snapshotRows = shownRows.map(rk => {
        const out: Record<string, unknown> = {
          [dimLabel]: dashboardDimensionLabel(tableDimKey, rk, permissions.canViewPlatform),
        };
        shownCols.forEach((ck, i) => {
          const v = cells.get(pivotCellKey(rk, ck));
          out[columnKeys[i]] = v == null ? '—' : formatMetricValue(metricKey, v);
        });
        return out;
      });
      const rawRows: Array<Record<string, number | null>> = shownRows.map(rk => {
        const out: Record<string, number | null> = {};
        shownCols.forEach((ck, i) => {
          out[columnKeys[i]] = cells.get(pivotCellKey(rk, ck)) ?? null;
        });
        return out;
      });
      onExportReady(widget.id, {
        kind: 'table',
        id: widget.id,
        title,
        table: { columns: [dimLabel, ...columnKeys], rows: snapshotRows, rawRows },
      });
    } else if (widget.type === 'table') {
      const snapshotRows = tableRows.map(row => {
        const raw = String(row[tableDimKey] ?? '');
        const out: Record<string, unknown> = {
          [widget.dimension ?? 'DSP']: dashboardDimensionLabel(tableDimKey, raw, permissions.canViewPlatform),
        };
        for (const mk of tableMetricKeys)
          out[metricLabel(mk)] = row[mk] == null ? '—' : formatMetricValue(mk, Number(row[mk]));
        return out;
      });
      // Raw numeric value per metric column, parallel to snapshotRows — CSV
      // and Excel export need a real number to SUM/sort, not
      // formatMetricValue's display string (flank#4553, CodeRabbit). The
      // dimension column has no entry here; it's never numeric.
      const rawRows: Array<Record<string, number | null>> = tableRows.map(row => {
        const out: Record<string, number | null> = {};
        for (const mk of tableMetricKeys) out[metricLabel(mk)] = row[mk] == null ? null : Number(row[mk]);
        return out;
      });
      // The live table renders a totals footer (widget.showTotalsRow !== false)
      // below whatever rows are shown — "export what's on screen" means the
      // PPTX table needs that row too, or the exported number set is quietly
      // incomplete next to the one on the dashboard (review, flank#4414). Only
      // when there ARE rows: an empty table shows its own empty state, not a
      // totals-only row with nothing to total.
      //
      // Two rows, not one (flank#5051): the first sums only the fetched
      // (capped) rows and says so — it used to read bare "Total", which a
      // 10-row table next to a Headline KPIs card reporting 5x the number
      // reads as the dashboard's real total. The second comes from the
      // all-rows aggregate fetched above (same filters/metrics, no grouping —
      // the same query shape the scorecard widget type already uses for its
      // own one-row total), so it agrees with that card by construction.
      if (tableRows.length > 0 && widget.showTotalsRow !== false) {
        // flank#5054: a saved widget can still carry Client Spend/CPM from
        // before a permission was revoked (or shared with a user who never
        // had it) — mask it here too, not just in the picker that offers it.
        const rowTotals = maskPermissionDeniedValues(
          tableMetricKeys,
          computeTotalsRow(tableRows, tableMetricKeys),
          permissions,
        );
        const totalRow: Record<string, unknown> = {
          [widget.dimension ?? 'DSP']: `Total (top ${tableRows.length})`,
        };
        const rawTotalRow: Record<string, number | null> = {};
        tableMetricKeys.forEach((mk, i) => {
          const total = rowTotals[i];
          totalRow[metricLabel(mk)] = total == null ? '—' : formatMetricValue(mk, total);
          rawTotalRow[metricLabel(mk)] = total ?? null;
        });
        snapshotRows.push(totalRow);
        rawRows.push(rawTotalRow);

        const allRow: Record<string, unknown> = {
          [widget.dimension ?? 'DSP']: `All ${dashboardDimensionPlural(widget.dimension ?? 'DSP')}`,
        };
        const rawAllRow: Record<string, number | null> = {};
        tableMetricKeys.forEach((mk, i) => {
          const total = safeAllRowsTotal?.[i] ?? null;
          allRow[metricLabel(mk)] = total == null ? '—' : formatMetricValue(mk, total);
          rawAllRow[metricLabel(mk)] = total;
        });
        snapshotRows.push(allRow);
        rawRows.push(rawAllRow);
      }
      onExportReady(widget.id, {
        kind: 'table',
        id: widget.id,
        title,
        table: {
          columns: [widget.dimension ?? 'DSP', ...tableMetricKeys.map(metricLabel)],
          rows: snapshotRows,
          rawRows,
        },
      });
    } else if (
      (widget.type === 'line' || widget.type === 'area' || widget.type === 'bar' || widget.type === 'donut') &&
      chartRows.length === 0
    ) {
      // No chart rows means the chart never mounted, so chartRef.current is
      // null and getImage() would report "not available at export time" —
      // a technical-sounding message for what's actually just an empty
      // result, distinct from every other widget's own "No data for the
      // current filters." (review, flank#4414). Reusing the 'metrics' empty
      // state gets that same message for free instead of a new snapshot kind.
      onExportReady(widget.id, { kind: 'metrics', id: widget.id, title, metrics: [] });
    } else if (widget.type === 'line' || widget.type === 'area' || widget.type === 'bar' || widget.type === 'donut') {
      // Datasets on a bar/line chart are one per chartMetricKeys entry (see
      // buildSeries); a donut only ever plots widget.metric as its one
      // dataset. Reused for the datalabels formatter too, so an exported
      // spend/CTR/ratio metric keeps its own format instead of a plain count
      // (review, flank#4414).
      const exportMetricKeys =
        widget.type === 'donut' ? [toMetricKey(widget.metric ?? 'impressions')] : chartMetricKeys;
      onExportReady(widget.id, {
        kind: 'chart',
        id: widget.id,
        title,
        subtitle: chartExportSubtitle(
          widget.type,
          widget.dimension,
          exportMetricKeys.map(metricLabel),
          Boolean(widget.normalizeChartAxes),
        ),
        getImage: () => captureChartImage(chartRef.current, exportMetricKeys),
      });
    } else {
      onExportReady(widget.id, { kind: 'skip', id: widget.id, title, reason: 'Not included in PPTX export yet.' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    onExportReady,
    widget.id,
    widget.type,
    widget.title,
    widget.text,
    widget.metric,
    widget.dimension,
    widget.normalizeChartAxes,
    widget.showTotalsRow,
    chartMetricsSig,
    kpiValue,
    chartRows,
    tableRows,
    totals,
    allRowsTotal,
    tableMetricsSig,
    dataLoading,
    dataFailed,
  ]);

  const showChart = (widget.type === 'line' || widget.type === 'area') && chartRows.length > 0;
  const showBar = widget.type === 'bar' && chartRows.length > 0;
  const showDonut = widget.type === 'donut' && chartRows.length > 0;
  // Suppression is handled by a branch ahead of every per-type branch, so this
  // only needs to answer "is this a KPI widget we should draw".
  const showKpi = widget.type === 'kpi';
  const showTable = widget.type === 'table' && tableRows.length > 0;

  const showGeo = widget.type === 'geo' && tableRows.length > 0;

  /**
   * A "Top DMAs" ranking is a ranking of places, so the unmapped bucket doesn't
   * belong in it. GAM reports no DMA at all — 120.9M impressions in a 30-day
   * window — which rendered as a single "Unknown" bar roughly 12x the largest
   * real metro, flattening every actual market against it.
   *
   * The volume is real and still counted everywhere else; it just isn't a row in
   * a list of top markets.
   */
  const geoLevelColumn = GEO_LEVEL_DIMENSIONS[widget.geoLevel ?? 'state'];
  const rankedGeoRows = tableRows.filter(row => {
    const value = String(row[geoLevelColumn] ?? '')
      .trim()
      .toLowerCase();
    return value !== '' && value !== 'unknown' && value !== 'unmapped';
  });
  const showPivot = widget.type === 'pivot' && tableRows.length > 0;

  // Rows came back but the grouped column is blank on every one of them: the
  // source doesn't carry this field, which is a source limitation to disclose
  // rather than an empty chart to puzzle over (Bonneville FB-009). Measured
  // from the rows, so a dimension starts working on its own once a platform
  // begins returning it — no list to keep in sync.
  // Named rather than keyed: "Device" means something to a partner reading a
  // dashboard; "device_ids" does not.
  const ignoredFilterNote = useMemo(() => {
    if (ignoredFilters.length === 0) return null;
    const names = ignoredFilters.map(key => FILTER_LABELS[key] ?? key);
    const list = names.length > 1 ? `${names.slice(0, -1).join(', ')} and ${names.at(-1)}` : names[0];
    return `${list} ${names.length > 1 ? 'filters do' : 'filter does'} not apply to this breakdown — the source doesn't carry ${names.length > 1 ? 'those fields' : 'that field'} alongside it, so ${names.length > 1 ? 'they were' : 'it was'} not applied to these numbers.`;
  }, [ignoredFilters]);

  const unavailableDetail = useMemo(
    () => dimensionUnavailableDetail(widget.dimension ?? 'This breakdown', tableRows, tableDimKey),
    [widget.dimension, tableRows, tableDimKey],
  );
  const showScorecard = widget.type === 'scorecard' && totals !== null;
  const showGoal = widget.type === 'goal' && totals !== null;
  const showFunnel = widget.type === 'funnel' && totals !== null;
  const showPacing = widget.type === 'pacing' && totals !== null;

  // flank#5098 — resolved once per render from this widget's own data, not
  // just its saved preference: a type the viewer picked that this filter
  // selection no longer has (e.g. they narrowed to a DSP with no
  // listen-throughs) falls back rather than showing a stale, now-impossible
  // choice as still selected.
  const selectedConversionType = resolveConversionType(widget.conversionType, conversionTypesSeen);
  // KPI/Pivot show one metric (`widget.metric`); Table shows a column per
  // `tableMetricKeys` entry; Scorecard/Goal/Funnel/Pacing show one card per
  // `totalsMetricKeys` entry. Donut/Bar/Line/Area aren't covered yet — see
  // the fetch effect's own note on why chart series need a separate pass.
  const touchesConversions =
    ((widget.type === 'kpi' || widget.type === 'pivot') &&
      toMetricKey(widget.metric ?? 'impressions') === 'conversions') ||
    (widget.type === 'table' && tableMetricKeys.includes('conversions')) ||
    (WIDGET_TOTALS_TYPES.has(widget.type) && totalsMetricKeys.includes('conversions'));

  // Filter-inheritance badge (data widgets only — text/image don't query).
  const inherited = inheritedCount(filters, widget.widgetFilters);
  const overrides = overrideCount(widget.widgetFilters);

  return (
    <Paper variant='outlined' sx={{ borderRadius: 2, overflow: 'hidden', position: 'relative' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 2,
          py: 1,
          borderBottom: '1px solid',
          borderColor: 'divider',
          gap: 1,
        }}
      >
        {editing && (
          <Box {...dragHandleProps} sx={{ display: 'flex', alignItems: 'center', mr: 0.5, cursor: 'grab' }}>
            <DragIndicatorRounded sx={{ fontSize: 18, color: 'text.disabled' }} />
          </Box>
        )}
        <Typography variant='body2' sx={{ fontWeight: 600, flexGrow: 1 }}>
          {widget.title}
        </Typography>
        {touchesConversions && conversionTypesSeen.length > 1 && (
          <MuiTooltip title="Web, Listen-through and Foot traffic conversions can't be added together — pick which type this widget shows, the same as Unified Reporting.">
            <ToggleButtonGroup
              size='small'
              exclusive
              value={selectedConversionType}
              onChange={(_e, v) => v && onConversionTypeChange(v)}
              sx={{ '& .MuiToggleButton-root': { px: 1, py: 0.25, fontSize: 11, textTransform: 'none' } }}
            >
              {conversionTypesSeen.map(t => (
                <ToggleButton key={t} value={t}>
                  {CONVERSION_TYPE_LABELS[t]}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </MuiTooltip>
        )}
        {WIDGET_DATA_TYPES.has(widget.type) && (
          <Chip
            size='small'
            variant='outlined'
            // Wording matches the flank-20 design source ("Extend · N inherited"
            // / "Extend · +N widget") so the two surfaces read as one product.
            label={
              overrides > 0
                ? `Extend · +${overrides} widget${overrides === 1 ? '' : 's'}`
                : `Extend · ${inherited} inherited`
            }
            sx={{
              height: 22,
              borderRadius: 1,
              fontSize: 11,
              color: overrides > 0 ? 'primary.main' : 'text.secondary',
              borderColor: overrides > 0 ? 'primary.main' : 'divider',
            }}
          />
        )}
        {!hideExpand && WIDGET_DATA_TYPES.has(widget.type) && (
          <IconButton size='small' onClick={() => setExpandOpen(true)} aria-label='Expand widget'>
            <FullscreenRounded sx={{ fontSize: 16 }} />
          </IconButton>
        )}
        {editing && (
          <>
            <IconButton size='small' onClick={onConfigure} aria-label='Configure widget'>
              <SettingsRounded sx={{ fontSize: 16 }} />
            </IconButton>
            <IconButton size='small' onClick={onDuplicate} aria-label='Duplicate widget'>
              <ContentCopyRounded sx={{ fontSize: 15 }} />
            </IconButton>
            <IconButton size='small' onClick={onRemove} aria-label='Remove widget'>
              <CloseRounded sx={{ fontSize: 16 }} />
            </IconButton>
          </>
        )}
      </Box>

      <Box
        sx={{
          p: showChart || showBar || showDonut || showFunnel || showGeo ? 2 : 3,
          minHeight: widgetBodyMinHeight(widget.rowSpan),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {dataLoading ? (
          <Box sx={{ width: '100%' }}>
            <Skeleton
              variant='rectangular'
              height={showChart || showBar || showDonut ? 120 : 48}
              sx={{ borderRadius: 1 }}
            />
          </Box>
        ) : allSuppressed ? (
          // Must come before every per-type branch: the `widget.type === 'kpi'`
          // fallback below renders a bare em-dash, so a suppressed KPI used to
          // land there and look like missing data instead of explaining itself.
          <Typography variant='body2' color='text.secondary' sx={{ textAlign: 'center' }}>
            {suppressionNote}
          </Typography>
        ) : showKpi ? (
          <Box sx={{ width: '100%' }}>
            {/* toMetricKey, not a raw comparison: legacy dashboards can still store the
                friendly label ("Conversions") in widget.metric — the KPI query already
                normalizes it via toMetricKey, so the drill-down gate has to match. */}
            {toMetricKey(widget.metric ?? '') === 'conversions' ? (
              // A real button, not a clickable Typography (CodeRabbit,
              // flank#4415): keyboard-reachable and has an accessible name,
              // while keeping the h3 visual weight.
              <ButtonBase
                onClick={() => setConversionModalOpen(true)}
                aria-label='View conversion action breakdown'
                sx={{
                  display: 'block',
                  textAlign: 'left',
                  borderRadius: 1,
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                <Typography variant='h3' sx={{ fontWeight: 700 }}>
                  {kpiValue !== null ? formatMetricValue(toMetricKey(widget.metric ?? 'impressions'), kpiValue) : '—'}
                </Typography>
              </ButtonBase>
            ) : kpiValue === null && !dataFailed && notApplicableReason(toMetricKey(widget.metric ?? 'impressions')) ? (
              // CodeRabbit: gated on !dataFailed — a failed request also
              // leaves kpiValue null, and shouldn't be misread as "this
              // metric doesn't apply" (e.g. CTR's clickable-media note).
              // tabIndex so a keyboard user can focus the dash and open the
              // tooltip, not just hover it.
              <MuiTooltip title={notApplicableReason(toMetricKey(widget.metric ?? 'impressions'))}>
                <Typography variant='h3' tabIndex={0} sx={{ fontWeight: 700, width: 'fit-content' }}>
                  —
                </Typography>
              </MuiTooltip>
            ) : (
              <Typography variant='h3' sx={{ fontWeight: 700 }}>
                {kpiValue !== null ? formatMetricValue(toMetricKey(widget.metric ?? 'impressions'), kpiValue) : '—'}
              </Typography>
            )}
            {kpiDelta !== null && widget.showDelta !== false && (
              <Typography
                variant='body2'
                sx={{ fontWeight: 600, mt: 0.5, color: kpiDelta >= 0 ? '#16a34a' : '#dc2626' }}
              >
                {kpiDelta >= 0 ? '↑' : '↓'} {Math.abs(kpiDelta).toFixed(1)}% vs {widget.compareTo ?? 'prior period'}
              </Typography>
            )}
            {kpiDelta === null && widget.showDelta !== false && (
              <Typography variant='caption' color='text.secondary'>
                vs {widget.compareTo ?? 'prior period'}
              </Typography>
            )}
            {toMetricKey(widget.metric ?? '') === 'conversions' && (
              <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mt: 0.5 }}>
                {CONVERSION_TYPE_LABELS[selectedConversionType]} conversions — click to see conversion actions
              </Typography>
            )}
            {toMetricKey(widget.metric ?? '') === 'conversions' && (
              <ConversionBreakdownModal
                open={conversionModalOpen}
                onClose={() => setConversionModalOpen(false)}
                filters={effectiveFilters}
                total={kpiValue}
              />
            )}
          </Box>
        ) : showChart ? (
          <Box sx={{ height: widgetChartHeight(widget.rowSpan), width: '100%' }}>
            <Line
              ref={chartRef}
              data={{
                labels: chartRows.map(r => chartRowLabel(r)),
                datasets: chartSeries.map((s, i) => ({
                  label: s.label,
                  data: normalizedChartRows
                    ? normalizedChartRows.map(row => row[i])
                    : chartRows.map(r => r.values[i] ?? 0),
                  yAxisID: widget.normalizeChartAxes ? 'y' : s.axis,
                  borderColor: s.color,
                  borderWidth: 2,
                  fill: widget.type === 'area',
                  backgroundColor: `${s.color}14`,
                  // Visible markers + a moderate hover target so an exact value
                  // can be read without hairline-precision hovering (#4410).
                  pointRadius: 3,
                  pointHoverRadius: 5,
                  pointBackgroundColor: s.color,
                  tension: 0.35,
                })),
              }}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                // Snap to the nearest point along the shared x position instead of
                // requiring the cursor sit exactly on a point (#4410).
                interaction: { mode: 'nearest', axis: 'x', intersect: false },
                hover: { mode: 'nearest', axis: 'x', intersect: false },
                plugins: {
                  legend: {
                    display: chartSeries.length > 1,
                    position: 'bottom',
                    // Bumped up when this is the enlarged instance inside the expand
                    // Dialog (#4434) — the fixed small-tile size otherwise stays tiny
                    // while the plot area around it scales up with the container.
                    labels: {
                      boxWidth: hideExpand ? 14 : 10,
                      font: { size: hideExpand ? 14 : 10 },
                      padding: hideExpand ? 12 : 8,
                    },
                  },
                  datalabels: { display: false },
                  tooltip: {
                    callbacks: {
                      label: tooltipLabelCallback(chartSeries, normalizedChartRows ? chartRows : undefined),
                    },
                  },
                },
                scales: buildScales(chartSeries, { normalize: widget.normalizeChartAxes }),
              }}
            />
          </Box>
        ) : showBar ? (
          <>
            <Box sx={{ height: widgetChartHeight(widget.rowSpan), width: '100%' }}>
              <Bar
                ref={chartRef}
                data={{
                  labels: chartRows.map(r => chartRowLabel(r)),
                  datasets: chartSeries.map((s, i) => ({
                    label: s.label,
                    data: chartRows.map(r => r.values[i] ?? 0),
                    backgroundColor: s.color,
                    borderRadius: 3,
                  })),
                }}
                options={{
                  indexAxis: widget.orientation === 'vertical' ? 'x' : 'y',
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      display: chartSeries.length > 1,
                      position: 'bottom',
                      // See the Line chart's legend above (#4434) — same fixed-size fix.
                      labels: {
                        boxWidth: hideExpand ? 14 : 10,
                        font: { size: hideExpand ? 14 : 10 },
                        padding: hideExpand ? 12 : 8,
                      },
                    },
                    datalabels: { display: false },
                    tooltip: { callbacks: { label: tooltipLabelCallback(chartSeries) } },
                  },
                  scales: {
                    x: { ticks: { font: { size: hideExpand ? 14 : 10 } } },
                    y: { ticks: { font: { size: hideExpand ? 14 : 10 } } },
                  },
                }}
              />
            </Box>
            {channelUnmappedNote && (
              <Typography variant='caption' color='text.secondary' sx={{ mt: 0.5, display: 'block' }}>
                {channelUnmappedNote}
              </Typography>
            )}
          </>
        ) : showDonut ? (
          <>
            <Box sx={{ height: widgetMapHeight(widget.rowSpan), width: '100%' }}>
              <Doughnut
                ref={chartRef}
                data={{
                  labels: chartRows.map(r => chartRowLabel(r)),
                  datasets: [
                    {
                      data: chartRows.map(r => r.values[0] ?? 0),
                      backgroundColor: chartRows.map((_, i) =>
                        i === 0 && accent ? accent : DONUT_PALETTE[i % DONUT_PALETTE.length],
                      ),
                      borderWidth: 0,
                    },
                  ],
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  cutout: '62%',
                  plugins: {
                    legend: {
                      position: 'right',
                      // See the Line chart's legend above (#4434) — same fixed-size fix.
                      labels: {
                        boxWidth: hideExpand ? 14 : 10,
                        font: { size: hideExpand ? 14 : 10 },
                        padding: hideExpand ? 10 : 6,
                      },
                    },
                    datalabels: { display: false },
                    tooltip: {
                      callbacks: {
                        label: ctx =>
                          `${ctx.label}: ${formatMetricValue(toMetricKey(widget.metric ?? 'impressions'), Number(ctx.parsed))}`,
                      },
                    },
                  },
                }}
              />
            </Box>
            {channelUnmappedNote && (
              <Typography variant='caption' color='text.secondary' sx={{ mt: 0.5, display: 'block' }}>
                {channelUnmappedNote}
              </Typography>
            )}
          </>
        ) : showGeo ? (
          // State, Market, City, and ZIP all render via Mapbox GL — see
          // GEO_LEVEL_DIMENSIONS above for why.
          (widget.geoLevel ?? 'state') === 'state' ? (
            // Taller than the other widget bodies on purpose. A short box scales
            // the whole US down and strands it in horizontal whitespace instead
            // of filling the card. 300 + the legend reads at a usable size; the
            // component's 360 default overflowed this card and clipped Texas,
            // Florida and the AK/HI insets.
            <Box sx={{ width: '100%', height: 340 }}>
              <StateChoropleth
                chartHeight={300}
                rows={tableRows as never}
                labelKey='geo_state'
                valueKey={toMetricKey(widget.metric ?? 'impressions')}
                metricLabel={metricLabel(toMetricKey(widget.metric ?? 'impressions'))}
                formatValue={v => formatMetricValue(toMetricKey(widget.metric ?? 'impressions'), v)}
              />
            </Box>
          ) : widget.geoLevel === 'dma' ? (
            <DmaChoropleth
              rows={tableRows as never}
              labelKey='geo_dma'
              valueKey={toMetricKey(widget.metric ?? 'impressions')}
              codeKey='geo_dma_code'
              metricLabel={metricLabel(toMetricKey(widget.metric ?? 'impressions'))}
              formatValue={v => formatMetricValue(toMetricKey(widget.metric ?? 'impressions'), v)}
            />
          ) : widget.geoLevel === 'city' ? (
            <CityChoropleth
              rows={tableRows as never}
              labelKey='geo_city'
              valueKey={toMetricKey(widget.metric ?? 'impressions')}
              stateKey='geo_state_code'
              metricLabel={metricLabel(toMetricKey(widget.metric ?? 'impressions'))}
              formatValue={v => formatMetricValue(toMetricKey(widget.metric ?? 'impressions'), v)}
            />
          ) : widget.geoLevel === 'zip' ? (
            <ZipChoropleth
              rows={tableRows as never}
              labelKey='geo_zip'
              valueKey={toMetricKey(widget.metric ?? 'impressions')}
              metricLabel={metricLabel(toMetricKey(widget.metric ?? 'impressions'))}
              formatValue={v => formatMetricValue(toMetricKey(widget.metric ?? 'impressions'), v)}
            />
          ) : rankedGeoRows.length === 0 ? (
            // Filtering to a platform that reports no geo at this level (e.g.
            // GAM reports no DMA) empties the ranking. Say so rather than
            // drawing an empty chart.
            <Typography variant='body2' color='text.secondary' sx={{ textAlign: 'center', py: 4 }}>
              No {GEO_LEVEL_LABELS[widget.geoLevel ?? 'state']}-level delivery for the selected filters.
            </Typography>
          ) : (
            <Box sx={{ height: 200, width: '100%' }}>
              <Bar
                data={{
                  labels: rankedGeoRows.slice(0, 10).map(r => String(r[geoLevelColumn] ?? '')),
                  datasets: [
                    {
                      label: metricLabel(toMetricKey(widget.metric ?? 'impressions')),
                      data: rankedGeoRows
                        .slice(0, 10)
                        .map(r => Number(r[toMetricKey(widget.metric ?? 'impressions')] ?? 0)),
                      backgroundColor: '#3b82f6',
                      borderRadius: 3,
                    },
                  ],
                }}
                options={{
                  indexAxis: 'y',
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: { display: false },
                    datalabels: { display: false },
                    tooltip: {
                      callbacks: {
                        label: ctx =>
                          formatMetricValue(toMetricKey(widget.metric ?? 'impressions'), Number(ctx.raw ?? 0)),
                      },
                    },
                  },
                  scales: {
                    x: { ticks: { font: { size: 10 } } },
                    y: { ticks: { font: { size: 10 } } },
                  },
                }}
              />
            </Box>
          )
        ) : showPivot ? (
          (() => {
            // Rows × columns crosstab for one metric, built from the flat rows.
            const metricKey = toMetricKey(widget.metric ?? 'impressions');
            const rowKeys: string[] = [];
            const colKeys: string[] = [];
            const cells = new Map<string, number>();
            for (const r of tableRows) {
              const rk = String(r[tableDimKey] ?? '');
              const ck = String(r[pivotColKey] ?? '');
              if (!rowKeys.includes(rk)) rowKeys.push(rk);
              if (!colKeys.includes(ck)) colKeys.push(ck);
              const key = pivotCellKey(rk, ck);
              cells.set(key, (cells.get(key) ?? 0) + Number(r[metricKey] ?? 0));
            }
            const shownRows = rowKeys.slice(0, widget.rowLimit ?? 10);
            const shownCols = colKeys.slice(0, 6);
            const max = Math.max(...[...cells.values()], 1);
            return (
              <Box sx={{ width: '100%', overflowX: 'auto', alignSelf: 'stretch' }}>
                <Table size='small'>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>{widget.dimension ?? 'DSP'}</TableCell>
                      {shownCols.map(ck => (
                        <TableCell key={ck} align='right' sx={{ fontWeight: 600, fontSize: 12 }}>
                          {dashboardDimensionLabel(pivotColKey, ck, permissions.canViewPlatform)}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {shownRows.map(rk => (
                      <TableRow key={rk}>
                        <TableCell
                          sx={{
                            fontSize: 13,
                            whiteSpace: 'nowrap',
                            maxWidth: 160,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {dashboardDimensionLabel(tableDimKey, rk, permissions.canViewPlatform)}
                        </TableCell>
                        {shownCols.map(ck => {
                          const v = cells.get(pivotCellKey(rk, ck));
                          // heat-shade each cell by its share of the largest value
                          const alpha = v ? Math.max(0.06, Math.min(v / max, 1) * 0.55) : 0;
                          return (
                            <TableCell
                              key={ck}
                              align='right'
                              sx={{
                                fontSize: 13,
                                bgcolor: v ? `rgba(59,130,246,${alpha})` : undefined,
                              }}
                            >
                              {v == null ? '—' : formatMetricValue(metricKey, v)}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            );
          })()
        ) : showScorecard ? (
          <Box
            sx={{
              width: '100%',
              display: 'grid',
              // auto-fit so cards wrap instead of clipping in a narrow column
              gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))',
              gap: 2,
              rowGap: 1.5,
            }}
          >
            {totalsMetricKeys.map((mk, i) => {
              const v = totals?.[i] ?? null;
              const pv = priorTotals?.[i] ?? null;
              // flank#5049: the delta's numerator is the freshness-clamped
              // current value, not the unclamped `v` the card displays above
              // — an equal-length, equally-complete pair against `pv`.
              const deltaCur = clampedTotals?.[i] ?? null;
              const delta = deltaCur !== null && pv !== null && pv > 0 ? ((deltaCur - pv) / pv) * 100 : null;
              const isConversions = mk === 'conversions';
              // CodeRabbit: !dataFailed — a failed request must not be
              // misread as "this metric doesn't apply".
              const reason = v === null && !dataFailed ? notApplicableReason(mk) : undefined;
              const valueText = (
                <Typography
                  variant='h5'
                  tabIndex={reason ? 0 : undefined}
                  sx={{ fontWeight: 700, lineHeight: 1.2, width: 'fit-content' }}
                >
                  {formatMetricValue(mk, v)}
                </Typography>
              );
              const card = (
                <>
                  <Typography
                    variant='caption'
                    color='text.secondary'
                    sx={{ textTransform: 'uppercase', fontWeight: 600 }}
                  >
                    {isConversions ? conversionsLabel(selectedConversionType) : metricLabel(mk)}
                  </Typography>
                  {reason ? <MuiTooltip title={reason}>{valueText}</MuiTooltip> : valueText}
                  {delta !== null && (
                    <Typography variant='caption' sx={{ fontWeight: 600, color: delta >= 0 ? '#16a34a' : '#dc2626' }}>
                      {delta >= 0 ? '↑' : '↓'} {Math.abs(delta).toFixed(1)}%
                    </Typography>
                  )}
                </>
              );
              return (
                <Box key={mk}>
                  {isConversions ? (
                    <ButtonBase
                      onClick={() => setConversionModalOpen(true)}
                      aria-label='View conversion action breakdown'
                      sx={{
                        display: 'block',
                        textAlign: 'left',
                        borderRadius: 1,
                        '&:hover': { textDecoration: 'underline' },
                      }}
                    >
                      {card}
                    </ButtonBase>
                  ) : (
                    card
                  )}
                </Box>
              );
            })}
            {totalsMetricKeys.includes('conversions') && (
              <ConversionBreakdownModal
                open={conversionModalOpen}
                onClose={() => setConversionModalOpen(false)}
                filters={effectiveFilters}
                total={totals?.[totalsMetricKeys.indexOf('conversions')] ?? null}
              />
            )}
          </Box>
        ) : showGoal ? (
          (() => {
            const metricKey = totalsMetricKeys[0];
            // flank#5054: `?? 0` here used to turn readMetricValue's
            // genuine null (Client Spend/CPM restricted or unentered) into a
            // real $0.00 — keep it null and let formatMetricValue render the
            // "—" it already renders for every other null metric.
            const actual = totals?.[0] ?? null;
            const target = widget.goalTarget && widget.goalTarget > 0 ? widget.goalTarget : null;
            const pct = target && actual !== null ? Math.min((actual / target) * 100, 100) : 0;
            return (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                <Box sx={{ width: 110, height: 110, flexShrink: 0 }}>
                  <Doughnut
                    data={{
                      labels: ['Achieved', 'Remaining'],
                      datasets: [
                        {
                          data: [pct, 100 - pct],
                          backgroundColor: [accent ?? '#3b82f6', '#e5e7eb'],
                          borderWidth: 0,
                        },
                      ],
                    }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      cutout: '72%',
                      plugins: {
                        legend: { display: false },
                        tooltip: { enabled: false },
                        datalabels: { display: false },
                      },
                    }}
                  />
                </Box>
                <Box>
                  <Typography
                    variant='caption'
                    color='text.secondary'
                    sx={{ textTransform: 'uppercase', fontWeight: 600 }}
                  >
                    {metricKey === 'conversions' ? conversionsLabel(selectedConversionType) : metricLabel(metricKey)}
                  </Typography>
                  {(() => {
                    // CodeRabbit: !dataFailed — a failed request must not be
                    // misread as "this metric doesn't apply".
                    const reason = actual === null && !dataFailed ? notApplicableReason(metricKey) : undefined;
                    const text = (
                      <Typography
                        variant='h4'
                        tabIndex={reason ? 0 : undefined}
                        sx={{ fontWeight: 700, lineHeight: 1.2, width: 'fit-content' }}
                      >
                        {formatMetricValue(metricKey, actual)}
                      </Typography>
                    );
                    return reason ? <MuiTooltip title={reason}>{text}</MuiTooltip> : text;
                  })()}
                  <Typography variant='caption' sx={{ color: target ? '#16a34a' : 'text.secondary' }}>
                    {!target
                      ? 'Set a target in settings'
                      : actual === null
                        ? `Goal ≥ ${formatMetricValue(metricKey, target)}`
                        : `Goal ≥ ${formatMetricValue(metricKey, target)} · ${pct.toFixed(0)}%`}
                  </Typography>
                </Box>
              </Box>
            );
          })()
        ) : showFunnel ? (
          <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 1 }}>
            {totalsMetricKeys.map((mk, i) => {
              const v = totals?.[i] ?? 0;
              const first = totals?.[0] ?? 0;
              const prev = i > 0 ? (totals?.[i - 1] ?? 0) : 0;
              const widthPct = first > 0 ? Math.max((v / first) * 100, 1.5) : 0;
              const stagePct = i > 0 && prev > 0 ? (v / prev) * 100 : null;
              const isConversions = mk === 'conversions';
              const bar = (
                <>
                  <Box
                    sx={{
                      width: `${widthPct}%`,
                      bgcolor: '#3b82f6',
                      borderRadius: 0.5,
                      px: 1,
                      py: 0.5,
                      minWidth: 44,
                    }}
                  >
                    <Typography variant='caption' sx={{ color: '#fff', fontWeight: 700 }}>
                      {formatMetricValue(mk, v)}
                    </Typography>
                  </Box>
                  {stagePct !== null && (
                    <Typography
                      variant='caption'
                      color='text.secondary'
                      sx={{
                        position: 'absolute',
                        right: 6,
                        top: '50%',
                        transform: 'translateY(-50%)',
                      }}
                    >
                      {stagePct.toFixed(1)}%
                    </Typography>
                  )}
                </>
              );
              return (
                <Box key={mk} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography
                    variant='caption'
                    color='text.secondary'
                    sx={{ width: 92, flexShrink: 0, textAlign: 'right' }}
                  >
                    {isConversions ? conversionsLabel(selectedConversionType) : metricLabel(mk)}
                  </Typography>
                  {isConversions ? (
                    <ButtonBase
                      onClick={() => setConversionModalOpen(true)}
                      aria-label='View conversion action breakdown'
                      sx={{
                        flexGrow: 1,
                        position: 'relative',
                        bgcolor: 'action.hover',
                        borderRadius: 0.5,
                        justifyContent: 'flex-start',
                        '&:hover': { opacity: 0.85 },
                      }}
                    >
                      {bar}
                    </ButtonBase>
                  ) : (
                    <Box
                      sx={{
                        flexGrow: 1,
                        position: 'relative',
                        bgcolor: 'action.hover',
                        borderRadius: 0.5,
                      }}
                    >
                      {bar}
                    </Box>
                  )}
                </Box>
              );
            })}
            {totalsMetricKeys.includes('conversions') && (
              <ConversionBreakdownModal
                open={conversionModalOpen}
                onClose={() => setConversionModalOpen(false)}
                filters={effectiveFilters}
                total={totals?.[totalsMetricKeys.indexOf('conversions')] ?? null}
              />
            )}
          </Box>
        ) : showPacing ? (
          (() => {
            const metricKey = totalsMetricKeys[0];
            const actual = totals?.[0] ?? 0;
            const target = widget.goalTarget && widget.goalTarget > 0 ? widget.goalTarget : null;
            // Expected = target pro-rated by how much of the window has elapsed.
            const start = new Date(`${effectiveFilters.dateStart}T00:00:00`);
            const end = new Date(`${effectiveFilters.dateEnd}T00:00:00`);
            const totalDays = Math.max(Math.round((end.getTime() - start.getTime()) / 86_400_000) + 1, 1);
            const elapsed = Math.min(
              Math.max(Math.round((Date.now() - start.getTime()) / 86_400_000) + 1, 0),
              totalDays,
            );
            // A trailing window (ending today) is fully elapsed, so pro-rating
            // degenerates to the whole target — report against the goal instead
            // of using pace language that would always read "behind".
            const inFlight = elapsed < totalDays;
            const expected = target ? (inFlight ? (target * elapsed) / totalDays : target) : null;
            const pct = target ? Math.min((actual / target) * 100, 100) : 0;
            const ahead = expected !== null ? actual >= expected : null;
            return (
              <Box sx={{ width: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
                  <Typography
                    variant='caption'
                    color='text.secondary'
                    sx={{ textTransform: 'uppercase', fontWeight: 600 }}
                  >
                    {metricLabel(metricKey)} pacing
                  </Typography>
                  {ahead !== null && (
                    <Typography variant='caption' sx={{ fontWeight: 700, color: ahead ? '#16a34a' : '#f59e0b' }}>
                      {inFlight ? (ahead ? 'Ahead of pace' : 'Behind pace') : ahead ? 'Goal met' : 'Short of goal'}
                    </Typography>
                  )}
                </Box>
                <Typography variant='h4' sx={{ fontWeight: 700, lineHeight: 1.3 }}>
                  {formatMetricValue(metricKey, actual)}
                </Typography>
                <Typography variant='caption' color='text.secondary'>
                  {target
                    ? `of ${formatMetricValue(metricKey, target)} goal · ${pct.toFixed(0)}% delivered`
                    : 'Set a target in settings'}
                </Typography>
                <Box
                  sx={{
                    mt: 1,
                    height: 8,
                    bgcolor: 'action.hover',
                    borderRadius: 4,
                    position: 'relative',
                  }}
                >
                  <Box sx={{ width: `${pct}%`, height: '100%', bgcolor: '#3b82f6', borderRadius: 4 }} />
                  {expected !== null && inFlight && (
                    <Box
                      sx={{
                        position: 'absolute',
                        left: `${Math.min((expected / (target ?? 1)) * 100, 100)}%`,
                        top: -2,
                        width: 2,
                        height: 12,
                        bgcolor: 'text.primary',
                      }}
                    />
                  )}
                </Box>
                {expected !== null && inFlight && (
                  <Typography variant='caption' color='text.secondary' sx={{ mt: 0.5, display: 'block' }}>
                    Expected {formatMetricValue(metricKey, expected)} by today ({elapsed} of {totalDays} days elapsed)
                  </Typography>
                )}
              </Box>
            );
          })()
        ) : showTable ? (
          <Box sx={{ width: '100%', overflowX: 'auto', alignSelf: 'stretch' }}>
            <Table size='small'>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>{widget.dimension ?? 'DSP'}</TableCell>
                  {tableMetricKeys.map(mk => (
                    <TableCell key={mk} align='right' sx={{ fontWeight: 600, fontSize: 12 }}>
                      {mk === 'conversions' && tableConversionsSplit
                        ? conversionsLabel(selectedConversionType)
                        : metricLabel(mk)}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {tableRows.map((row, ri) => (
                  <TableRow key={`${String(row[tableDimKey] ?? '')}-${ri}`}>
                    <TableCell
                      sx={{
                        fontSize: 13,
                        whiteSpace: 'nowrap',
                        maxWidth: 220,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {dashboardDimensionLabel(
                        tableDimKey,
                        String(row[tableDimKey] ?? ''),
                        permissions.canViewPlatform,
                      )}
                    </TableCell>
                    {tableMetricKeys.map(mk => (
                      <TableCell key={mk} align='right' sx={{ fontSize: 13 }}>
                        {row[mk] == null ? '—' : formatMetricValue(mk, Number(row[mk]))}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
              {widget.showTotalsRow !== false && (
                <TableFooter>
                  <TableRow sx={{ '& td': { borderTop: '2px solid', borderTopColor: 'divider', fontWeight: 700 } }}>
                    <TableCell sx={{ fontSize: 13 }}>Total (top {tableRows.length})</TableCell>
                    {maskPermissionDeniedValues(
                      tableMetricKeys,
                      computeTotalsRow(tableRows, tableMetricKeys),
                      permissions,
                    ).map((total, i) => (
                      <TableCell key={tableMetricKeys[i]} align='right' sx={{ fontSize: 13 }}>
                        {total == null ? '—' : formatMetricValue(tableMetricKeys[i], total)}
                      </TableCell>
                    ))}
                  </TableRow>
                  <TableRow sx={{ '& td': { fontWeight: 700 } }}>
                    <TableCell sx={{ fontSize: 13 }}>
                      All {dashboardDimensionPlural(widget.dimension ?? 'DSP')}
                    </TableCell>
                    {tableMetricKeys.map((mk, i) => {
                      const total = safeAllRowsTotal?.[i] ?? null;
                      return (
                        <TableCell key={mk} align='right' sx={{ fontSize: 13 }}>
                          {total == null ? '—' : formatMetricValue(mk, total)}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                </TableFooter>
              )}
            </Table>
          </Box>
        ) : widget.type === 'kpi' ? (
          <Box>
            <Typography variant='h3' sx={{ fontWeight: 700 }}>
              —
            </Typography>
            {widget.showDelta !== false && (
              <Typography variant='caption' color='text.secondary'>
                vs {widget.compareTo ?? 'prior period'}
              </Typography>
            )}
          </Box>
        ) : widget.type === 'image' ? (
          widget.imageUrl ? (
            <Box
              component='img'
              src={widget.imageUrl}
              alt={widget.title}
              sx={{
                width: '100%',
                maxHeight: 180,
                objectFit: widget.imageFit ?? 'contain',
                borderRadius: 1,
              }}
            />
          ) : (
            <Typography variant='body2' color='text.secondary'>
              Add an image URL in settings.
            </Typography>
          )
        ) : widget.type === 'text' ? (
          <Typography
            variant={TEXT_STYLE_VARIANTS[widget.textStyle ?? 'body']}
            color={widget.textStyle && widget.textStyle !== 'body' ? 'text.primary' : 'text.secondary'}
            sx={{ whiteSpace: 'pre-wrap', width: '100%', textAlign: widget.textAlign ?? 'left' }}
          >
            {widget.text || 'Empty text block — click the gear to add content.'}
          </Typography>
        ) : widget.type === 'divider' ? (
          <Divider sx={{ width: '100%' }}>
            {widget.text && (
              <Typography variant='overline' color='text.secondary'>
                {widget.text}
              </Typography>
            )}
          </Divider>
        ) : widget.type === 'callout' ? (
          <Alert severity={widget.calloutTone ?? 'info'} sx={{ width: '100%', textAlign: widget.textAlign ?? 'left' }}>
            {widget.text || 'Empty callout — click the gear to add commentary.'}
          </Alert>
        ) : ignoredFilters.length > 0 && tableRows.length === 0 ? (
          <Typography variant='body2' color='text.secondary' sx={{ textAlign: 'center' }}>
            {ignoredFilterNote}
          </Typography>
        ) : WIDGET_DATA_TYPES.has(widget.type) ? (
          // A data widget that fetched and got nothing back: say so, rather than
          // falling through to "configure via the gear icon", which reads as
          // "you set this up wrong" when the filters simply match no rows.
          //
          // "No matches" and "this platform never returns that field" look
          // identical on screen but need different fixes — the second is a
          // source limitation the partner has to be told about rather than a
          // filter to loosen (Bonneville FB-009).
          <Typography variant='body2' color='text.secondary' sx={{ textAlign: 'center' }}>
            {unavailableDetail ?? 'No data for the current filters.'}
          </Typography>
        ) : (
          <Typography variant='body2' color='text.secondary'>
            {WIDGET_TYPES.find(t => t.type === widget.type)?.label ?? widget.type} — configure via the gear icon.
          </Typography>
        )}
      </Box>

      {/* Some (not all) series were conversion-based and got dropped — caption the
          omission so a missing line isn't mistaken for missing data. */}
      {someSuppressed && (
        <Typography
          variant='caption'
          color='text.secondary'
          sx={{ display: 'block', px: 2, pb: 1.5, mt: -0.5, textAlign: 'center' }}
        >
          {suppressionNote}
        </Typography>
      )}

      {/* Corner resize handle — drags across the grid to set width and height.
          It used to read clientX only, which is why width could be adjusted and
          height could not (FB-012). */}
      {editing && onResize && (
        <Box
          role='separator'
          aria-label='Resize widget'
          onMouseDown={e => {
            e.preventDefault();
            e.stopPropagation();
            const card = (e.currentTarget as HTMLElement).parentElement;
            if (!card) return;
            const startX = e.clientX;
            const startY = e.clientY;
            const rect = card.getBoundingClientRect();
            const colWidth = rect.width / (widget.span ?? 1);
            const onMove = (ev: MouseEvent) => {
              const delta = ev.clientX - startX;
              const next = Math.min(3, Math.max(1, Math.round((widget.span ?? 1) + delta / colWidth)));
              if (next !== (widget.span ?? 1)) onResize(next as 1 | 2 | 3);
              if (onResizeRows) {
                const nextRows = nextRowSpanFromDrag(widget.rowSpan, rect.height, ev.clientY - startY);
                if (nextRows !== clampRowSpan(widget.rowSpan)) onResizeRows(nextRows);
              }
            };
            const onUp = () => {
              window.removeEventListener('mousemove', onMove);
              window.removeEventListener('mouseup', onUp);
            };
            window.addEventListener('mousemove', onMove);
            window.addEventListener('mouseup', onUp);
          }}
          sx={{
            position: 'absolute',
            right: 2,
            bottom: 2,
            width: 14,
            height: 14,
            cursor: 'ew-resize',
            borderRight: '2px solid',
            borderBottom: '2px solid',
            borderColor: 'text.disabled',
            borderBottomRightRadius: 3,
            opacity: 0.6,
            '&:hover': { opacity: 1, borderColor: 'primary.main' },
          }}
        />
      )}
      {expandOpen && (
        <Dialog open onClose={() => setExpandOpen(false)} maxWidth='lg' fullWidth>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {widget.title}
            <Box sx={{ flexGrow: 1 }} />
            <IconButton size='small' onClick={() => setExpandOpen(false)} aria-label='Close'>
              <CloseRounded sx={{ fontSize: 18 }} />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ pb: 3 }}>
            <WidgetCard
              widget={{ ...widget, rowSpan: 3 }}
              editing={false}
              filters={filters}
              tenantFields={tenantFields}
              refreshNonce={refreshNonce}
              accent={accent}
              // Not offered inside the expand dialog — editing={false} hides
              // the toolbar these would sit on, so they're never called.
              onConfigure={NOOP}
              onDuplicate={NOOP}
              onRemove={NOOP}
              onConversionTypeChange={onConversionTypeChange}
              hideExpand
            />
          </DialogContent>
        </Dialog>
      )}
    </Paper>
  );
};

// ─── Per-widget filter editor ("This widget" section) ────────────────────────

/**
 * One dimension row: shows what the widget inherits from the dashboard, and
 * lets the user override it for this widget only. Overriding REPLACES the
 * inherited selection for that dimension.
 */
const WidgetFilterRow = ({
  spec,
  scope,
  dashboardSelection,
  ownSelection,
  onChange,
}: {
  spec: { key: WidgetFilterKey; label: string; dimension: string };
  /** Campaign / Line Item: the widget's effective parents (see widgetEntityScope). */
  scope: OptionsScope;
  dashboardSelection: string[];
  ownSelection: string[];
  onChange: (next: string[]) => void;
}) => {
  const { options, loading, setSearch, labelOf } = useFilterOptions(spec.dimension, scope);
  const overriding = ownSelection.length > 0;

  return (
    <Box>
      <MultiSelectFilter
        label={spec.label}
        options={options}
        loading={loading}
        onSearchChange={setSearch}
        labelOf={labelOf}
        selected={ownSelection}
        onChange={onChange}
      />
      <Typography variant='caption' color='text.secondary' sx={{ mt: 0.25, display: 'block' }}>
        {overriding
          ? 'Overrides the dashboard filter for this widget'
          : dashboardSelection.length > 0
            ? `Inheriting ${dashboardSelection.length} from dashboard`
            : 'No dashboard filter — all values'}
      </Typography>
    </Box>
  );
};

// ─── Config panel ─────────────────────────────────────────────────────────────

const ConfigPanel = ({
  widget,
  dashboardFilters,
  filterBar,
  tenantFields,
  onSave,
  onClose,
}: {
  widget: WidgetConfig;
  dashboardFilters: DashboardFilters;
  filterBar: FilterBarConfig;
  tenantFields: TenantFields;
  onSave: (patch: Partial<WidgetConfig>) => void;
  onClose: () => void;
}) => {
  // flank#5054 — a metric or group-by the viewer has no permission to see at
  // all is left out of these pickers entirely, not merely disabled (that's
  // reserved for "not wired to postReport yet", a different concept — see
  // renderMetricOption's `unsupported` branch).
  const isRibeyeAdmin = useAppSelector(selectIsSuperAdmin);
  const permissions = useDashboardPermissions(isRibeyeAdmin);
  // Canonical metrics plus tenant ones, with hidden metrics dropped and tenant
  // labels applied. Every metric picker below reads from this, not METRIC_OPTIONS.
  const metricOptions = useMemo(
    () =>
      mergeMetricOptions(METRIC_OPTIONS, tenantFields).filter(opt => !isPermissionDeniedMetric(opt.value, permissions)),
    [tenantFields, permissions],
  );
  const dimensionEntries = useMemo(
    () =>
      [
        ...Object.keys(TABLE_DIMENSIONS).map(d => ({
          value: d,
          label: tenantFields.labels[TABLE_DIMENSIONS[d]] ?? d,
        })),
        // flank#5100 — a legacy custom dimension bucketed on `product` is the
        // same Product breakdown this ticket removes; drop it from the picker
        // too, not just the canonical `Product` entry.
        ...tenantFields.dimensions.filter(d => d.field !== 'product').map(d => ({ value: d.key, label: d.label })),
      ].filter(d => permissions.canViewPlatform || TABLE_DIMENSIONS[d.value] !== 'dsp'),
    [tenantFields, permissions],
  );
  const [title, setTitle] = useState(widget.title);
  const [metric, setMetric] = useState(toMetricKey(widget.metric ?? 'impressions'));
  const [showDelta, setShowDelta] = useState(widget.showDelta !== false);
  const [compareTo, setCompareTo] = useState(widget.compareTo ?? 'Prior period');
  const [text, setText] = useState(widget.text ?? '');
  const [textStyle, setTextStyle] = useState(widget.textStyle ?? 'body');
  const [textAlign, setTextAlign] = useState(widget.textAlign ?? 'left');
  const [calloutTone, setCalloutTone] = useState(widget.calloutTone ?? 'info');
  const [dimension, setDimension] = useState(widget.dimension ?? (widget.type === 'table' ? 'DSP' : 'Channel'));
  const defaultMetrics =
    widget.type === 'table'
      ? ['impressions', 'spend']
      : (DEFAULT_TOTALS_METRICS[widget.type] ?? [toMetricKey(widget.metric ?? 'impressions')]);
  const [metrics, setMetrics] = useState<string[]>(
    widget.metrics?.length ? widget.metrics.map(toMetricKey) : defaultMetrics,
  );
  const [normalizeChartAxes, setNormalizeChartAxes] = useState(widget.normalizeChartAxes ?? false);
  const [axisOverrides, setAxisOverrides] = useState<AxisOverrides>(widget.axisOverrides ?? {});
  const [showTotalsRow, setShowTotalsRow] = useState(widget.showTotalsRow !== false);
  const [rowLimit, setRowLimit] = useState<number>(widget.rowLimit ?? 10);
  const [sortOrder, setSortOrder] = useState<'desc' | 'asc'>(widget.sortOrder ?? 'desc');
  const [orientation, setOrientation] = useState<'horizontal' | 'vertical'>(widget.orientation ?? 'horizontal');
  const [timeGrain, setTimeGrain] = useState<'day' | 'week' | 'month'>(widget.timeGrain ?? 'day');
  const [widgetFilters, setWidgetFilters] = useState<WidgetFilters>(widget.widgetFilters ?? {});
  const [goalTarget, setGoalTarget] = useState<string>(widget.goalTarget != null ? String(widget.goalTarget) : '');
  const [span, setSpan] = useState<1 | 2 | 3>(widget.span ?? 1);
  const [rowSpan, setRowSpan] = useState<RowSpan>(clampRowSpan(widget.rowSpan));
  const [imageUrl, setImageUrl] = useState(widget.imageUrl ?? '');
  const [imageFit, setImageFit] = useState<'contain' | 'cover'>(widget.imageFit ?? 'contain');
  const [geoLevel, setGeoLevel] = useState<'state' | 'dma' | 'zip' | 'city'>(widget.geoLevel ?? 'state');
  const [columnDimension, setColumnDimension] = useState(widget.columnDimension ?? 'Channel');
  // Sections collapse like the flank-20 panel's chevrons. Open by default so
  // nothing is hidden from someone exploring; collapsing DISPLAY is how you get
  // the per-widget filters into view without a long scroll.
  const [displayOpen, setDisplayOpen] = useState(true);
  const [filtersOpen, setFiltersOpen] = useState(true);

  const typeInfo = WIDGET_TYPES.find(t => t.type === widget.type);
  const isDataWidget = WIDGET_DATA_TYPES.has(widget.type);

  const offeredFilterKeys = availableWidgetFilterKeys(filterBar, permissions);
  const inheritedRules = offeredFilterKeys.filter(
    ({ key }) => (dashboardFilters[key] ?? []).length > 0 && !widgetFilters[key]?.length,
  ).length;
  const ownRules = overrideCount(widgetFilters);

  // Gated metrics stay selectable even when they can't currently render — you
  // need to be able to build a conversions (or CTR) widget and have it appear
  // once a DSP / channel is isolated. Warn instead of disabling, recomputed
  // against this widget's own overrides so setting one here clears it live.
  const panelFilters = resolveWidgetFilters(dashboardFilters, widgetFilters, tenantFields.dimensions);
  const selectedMetrics = [metric, ...metrics];
  const conversionWarning =
    !conversionsVisible(panelFilters, tenantFields.dimensions) && selectedMetrics.some(isConversionMetric);
  const vanityWarning =
    !vanityMetricsVisible(panelFilters, tenantFields.dimensions) && selectedMetrics.some(isChannelSpecificMetric);
  // Checks the metric(s) actually rendered for THIS widget type, not the shared
  // `selectedMetrics` above — a single-metric type (kpi/donut/geo/pivot/goal/
  // pacing) only ever renders `metric`, and `metrics` can hold a stale value
  // left over from before the widget was last a multi-metric type, producing a
  // false warning after switching the metric back off Reach (CodeRabbit,
  // flank#4460).
  const usesSingleMetric = SINGLE_METRIC_WIDGETS.has(widget.type) || widget.type === 'goal' || widget.type === 'pacing';
  const reachRelevantMetrics = usesSingleMetric ? [metric] : metrics;
  const reachWarning = !reachVisible(panelFilters, tenantFields.dimensions) && reachRelevantMetrics.some(isReachMetric);
  // Week/Month bucketing averages the daily ratio (bucketRows) — sound for
  // CTR, but Frequency's reach denominator isn't deduplicated across days, so
  // a period average of daily frequencies understates the true figure. No
  // period-level calculation exists yet (CodeRabbit, flank#4460), so block the
  // grain rather than show a wrong number; only line/area charts read `metrics`
  // this way, which is exactly where Week/Month bucketing applies.
  const frequencyBlocksGrain = metrics.includes('frequency');
  useEffect(() => {
    if (frequencyBlocksGrain && timeGrain !== 'day') setTimeGrain('day');
  }, [frequencyBlocksGrain, timeGrain]);

  return (
    <Box sx={{ width: 340, p: 3, display: 'flex', flexDirection: 'column', gap: 2.5, height: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant='subtitle1' sx={{ fontWeight: 700 }}>
            {typeInfo?.label ?? widget.type}
          </Typography>
          <Typography variant='caption' color='text.secondary'>
            {typeInfo?.description}
          </Typography>
        </Box>
        <IconButton size='small' onClick={onClose}>
          <CloseRounded fontSize='small' />
        </IconButton>
      </Box>

      {/* Filter-inheritance summary, matching the Lovable panel's THIS WIDGET line */}
      {isDataWidget && (
        <Box sx={{ bgcolor: 'action.hover', borderRadius: 1, px: 1.5, py: 1 }}>
          <Typography variant='caption' color='text.secondary'>
            Inherits {inheritedRules} dashboard rule{inheritedRules === 1 ? '' : 's'};{' '}
            {ownRules === 0 ? 'no extra widget rules' : `${ownRules} widget rule${ownRules === 1 ? '' : 's'}`}.
          </Typography>
        </Box>
      )}

      {(conversionWarning || vanityWarning || reachWarning) && (
        <Box
          sx={{
            bgcolor: 'warning.light',
            color: 'warning.contrastText',
            borderRadius: 1,
            px: 1.5,
            py: 1,
          }}
        >
          {conversionWarning && (
            <Typography variant='caption' sx={{ display: 'block' }}>
              This widget uses a conversion metric, which stays hidden until exactly one <b>DSP</b> is selected — on the
              dashboard filter bar or as a DSP override below. Across DSPs conversions aren’t deduplicated, so a
              combined total would double-count.
            </Typography>
          )}
          {vanityWarning && (
            <Typography variant='caption' sx={{ display: 'block', mt: conversionWarning ? 1 : 0 }}>
              This widget uses a rate metric (CTR / VCR), which stays hidden until exactly one <b>channel</b> is
              selected — rates aren’t comparable across channels, so the combined view stays on impressions, spend, CPM,
              reach and frequency.
            </Typography>
          )}
          {reachWarning && (
            <Typography variant='caption' sx={{ display: 'block', mt: conversionWarning || vanityWarning ? 1 : 0 }}>
              This widget uses Reach or Frequency, which stay hidden until exactly one <b>DSP</b> is selected — on the
              dashboard filter bar or as a DSP override below. Each platform reports its own audience, so a combined
              total would double-count anyone reached on more than one.
            </Typography>
          )}
        </Box>
      )}

      <Divider />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, flexGrow: 1 }}>
        <TextField label='Title' size='small' fullWidth value={title} onChange={e => setTitle(e.target.value)} />

        <Box
          role='button'
          tabIndex={0}
          onClick={() => setDisplayOpen(o => !o)}
          onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && setDisplayOpen(o => !o)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            cursor: 'pointer',
          }}
        >
          <Typography variant='caption' sx={{ fontWeight: 700, color: 'text.secondary', letterSpacing: 0.4 }}>
            DISPLAY
          </Typography>
          {displayOpen ? (
            <ExpandLessRounded sx={{ fontSize: 18, color: 'text.secondary' }} />
          ) : (
            <ExpandMoreRounded sx={{ fontSize: 18, color: 'text.secondary' }} />
          )}
        </Box>
        {displayOpen && (
          <Box>
            <Typography variant='caption' sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
              Width
            </Typography>
            <Select
              size='small'
              fullWidth
              value={String(span)}
              onChange={e => setSpan(Number(e.target.value) as 1 | 2 | 3)}
            >
              <MenuItem value='1'>1 column</MenuItem>
              <MenuItem value='2'>2 columns</MenuItem>
              <MenuItem value='3'>Full width</MenuItem>
            </Select>
            <Typography variant='caption' sx={{ fontWeight: 600, mb: 0.5, mt: 1.5, display: 'block' }}>
              Height
            </Typography>
            <Select
              size='small'
              fullWidth
              value={String(rowSpan)}
              onChange={e => setRowSpan(clampRowSpan(Number(e.target.value)))}
            >
              {ROW_SPAN_OPTIONS.map(opt => (
                <MenuItem key={opt.value} value={String(opt.value)}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
            <Typography variant='caption' color='text.secondary' sx={{ mt: 0.5, display: 'block' }}>
              Taller widgets show more rows and make trends easier to read. You can also drag a widget's bottom-right
              corner.
            </Typography>
          </Box>
        )}

        {isDataWidget && (
          <Typography variant='caption' sx={{ fontWeight: 700, color: 'text.secondary', letterSpacing: 0.4, mt: 0.5 }}>
            DATA
          </Typography>
        )}

        {widget.type === 'text' ? (
          <>
            <TextField
              label='Content'
              size='small'
              fullWidth
              multiline
              minRows={4}
              placeholder='Enter markdown text…'
              value={text}
              onChange={e => setText(e.target.value)}
            />
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant='caption' sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
                  Style
                </Typography>
                <Select
                  size='small'
                  fullWidth
                  value={textStyle}
                  onChange={e => setTextStyle(e.target.value as NonNullable<WidgetConfig['textStyle']>)}
                >
                  <MenuItem value='title'>Page title</MenuItem>
                  <MenuItem value='heading'>Section header</MenuItem>
                  <MenuItem value='subheader'>Subheader</MenuItem>
                  <MenuItem value='body'>Body</MenuItem>
                  <MenuItem value='caption'>Caption</MenuItem>
                </Select>
              </Box>
              <Box sx={{ width: 120 }}>
                <Typography variant='caption' sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
                  Align
                </Typography>
                <Select
                  size='small'
                  fullWidth
                  value={textAlign}
                  onChange={e => setTextAlign(e.target.value as NonNullable<WidgetConfig['textAlign']>)}
                >
                  <MenuItem value='left'>Left</MenuItem>
                  <MenuItem value='center'>Center</MenuItem>
                  <MenuItem value='right'>Right</MenuItem>
                </Select>
              </Box>
            </Box>
          </>
        ) : widget.type === 'divider' ? (
          <TextField
            label='Label (optional)'
            size='small'
            fullWidth
            placeholder='e.g. Delivery metrics'
            value={text}
            onChange={e => setText(e.target.value)}
          />
        ) : widget.type === 'callout' ? (
          <>
            <TextField
              label='Content'
              size='small'
              fullWidth
              multiline
              minRows={3}
              placeholder='Call out an insight or note for the reader…'
              value={text}
              onChange={e => setText(e.target.value)}
            />
            <Box>
              <Typography variant='caption' sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
                Tone
              </Typography>
              <Select
                size='small'
                fullWidth
                value={calloutTone}
                onChange={e => setCalloutTone(e.target.value as NonNullable<WidgetConfig['calloutTone']>)}
              >
                <MenuItem value='info'>Info</MenuItem>
                <MenuItem value='success'>Success</MenuItem>
                <MenuItem value='warning'>Warning</MenuItem>
                <MenuItem value='error'>Alert</MenuItem>
              </Select>
            </Box>
          </>
        ) : widget.type === 'table' ? (
          <>
            <Box>
              <Typography variant='caption' sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
                Group by
              </Typography>
              <Select size='small' fullWidth value={dimension} onChange={e => setDimension(e.target.value)}>
                {dimensionEntries.map(d => (
                  <MenuItem key={d.value} value={d.value}>
                    {d.label}
                  </MenuItem>
                ))}
              </Select>
            </Box>
            <Box>
              <Typography variant='caption' sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
                Metrics (columns)
              </Typography>
              <Select
                size='small'
                fullWidth
                multiple
                value={metrics}
                onChange={e =>
                  setMetrics(
                    typeof e.target.value === 'string' ? e.target.value.split(',') : (e.target.value as string[]),
                  )
                }
                renderValue={sel => (sel as string[]).map(metricLabel).join(', ')}
              >
                {metricOptions.map(opt => renderMetricOption(opt))}
              </Select>
            </Box>
            <Box>
              <Typography variant='caption' sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
                Rows
              </Typography>
              <Select
                size='small'
                fullWidth
                value={String(rowLimit)}
                onChange={e => setRowLimit(Number(e.target.value))}
              >
                {[5, 10, 20, 50].map(n => (
                  <MenuItem key={n} value={String(n)}>
                    {n}
                  </MenuItem>
                ))}
              </Select>
            </Box>
            <FormControlLabel
              control={
                <Switch checked={showTotalsRow} onChange={e => setShowTotalsRow(e.target.checked)} size='small' />
              }
              label={<Typography variant='body2'>Show totals row</Typography>}
            />
          </>
        ) : widget.type === 'image' ? (
          <>
            <TextField
              label='Image URL'
              size='small'
              fullWidth
              value={imageUrl}
              onChange={e => setImageUrl(e.target.value)}
              placeholder='https://…'
              helperText='Paste a hosted image URL — client logo, divider or screenshot.'
            />
            <Box>
              <Typography variant='caption' sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
                Fit
              </Typography>
              <Select
                size='small'
                fullWidth
                value={imageFit}
                onChange={e => setImageFit(e.target.value as 'contain' | 'cover')}
              >
                <MenuItem value='contain'>Contain</MenuItem>
                <MenuItem value='cover'>Cover</MenuItem>
              </Select>
            </Box>
          </>
        ) : widget.type === 'geo' ? (
          <>
            <Box>
              <Typography variant='caption' sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
                Metric
              </Typography>
              <Select size='small' fullWidth value={metric} onChange={e => setMetric(e.target.value)}>
                {metricOptions.map(opt => renderMetricOption(opt))}
              </Select>
            </Box>
            <Box>
              <Typography variant='caption' sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
                Granularity
              </Typography>
              <Select
                size='small'
                fullWidth
                value={geoLevel}
                onChange={e => setGeoLevel(e.target.value as 'state' | 'dma' | 'zip' | 'city')}
              >
                <MenuItem value='state'>State</MenuItem>
                <MenuItem value='dma'>Market (map)</MenuItem>
                <MenuItem value='city'>City (map)</MenuItem>
                <MenuItem value='zip'>ZIP (map)</MenuItem>
              </Select>
              <Typography variant='caption' color='text.secondary' sx={{ mt: 0.5, display: 'block' }}>
                State, Market, City, and ZIP all draw a map. County isn’t in canonical yet.
              </Typography>
            </Box>
          </>
        ) : widget.type === 'pivot' ? (
          <>
            <Box>
              <Typography variant='caption' sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
                Metric
              </Typography>
              <Select size='small' fullWidth value={metric} onChange={e => setMetric(e.target.value)}>
                {metricOptions.map(opt => renderMetricOption(opt))}
              </Select>
            </Box>
            <Box>
              <Typography variant='caption' sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
                Rows
              </Typography>
              <Select size='small' fullWidth value={dimension} onChange={e => setDimension(e.target.value)}>
                {dimensionEntries.map(d => (
                  <MenuItem key={d.value} value={d.value}>
                    {d.label}
                  </MenuItem>
                ))}
              </Select>
            </Box>
            <Box>
              <Typography variant='caption' sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
                Columns
              </Typography>
              <Select size='small' fullWidth value={columnDimension} onChange={e => setColumnDimension(e.target.value)}>
                {dimensionEntries.map(d => (
                  <MenuItem key={d.value} value={d.value}>
                    {d.label}
                  </MenuItem>
                ))}
              </Select>
            </Box>
            <Box>
              <Typography variant='caption' sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
                Row limit
              </Typography>
              <Select
                size='small'
                fullWidth
                value={String(rowLimit)}
                onChange={e => setRowLimit(Number(e.target.value))}
              >
                {[5, 10, 20].map(n => (
                  <MenuItem key={n} value={String(n)}>
                    {n}
                  </MenuItem>
                ))}
              </Select>
            </Box>
          </>
        ) : widget.type === 'scorecard' || widget.type === 'funnel' ? (
          <>
            <Box>
              <Typography variant='caption' sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
                {widget.type === 'funnel' ? 'Funnel stages (in order)' : 'Metrics'}
              </Typography>
              <Select
                size='small'
                fullWidth
                multiple
                value={metrics}
                onChange={e =>
                  setMetrics(
                    typeof e.target.value === 'string' ? e.target.value.split(',') : (e.target.value as string[]),
                  )
                }
                renderValue={sel => (sel as string[]).map(metricLabel).join(' → ')}
              >
                {metricOptions.map(opt => renderMetricOption(opt))}
              </Select>
              <Typography variant='caption' color='text.secondary' sx={{ mt: 0.5, display: 'block' }}>
                {widget.type === 'funnel'
                  ? 'Each stage shows its conversion rate from the stage above.'
                  : 'One card per metric, sharing this widget’s filters.'}
              </Typography>
            </Box>
            {widget.type === 'scorecard' && (
              <FormControlLabel
                control={<Switch checked={showDelta} onChange={e => setShowDelta(e.target.checked)} size='small' />}
                label={<Typography variant='body2'>Show change vs prior period</Typography>}
              />
            )}
          </>
        ) : widget.type === 'goal' || widget.type === 'pacing' ? (
          <>
            <Box>
              <Typography variant='caption' sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
                Metric
              </Typography>
              <Select size='small' fullWidth value={metric} onChange={e => setMetric(e.target.value)}>
                {metricOptions.map(opt => renderMetricOption(opt))}
              </Select>
            </Box>
            <TextField
              label='Target'
              size='small'
              fullWidth
              type='number'
              value={goalTarget}
              onChange={e => setGoalTarget(e.target.value)}
              helperText={
                widget.type === 'pacing'
                  ? 'Goal for the selected window; expected delivery is pro-rated by elapsed days.'
                  : 'The goal this metric is tracking toward.'
              }
            />
          </>
        ) : widget.type === 'bar' ? (
          <>
            <Box>
              <Typography variant='caption' sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
                Group by
              </Typography>
              <Select size='small' fullWidth value={dimension} onChange={e => setDimension(e.target.value)}>
                {dimensionEntries.map(d => (
                  <MenuItem key={d.value} value={d.value}>
                    {d.label}
                  </MenuItem>
                ))}
              </Select>
            </Box>
            <Box>
              <Typography variant='caption' sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
                Metrics (up to {MAX_CHART_METRICS})
              </Typography>
              <Select
                size='small'
                fullWidth
                multiple
                value={metrics.slice(0, MAX_CHART_METRICS)}
                onChange={e =>
                  setMetrics(
                    (typeof e.target.value === 'string'
                      ? e.target.value.split(',')
                      : (e.target.value as string[])
                    ).slice(0, MAX_CHART_METRICS),
                  )
                }
                renderValue={sel => (sel as string[]).map(metricLabel).join(', ')}
              >
                {metricOptions.map(opt =>
                  renderMetricOption(opt, metrics.length >= MAX_CHART_METRICS && !metrics.includes(opt.value)),
                )}
              </Select>
            </Box>
            <Box>
              <Typography variant='caption' sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
                Sort
              </Typography>
              <Select
                size='small'
                fullWidth
                value={sortOrder}
                onChange={e => setSortOrder(e.target.value as 'desc' | 'asc')}
              >
                <MenuItem value='desc'>Top (highest)</MenuItem>
                <MenuItem value='asc'>Bottom (lowest)</MenuItem>
              </Select>
            </Box>
            <Box>
              <Typography variant='caption' sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
                Top N
              </Typography>
              <Select
                size='small'
                fullWidth
                value={String(rowLimit)}
                onChange={e => setRowLimit(Number(e.target.value))}
              >
                {[5, 10, 20, 50].map(n => (
                  <MenuItem key={n} value={String(n)}>
                    {n}
                  </MenuItem>
                ))}
              </Select>
            </Box>
            <Box>
              <Typography variant='caption' sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
                Orientation
              </Typography>
              <Select
                size='small'
                fullWidth
                value={orientation}
                onChange={e => setOrientation(e.target.value as 'horizontal' | 'vertical')}
              >
                <MenuItem value='horizontal'>Horizontal</MenuItem>
                <MenuItem value='vertical'>Vertical</MenuItem>
              </Select>
            </Box>
          </>
        ) : widget.type === 'donut' ? (
          <>
            <Box>
              <Typography variant='caption' sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
                Metric
              </Typography>
              <Select size='small' fullWidth value={metric} onChange={e => setMetric(e.target.value)}>
                {metricOptions.map(opt => renderMetricOption(opt))}
              </Select>
            </Box>
            <Box>
              <Typography variant='caption' sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
                Group by
              </Typography>
              <Select size='small' fullWidth value={dimension} onChange={e => setDimension(e.target.value)}>
                {dimensionEntries.map(d => (
                  <MenuItem key={d.value} value={d.value}>
                    {d.label}
                  </MenuItem>
                ))}
              </Select>
            </Box>
            <Box>
              <Typography variant='caption' sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
                Slices
              </Typography>
              <Select
                size='small'
                fullWidth
                value={String(rowLimit)}
                onChange={e => setRowLimit(Number(e.target.value))}
              >
                {[5, 8, 10, 20].map(n => (
                  <MenuItem key={n} value={String(n)}>
                    {n}
                  </MenuItem>
                ))}
              </Select>
            </Box>
          </>
        ) : widget.type !== 'geo' && widget.type !== 'image' ? (
          <>
            {widget.type === 'line' || widget.type === 'area' ? (
              <Box>
                <Typography variant='caption' sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
                  Metrics (up to {MAX_CHART_METRICS})
                </Typography>
                <Select
                  size='small'
                  fullWidth
                  multiple
                  value={metrics.slice(0, MAX_CHART_METRICS)}
                  onChange={e => {
                    const next =
                      typeof e.target.value === 'string' ? e.target.value.split(',') : (e.target.value as string[]);
                    setMetrics(next.slice(0, MAX_CHART_METRICS));
                  }}
                  renderValue={sel => (sel as string[]).map(metricLabel).join(', ')}
                >
                  {metricOptions.map(opt =>
                    renderMetricOption(opt, metrics.length >= MAX_CHART_METRICS && !metrics.includes(opt.value)),
                  )}
                </Select>
                <Typography variant='caption' color='text.secondary' sx={{ mt: 0.5, display: 'block' }}>
                  First metric is the primary series. Add up to {MAX_CHART_METRICS} to overlay — mixed units (e.g.
                  Impressions + CTR) auto-split onto a second axis.
                </Typography>
                <Box sx={{ mt: 2 }}>
                  <Typography variant='caption' sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
                    Time grain
                  </Typography>
                  <Select
                    size='small'
                    fullWidth
                    value={timeGrain}
                    onChange={e => setTimeGrain(e.target.value as 'day' | 'week' | 'month')}
                  >
                    <MenuItem value='day'>Day</MenuItem>
                    {/* Week/Month bucket daily rows client-side by averaging the daily
                        ratio (bucketRows) — sound for CTR, but Frequency's reach
                        denominator isn't deduplicated across days, so a period average
                        of daily frequencies understates the real figure (CodeRabbit,
                        flank#4460). No period-level calculation exists yet, so this
                        stays Day-only for Frequency rather than showing a wrong number. */}
                    <MenuItem value='week' disabled={frequencyBlocksGrain}>
                      Week
                    </MenuItem>
                    <MenuItem value='month' disabled={frequencyBlocksGrain}>
                      Month
                    </MenuItem>
                  </Select>
                  {frequencyBlocksGrain && (
                    <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mt: 0.5 }}>
                      Frequency is only available at Day grain — reach isn't deduplicated across days, so a Week/Month
                      average would understate it.
                    </Typography>
                  )}
                </Box>

                <Box sx={{ mt: 2 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={normalizeChartAxes}
                        onChange={e => setNormalizeChartAxes(e.target.checked)}
                        size='small'
                      />
                    }
                    label={<Typography variant='body2'>Normalize to % of max</Typography>}
                  />
                  <Typography variant='caption' color='text.secondary' sx={{ display: 'block' }}>
                    Plots every metric as a share of its own peak on one shared axis — useful for comparing shape across
                    very different scales. Tooltips still show the real values.
                  </Typography>
                </Box>

                {!normalizeChartAxes && metrics.length > 1 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant='caption' sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
                      Axis assignment
                    </Typography>
                    {metrics.slice(0, MAX_CHART_METRICS).map(mk => (
                      <Box key={mk} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography variant='body2' sx={{ flexGrow: 1 }}>
                          {metricLabel(mk)}
                        </Typography>
                        <Select<'auto' | 'y' | 'y1'>
                          size='small'
                          value={axisOverrides[mk] ?? 'auto'}
                          onChange={e => {
                            const v = e.target.value;
                            setAxisOverrides(prev => {
                              if (v === 'auto') {
                                const { [mk]: _drop, ...rest } = prev;
                                return rest;
                              }
                              return { ...prev, [mk]: v };
                            });
                          }}
                          sx={{ width: 110 }}
                        >
                          <MenuItem value='auto'>Auto</MenuItem>
                          <MenuItem value='y'>Left</MenuItem>
                          <MenuItem value='y1'>Right</MenuItem>
                        </Select>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            ) : (
              <Box>
                <Typography variant='caption' sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
                  Metric
                </Typography>
                <Select size='small' fullWidth value={metric} onChange={e => setMetric(e.target.value)}>
                  {metricOptions.map(opt => renderMetricOption(opt))}
                </Select>
              </Box>
            )}

            {widget.type === 'kpi' && (
              <>
                <FormControlLabel
                  control={<Switch checked={showDelta} onChange={e => setShowDelta(e.target.checked)} size='small' />}
                  label={<Typography variant='body2'>Show change badge</Typography>}
                />
                <Box>
                  <Typography variant='caption' sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
                    Compare to
                  </Typography>
                  <Select size='small' fullWidth value={compareTo} onChange={e => setCompareTo(e.target.value)}>
                    <MenuItem value='Prior period'>Prior period</MenuItem>
                    <MenuItem value='Prior year'>Prior year</MenuItem>
                  </Select>
                </Box>
              </>
            )}
          </>
        ) : null}

        {isDataWidget && (
          <>
            <Divider sx={{ mt: 1 }} />
            <Box
              role='button'
              tabIndex={0}
              onClick={() => setFiltersOpen(o => !o)}
              onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && setFiltersOpen(o => !o)}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                cursor: 'pointer',
              }}
            >
              <Box>
                <Typography variant='caption' sx={{ fontWeight: 700, display: 'block' }}>
                  THIS WIDGET
                  {ownRules > 0 ? ` · ${ownRules} override${ownRules === 1 ? '' : 's'}` : ''}
                </Typography>
                <Typography variant='caption' color='text.secondary'>
                  Inherits the dashboard filters. Set a dimension here to override it for this widget only.
                </Typography>
              </Box>
              {filtersOpen ? (
                <ExpandLessRounded sx={{ fontSize: 18, color: 'text.secondary' }} />
              ) : (
                <ExpandMoreRounded sx={{ fontSize: 18, color: 'text.secondary' }} />
              )}
            </Box>
            {filtersOpen &&
              offeredFilterKeys.map(spec => (
                <WidgetFilterRow
                  key={spec.key}
                  spec={spec}
                  scope={widgetEntityScope(spec.key, panelFilters)}
                  dashboardSelection={dashboardFilters[spec.key] ?? []}
                  ownSelection={widgetFilters[spec.key] ?? []}
                  onChange={next => setWidgetFilters(prev => withWidgetOverride(prev, spec.key, next))}
                />
              ))}
            {overrideCount(widgetFilters) > 0 && (
              <Button
                size='small'
                onClick={() => setWidgetFilters({})}
                sx={{ textTransform: 'none', color: 'text.secondary', alignSelf: 'flex-start' }}
              >
                Reset to dashboard filters
              </Button>
            )}
          </>
        )}
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Button
          size='small'
          onClick={() => {
            // Revert the panel's fields back to the widget's saved config.
            setTitle(widget.title);
            setMetric(toMetricKey(widget.metric ?? 'impressions'));
            setMetrics(widget.metrics?.length ? widget.metrics.map(toMetricKey) : defaultMetrics);
            setWidgetFilters(widget.widgetFilters ?? {});
            setGoalTarget(widget.goalTarget != null ? String(widget.goalTarget) : '');
            setSpan(widget.span ?? 1);
            setRowSpan(clampRowSpan(widget.rowSpan));
          }}
          sx={{ textTransform: 'none', color: 'text.secondary' }}
        >
          Revert
        </Button>
        <Box sx={{ flexGrow: 1 }} />
        <Button size='small' onClick={onClose} sx={{ textTransform: 'none' }}>
          Close
        </Button>
      </Box>

      <Button
        variant='contained'
        disableElevation
        fullWidth
        sx={{ textTransform: 'none', bgcolor: '#111', '&:hover': { bgcolor: '#000' } }}
        onClick={() =>
          onSave({
            title,
            metric,
            showDelta,
            compareTo,
            text,
            textStyle,
            textAlign,
            calloutTone,
            dimension,
            metrics,
            rowLimit,
            sortOrder,
            orientation,
            timeGrain,
            normalizeChartAxes,
            axisOverrides,
            showTotalsRow,
            widgetFilters,
            goalTarget: goalTarget === '' ? undefined : Number(goalTarget),
            span,
            rowSpan,
            geoLevel,
            columnDimension,
            imageUrl: imageUrl.trim() || undefined,
            imageFit,
          })
        }
      >
        Save changes
      </Button>
    </Box>
  );
};

// ─── Widget picker drawer ─────────────────────────────────────────────────────

/**
 * "Add a widget" drawer, mirroring the Lovable flow: a search box, category
 * chips, and widgets grouped by category with per-group counts.
 */
const WidgetPickerDrawer = ({
  open,
  onClose,
  onPick,
}: {
  open: boolean;
  onClose: () => void;
  onPick: (type: string) => void;
}) => {
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState<WidgetCategory | 'All'>('All');

  // Reset the filters each time the drawer opens.
  useEffect(() => {
    if (open) {
      setSearch('');
      setCategory('All');
    }
  }, [open]);

  const term = search.trim().toLowerCase();
  const matches = WIDGET_TYPES.filter(
    wt =>
      (category === 'All' || wt.category === category) &&
      (!term || wt.label.toLowerCase().includes(term) || wt.description.toLowerCase().includes(term)),
  );
  const groups = WIDGET_CATEGORIES.map(c => ({
    category: c,
    items: matches.filter(wt => wt.category === c),
  })).filter(g => g.items.length > 0);

  return (
    <Drawer anchor='right' open={open} onClose={onClose} slotProps={{ paper: { sx: { width: 380 } } }}>
      <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2, height: '100%' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant='subtitle1' sx={{ fontWeight: 700 }}>
              Add a widget
            </Typography>
            <Typography variant='caption' color='text.secondary'>
              Pick from KPIs, charts, tables, performance and layout blocks.
            </Typography>
          </Box>
          <IconButton size='small' onClick={onClose} aria-label='Close'>
            <CloseRounded fontSize='small' />
          </IconButton>
        </Box>

        <TextField
          size='small'
          fullWidth
          autoFocus
          placeholder='Search widgets…'
          value={search}
          onChange={e => setSearch(e.target.value)}
        />

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
          {(['All', ...WIDGET_CATEGORIES] as Array<WidgetCategory | 'All'>).map(c => (
            <Chip
              key={c}
              label={c}
              size='small'
              onClick={() => setCategory(c)}
              variant={category === c ? 'filled' : 'outlined'}
              color={category === c ? 'primary' : 'default'}
              sx={{ height: 26, fontSize: 12 }}
            />
          ))}
        </Box>

        <Box sx={{ overflowY: 'auto', flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {groups.length === 0 && (
            <Typography variant='body2' color='text.secondary'>
              No widgets match “{search}”.
            </Typography>
          )}
          {groups.map(group => (
            <Box key={group.category}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
                <Typography
                  variant='caption'
                  sx={{ fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary' }}
                >
                  {group.category}
                </Typography>
                <Chip label={group.items.length} size='small' sx={{ height: 18, fontSize: 11 }} />
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1 }}>
                {group.items.map(wt => (
                  <Paper
                    key={wt.type}
                    variant='outlined'
                    onClick={() => onPick(wt.type)}
                    sx={{
                      p: 1.25,
                      borderRadius: 1.5,
                      cursor: 'pointer',
                      '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
                    }}
                  >
                    <Typography variant='body2' sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                      {wt.label}
                    </Typography>
                    <Typography
                      variant='caption'
                      color='text.secondary'
                      sx={{
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {wt.description}
                    </Typography>
                  </Paper>
                ))}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Drawer>
  );
};

// ─── Branding dialog ──────────────────────────────────────────────────────────

/**
 * Per-dashboard presentation: a logo, an accent colour applied to the primary
 * chart series, and a headline. Stored in the dashboard's `config`.
 */
const BrandingDialog = ({
  open,
  branding,
  onClose,
  onSave,
}: {
  open: boolean;
  branding: DashboardBranding;
  onClose: () => void;
  onSave: (next: DashboardBranding) => void;
}) => {
  const [logoUrl, setLogoUrl] = useState(branding.logoUrl ?? '');
  const [accentColor, setAccentColor] = useState(branding.accentColor ?? '#3b82f6');
  const [headline, setHeadline] = useState(branding.headline ?? '');

  // Re-seed the fields whenever the dialog is reopened.
  useEffect(() => {
    if (!open) return;
    setLogoUrl(branding.logoUrl ?? '');
    setAccentColor(branding.accentColor ?? '#3b82f6');
    setHeadline(branding.headline ?? '');
  }, [open, branding.logoUrl, branding.accentColor, branding.headline]);

  const validHex = /^#[0-9a-fA-F]{6}$/.test(accentColor);

  return (
    <Dialog open={open} onClose={onClose} maxWidth='xs' fullWidth>
      <DialogTitle sx={{ pb: 1 }}>Dashboard branding</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>
        <Typography variant='caption' color='text.secondary'>
          Applies to this dashboard only — useful when handing it to a client.
        </Typography>
        <TextField
          label='Logo URL'
          size='small'
          fullWidth
          value={logoUrl}
          onChange={e => setLogoUrl(e.target.value)}
          placeholder='https://…'
          helperText='Shown beside the dashboard name.'
        />
        <TextField
          label='Headline'
          size='small'
          fullWidth
          value={headline}
          onChange={e => setHeadline(e.target.value)}
          placeholder='e.g. Q3 performance for Acme'
        />
        <Box>
          <Typography variant='caption' sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
            Accent colour
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              component='input'
              type='color'
              value={validHex ? accentColor : '#3b82f6'}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setAccentColor(e.target.value)}
              sx={{
                width: 44,
                height: 36,
                p: 0,
                border: 'none',
                bgcolor: 'transparent',
                cursor: 'pointer',
              }}
            />
            <TextField
              size='small'
              value={accentColor}
              onChange={e => setAccentColor(e.target.value)}
              error={!validHex}
              helperText={validHex ? 'Used for the primary chart series.' : 'Enter a 6-digit hex colour.'}
              sx={{ width: 160 }}
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          variant='contained'
          disableElevation
          disabled={!validHex}
          onClick={() =>
            onSave({
              ...(logoUrl.trim() ? { logoUrl: logoUrl.trim() } : {}),
              ...(headline.trim() ? { headline: headline.trim() } : {}),
              accentColor,
            })
          }
          sx={{ textTransform: 'none', bgcolor: '#111', '&:hover': { bgcolor: '#000' } }}
        >
          Save branding
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

const DashboardBuilder = ({ dashboardId, onBack, onSaved }: Props) => {
  // flank#5101: the custom-field/formula builder dialog is rendered from this
  // outer component, not WidgetCard (which already has its own identical
  // permissions/allowedMetricKeys for the per-widget metric picker/Group-by
  // selector) — needs its own copy to filter what that dialog offers.
  // Also (flank#5097) needed here to gate the dashboard-level DSP/Publisher
  // filter offerings and strip a saved condition the viewer can no longer
  // use, below.
  const isRibeyeAdmin = useAppSelector(selectIsSuperAdmin);
  const permissions = useDashboardPermissions(isRibeyeAdmin);
  const allowedMetricKeys = useMemo(
    () => KNOWN_METRIC_KEYS.filter(k => !isPermissionDeniedMetric(k, permissions)),
    [permissions],
  );
  const [dashboard, setDashboard] = useState<CustomDashboard | null>(null);
  const [name, setName] = useState('');
  const [layout, setLayout] = useState<WidgetConfig[]>([]);
  // Mirrors `layout` so mutateLayout can compose several changes within one
  // tick; kept in sync below for the paths that setLayout directly (initial load).
  const layoutRef = useRef<WidgetConfig[]>([]);
  useEffect(() => {
    layoutRef.current = layout;
  }, [layout]);
  const [mode, setMode] = useState<'edit' | 'view'>('view');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // flank#5049 — dashboard-level "Data through <date>" next to the date range,
  // same accumulated-across-widgets signal WidgetCard reads for the delta clamp.
  const freshnessMapForLabel = useDashboardFreshness();
  const [pickerOpen, setPickerOpen] = useState(false);
  // Bumped by Refresh; every widget's data effect keys off it, which is what
  // makes the button actually refetch.
  const [refreshNonce, setRefreshNonce] = useState(0);
  const [moreAnchor, setMoreAnchor] = useState<HTMLElement | null>(null);
  const [configWidget, setConfigWidget] = useState<WidgetConfig | null>(null);
  const [filters, setFilters] = useState<DashboardFilters>(defaultDashboardFilters());
  // The dashboard's own saved starting filters, distinct from `filters` (what
  // THIS visit currently shows) — a View-mode browse changes `filters` only
  // (flank#5057), so an action that persists for an unrelated reason
  // (deleting a saved view, saving branding) must not pick up a half-browsed
  // `filters` through persist's own fallback (CodeRabbit, flank#5065).
  // `saveCurrentFiltersAsStartingView` is the one explicit action that still
  // passes `filters` on purpose. Updated on load and after every persist call
  // that resolves a real filters value (see persist below).
  const lastPersistedFiltersRef = useRef<DashboardFilters>(defaultDashboardFilters());
  const [views, setViews] = useState<SavedView[]>([]);
  const [activeViewId, setActiveViewId] = useState<string | null>(null);
  const [branding, setBranding] = useState<DashboardBranding>({});
  const [filterBar, setFilterBar] = useState<FilterBarConfig>(defaultFilterBarConfig());
  // Tenant layer of the slice F resolver. null = no tenant opinion (built-in
  // default applies); [] = an admin disabled every filter.
  const [tenantDefaults, setTenantDefaults] = useState<string[] | null>(null);
  const [tenantError, setTenantError] = useState<string | null>(null);
  const role = useAppSelector(state => state.me.role);
  const canManageTenant = role === Role.ADMIN || role === Role.ORG_ADMIN || role === Role.WHITELABEL_ADMIN;
  const [brandingOpen, setBrandingOpen] = useState(false);
  // Tenant custom fields live in the dashboard's own config, so they load and
  // persist alongside saved views rather than needing their own endpoint.
  const [tenantFields, setTenantFields] = useState<TenantFields>(EMPTY_TENANT_FIELDS);
  const [fieldsOpen, setFieldsOpen] = useState(false);
  // Pages own their widgets. A dashboard saved before pages existed reads back
  // as a single page holding everything (see backfillWidgetPages).
  const [pages, setPages] = useState<DashboardPage[]>([]);
  const [activePageId, setActivePageId] = useState<string | null>(null);
  // Populated by each on-screen widget as its data loads (#4414 PPTX export);
  // read only at export time, so a widget updating doesn't re-render the toolbar.
  const exportSnapshotsRef = useRef<Map<string, WidgetExportSnapshot>>(new Map());
  // Whether every widget has finished its first fetch, and how. PPTX shows a
  // "Not ready to export yet"/"This widget failed to load" note in place for
  // that widget, so a viewer sees the gap; CSV/Excel's stacked-sections shape
  // has nothing analogous to show per row, so it silently omitted the widget
  // instead -- a 4-of-6-widget file looked identical to a complete one
  // (Lucas, review of #4553). 'failed' is tracked separately from 'loading':
  // a failed widget won't resolve on its own, so a "waiting to load" message
  // would be permanently misleading for it (CodeRabbit). Tracked as its own
  // state (not read from the ref above) specifically so CSV/Excel can
  // disable-with-reason; kept in a ref alongside so the toolbar only
  // re-renders on the rare transition, not on every widget's routine data
  // refresh once the dashboard has already finished loading once.
  type ExportReadiness = 'ready' | 'loading' | 'failed';
  const [exportReadiness, setExportReadiness] = useState<ExportReadiness>('ready');
  const exportReadinessRef = useRef<ExportReadiness>('ready');
  const [exporting, setExporting] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [renaming, setRenaming] = useState<{ id: string; name: string } | null>(null);
  // Renaming the dashboard itself (distinct from `renaming`, which renames a
  // page within it), reached from the "..." menu's new "Rename…" entry.
  const [renamingDashboard, setRenamingDashboard] = useState(false);
  const [savingDashboardName, setSavingDashboardName] = useState(false);
  const [renameError, setRenameError] = useState<string | null>(null);
  // Set when loading dropped legacy campaign / line item names (#4614).
  const [entityNotice, setEntityNotice] = useState(false);
  // Set when loading dropped a saved Product filter selection (#5059).
  const [productFilterNotice, setProductFilterNotice] = useState(false);
  // Set when a widget metric/dimension this viewer isn't permitted to see is
  // hidden from view (flank#5101) — display-only, nothing is dropped.
  const [deniedFieldsNotice, setDeniedFieldsNotice] = useState(false);
  // Set when loading dropped a widget whose group-by was Product (#5100).
  const [productDimensionNotice, setProductDimensionNotice] = useState(false);
  // Set when loading dropped a saved DSP/Publisher condition the viewer no
  // longer has permission to use (flank#5097).
  const [deniedFilterNotice, setDeniedFilterNotice] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    getDashboard(dashboardId)
      .then(loaded => {
        if (!active) return;
        setDashboard(loaded);
        setName(loaded.name);
        // Legacy campaign / line item names out before any reader sees them
        // (#4614); persisted with the marker on the next save.
        const migrated = migrateEntityFilters((loaded.layout ?? []) as Record<string, unknown>[], loaded.config);
        setEntityNotice(migrated.cleared);
        // Same on-load cleanup for the Product filter the catalog dropped (#5059).
        const productMigrated = migrateProductFilter(migrated.layout, migrated.config);
        setProductFilterNotice(productMigrated.cleared);
        const loadedTenantFields = readTenantFields(productMigrated.config);
        // A widget grouped by Product (now gone from the catalog, flank#5100)
        // has nothing left to show — dropped entirely rather than falling
        // back to a different dimension it was never configured for. A saved
        // custom dimension bucketed on `product` counts too (CodeRabbit).
        const productDimensionKeys = new Set(
          loadedTenantFields.dimensions.filter(d => d.field === 'product').map(d => d.key),
        );
        const dimensionMigrated = migrateProductDimension(
          productMigrated.layout,
          productMigrated.config,
          productDimensionKeys,
        );
        setProductDimensionNotice(dimensionMigrated.cleared);
        // Same cleanup for a DSP/Publisher condition the viewer can no longer
        // use (flank#5097) — `permissions` may still be optimistic here (the
        // probe hasn't resolved yet), so a denied condition that slips past
        // this pass is caught by the same strip in `persist` below instead.
        const deniedMigrated = stripDeniedFilterConditions(
          dimensionMigrated.layout,
          dimensionMigrated.config,
          permissions,
        );
        setDeniedFilterNotice(deniedMigrated.cleared);
        const config = deniedMigrated.config;
        const { filters: loadedFilters, widgets } = splitLayout(deniedMigrated.layout, config);
        // Re-anchor a preset-length window to today; saved views get the same
        // treatment so switching to one doesn't drop back to a stale range.
        const refreshedFilters = refreshRelativeRange(loadedFilters);
        setFilters(refreshedFilters);
        lastPersistedFiltersRef.current = refreshedFilters;
        const loadedViews = readSavedViews(config).map(v => ({
          ...v,
          filters: refreshRelativeRange(v.filters),
        }));
        setViews(loadedViews);
        setActiveViewId(readActiveViewId(config, loadedViews));
        setBranding(readBranding(config));
        setTenantFields(loadedTenantFields);
        // Resolve against the tenant allowlist. Fetched alongside the dashboard
        // rather than in its own effect so the bar never renders one frame with
        // filters the tenant has disabled.
        getTenantFilterDefaults()
          .then(keys => {
            if (!active) return;
            setTenantDefaults(keys);
            setFilterBar(resolveFilterBarConfig(config, keys));
          })
          .catch(() => {
            // A tenant-defaults failure must not blank the dashboard; fall back
            // to the dashboard's own config, which is the pre-slice-F behaviour.
            if (active) setFilterBar(resolveFilterBarConfig(config, null));
          });
        const loadedPages = readPages(config);
        setPages(loadedPages);
        setActivePageId(loadedPages[0].id);
        // flank#5101/#5107 (Lucas): a widget's own metric/dimension not on
        // this viewer's allowed list is surfaced for the notice below, but
        // NEVER written into `layout` state — the config itself is left
        // intact. Unlike the Product-removal migrations above (a feature gone
        // for every user), this is a PER-VIEWER permission: a dashboard built
        // by a Ribeye Admin and opened by a restricted viewer must not have
        // its config silently rewritten just because this viewer can't see
        // part of it. `keepShown` (render) and `sanitizeReportRequest`
        // (serve-time, ribeye-api) already guarantee a denied field can't be
        // rendered or fetched — stripping storage on top of that bought
        // nothing and cost other users their configuration the moment any
        // save round-tripped through a restricted viewer.
        // A saved custom dimension bucketed on `dsp` is DSP-restricted the
        // same as the "DSP" friendly key (CodeRabbit) — same precedent as
        // `productDimensionKeys` in the load effect's #5100 migration above.
        const dspDimensionKeys = new Set(loadedTenantFields.dimensions.filter(d => d.field === 'dsp').map(d => d.key));
        setDeniedFieldsNotice(
          stripDeniedWidgetFields(widgets as Record<string, unknown>[], permissions, dspDimensionKeys).cleared,
        );
        setLayout(backfillWidgetPages(widgets as WidgetConfig[], loadedPages));
      })
      .catch(err => active && setError(err instanceof Error ? err.message : 'Failed to load'))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [dashboardId]);

  // Re-runs the same strip once `permissions` resolves (flank#5097): the
  // probe (`ensurePlatformSignal`) and `getDashboard` above race, and on a
  // fresh load the dashboard fetch routinely wins — the load effect's own
  // strip then runs against still-optimistic permissions and no-ops. This
  // catches a genuinely denied condition once the real answer is in, and
  // also self-heals a permission revoked mid-session. A no-op once the first
  // pass already cleared everything (`cleared` stays false), so this is safe
  // to re-run on every subsequent permissions update too.
  useEffect(() => {
    if (loading) return;
    const result = stripDeniedFilterConditions(
      layoutRef.current as unknown as Record<string, unknown>[],
      { filters } as unknown as Record<string, unknown>,
      permissions,
    );
    if (!result.cleared) return;
    setDeniedFilterNotice(true);
    setFilters((result.config as unknown as { filters: DashboardFilters }).filters);
    setLayout(result.layout as unknown as WidgetConfig[]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, permissions.canViewPlatform, permissions.canViewRawInventory]);

  const persist = async (
    nextLayout: WidgetConfig[],
    nextName?: string,
    nextFilters?: DashboardFilters,
    nextViews?: SavedView[],
    nextActiveViewId?: string | null,
    nextBranding?: DashboardBranding,
    nextFilterBar?: FilterBarConfig,
    nextTenantFields?: TenantFields,
    nextPages?: DashboardPage[],
  ) => {
    setSaving(true);
    setError(null);
    // A caller that doesn't explicitly pass filters (deleting a saved view,
    // saving branding, toggling filter-bar settings) means "don't touch the
    // starting filters" — in View mode that must resolve to the dashboard's
    // own last-saved filters, not `filters` (this visit's possibly-browsed
    // state), or the browse would reach the saved dashboard sideways through
    // an unrelated save (CodeRabbit, flank#5065). An explicit caller
    // (updateFilters, saveCurrentFiltersAsStartingView) always means it, in
    // either mode.
    const resolvedFilters = nextFilters ?? (mode === 'edit' ? filters : lastPersistedFiltersRef.current);
    try {
      await updateDashboard(dashboardId, {
        // Omit `name` unless this call is explicitly renaming: every general
        // autosave used to resend the current `name` closure, so one left in
        // flight during a rename (flank#4665) could finish after it and
        // overwrite the new name back to the old one (CodeRabbit).
        ...(nextName !== undefined ? { name: nextName } : {}),
        layout: nextLayout as Record<string, unknown>[],
        config: writePages(
          writeTenantFields(
            buildConfig(
              resolvedFilters,
              nextViews ?? views,
              nextActiveViewId !== undefined ? nextActiveViewId : activeViewId,
              nextBranding ?? branding,
              nextFilterBar ?? filterBar,
            ),
            nextTenantFields ?? tenantFields,
          ),
          nextPages ?? pages,
        ),
      });
      lastPersistedFiltersRef.current = resolvedFilters;
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  // Same `updateDashboard` call the "My dashboards" rename flow uses — only
  // the name, not the rest of the layout/config `persist` would also resave.
  const renameDashboard = async (newName: string) => {
    setSavingDashboardName(true);
    setRenameError(null);
    try {
      await updateDashboard(dashboardId, { name: newName });
      setName(newName);
      setRenamingDashboard(false);
      onSaved();
    } catch (err) {
      setRenameError(err instanceof Error ? err.message : 'Failed to rename');
    } finally {
      setSavingDashboardName(false);
    }
  };

  // Dashboard filters cascade to every widget and, in Edit mode, persist with
  // the layout. In View mode this only changes what THIS visit sees (flank#5057)
  // — every change here used to save straight to the dashboard regardless of
  // mode, so browsing a shared dashboard in View mode silently rewrote the
  // owner's saved starting point (and its "Last modified" date) for every
  // future viewer too. `saveCurrentFiltersAsStartingView` below is the one
  // explicit action that still persists from View mode.
  const updateFilterBar = (next: FilterBarConfig) => {
    setFilterBar(next);
    if (mode === 'edit') persist(layout, undefined, undefined, undefined, undefined, undefined, next);
  };

  /** Publish this dashboard's visible set as the tenant-wide starting point. */
  const saveTenantDefault = async () => {
    setTenantError(null);
    try {
      const keys = await setTenantFilterDefaults(enabledFilterKeys(filterBar));
      setTenantDefaults(keys);
    } catch (err) {
      setTenantError(err instanceof Error ? err.message : 'Could not save the tenant default');
    }
  };

  /** Drop the tenant opinion so dashboards fall back to the built-in default. */
  const resetTenantDefault = async () => {
    setTenantError(null);
    try {
      await clearTenantFilterDefaults();
      setTenantDefaults(null);
    } catch (err) {
      setTenantError(err instanceof Error ? err.message : 'Could not clear the tenant default');
    }
  };

  const updateFilters = (patch: Partial<DashboardFilters>) => {
    const next = { ...filters, ...withEntityCascade(filters, patch) };
    setFilters(next);
    if (mode === 'edit') persist(layout, undefined, next);
  };

  /** The one explicit save flank#5057 adds for View mode: makes the current,
   *  possibly-just-browsed filters the dashboard's own saved starting point. */
  const saveCurrentFiltersAsStartingView = () => {
    persist(layout, undefined, filters);
  };

  const handleDone = async () => {
    await persist(layout);
    setMode('view');
  };

  /**
   * Apply a layout mutation against the LATEST layout, not the one captured when
   * this render's handlers were created.
   *
   * Every mutation used to compute `next` from the `layout` closure and then
   * call setLayout(next) + persist(next). Two of them firing before a re-render
   * — a quick double-click, or Add followed straight by Remove — meant the
   * second computed from a stale array and silently discarded the first
   * (CodeRabbit, flank#3817).
   *
   * A ref rather than setLayout's functional form because `persist` has to see
   * the same array: side effects inside a state updater run twice under
   * StrictMode, which would double-write the dashboard. Updating the ref
   * synchronously means a second call in the same tick composes on top.
   */
  const mutateLayout = (fn: (prev: WidgetConfig[]) => WidgetConfig[]) => {
    const next = fn(layoutRef.current);
    layoutRef.current = next;
    setLayout(next);
    persist(next);
  };

  const addWidget = (type: string) => {
    setPickerOpen(false);
    const def = WIDGET_TYPES.find(t => t.type === type);
    const widget: WidgetConfig = {
      id: crypto.randomUUID(),
      type,
      // Describe what the widget shows, not which chart it is — "Line Chart"
      // reads as an unfinished placeholder on a client-facing dashboard.
      title: DEFAULT_WIDGET_TITLES[type] ?? def?.label ?? type,
      metric: 'impressions',
      showDelta: true,
      compareTo: 'Prior period',
      pageId: activePageId ?? pages[0]?.id,
      // A new Table groups by Channel, not DSP (flank#5097) — a restricted
      // viewer's first table otherwise lands on a dimension they may not be
      // able to see. Set explicitly here rather than changing the `?? 'DSP'`
      // fallback ConfigPanel/renderers use elsewhere, which stays needed for
      // an already-saved widget with no `dimension` of its own.
      ...(type === 'table' ? { dimension: 'Channel' } : {}),
    };
    mutateLayout(prev => [...prev, widget]);
  };

  // ── saved views ──
  const saveView = (name: string) => {
    // The new NAMED VIEW itself snapshots what's currently browsed — that's
    // the whole point of "Save filters as...". The dashboard's own starting
    // filters are a separate concern (CodeRabbit, flank#5065): leave
    // `nextFilters` unset so `persist` falls back to the last-saved value in
    // View mode, instead of this save sideways-persisting a browsed filter.
    const view: SavedView = { id: crypto.randomUUID(), name, filters };
    const next = [...views, view];
    setViews(next);
    setActiveViewId(view.id);
    persist(layout, undefined, undefined, next, view.id);
  };

  const selectView = (id: string | null) => {
    setActiveViewId(id);
    if (id === null) return;
    const view = views.find(v => v.id === id);
    if (!view) return;
    setFilters(view.filters);
    // Picking a saved filter is browsing, same as changing a filter by hand
    // (flank#5057) — persists in Edit mode only.
    if (mode === 'edit') persist(layout, undefined, view.filters, views, id);
  };

  /** Discard filter drift and re-apply the active view's snapshot. */
  const resetView = () => {
    const view = views.find(v => v.id === activeViewId);
    if (!view) return;
    setFilters(view.filters);
    if (mode === 'edit') persist(layout, undefined, view.filters, views);
  };

  const deleteView = (id: string) => {
    const next = views.filter(v => v.id !== id);
    setViews(next);
    const nextActive = activeViewId === id ? null : activeViewId;
    if (activeViewId === id) setActiveViewId(null);
    // Deleting a saved view doesn't mean the starting filters — leave
    // `nextFilters` unset (CodeRabbit, flank#5065), same reasoning as saveView.
    persist(layout, undefined, undefined, next, nextActive);
  };

  const currentPageId = activePageId ?? pages[0]?.id ?? null;
  const pageWidgets = currentPageId ? widgetsForPage(layout, currentPageId) : layout;

  // Reports which widgets rendered a snapshot on the CURRENT page — Export
  // reads them back in this same on-screen order, and drops any not yet ready
  // (a very fresh dashboard where a widget hasn't finished its first fetch).
  const onWidgetExportReady = (id: string, snapshot: WidgetExportSnapshot) => {
    exportSnapshotsRef.current.set(id, snapshot);
    const snapshots = pageWidgets.map(w => exportSnapshotsRef.current.get(w.id));
    const hasFailed = snapshots.some(s => s?.kind === 'skip' && s.reason === 'This widget failed to load.');
    const hasLoading = snapshots.some(s => !s || (s.kind === 'skip' && s.reason === 'Not ready to export yet.'));
    const readiness: ExportReadiness = hasFailed ? 'failed' : hasLoading ? 'loading' : 'ready';
    if (readiness !== exportReadinessRef.current) {
      exportReadinessRef.current = readiness;
      setExportReadiness(readiness);
    }
  };

  // CSV/Excel reuse Unified Reporting's builders (flank#4458 — same formats,
  // same in-page behavior, no separate tool to open). Only 'table' and
  // 'metrics' snapshots have real rows; a chart has no tabular shape to export
  // and a text/callout widget isn't data, so both are dropped rather than
  // forced into a row.
  const tabularExportSections = (): ExportSection[] => {
    const raw = pageWidgets
      .map(w => exportSnapshotsRef.current.get(w.id))
      .filter((s): s is WidgetExportSnapshot => s != null)
      .flatMap(s => {
        if (s.kind === 'table') {
          const rows = s.table.rows.map((row, i) => preferRawValues(row, s.table.rawRows?.[i]));
          return [{ key: s.id, label: s.title, rows }];
        }
        if (s.kind === 'metrics' && s.metrics.length) {
          const row: Record<string, string | number | null> = {};
          for (const m of s.metrics) row[m.label] = m.raw !== undefined ? m.raw : m.value;
          return [{ key: s.id, label: s.title, rows: [row] }];
        }
        return [];
      });
    // buildSpreadsheetXml uses each section's label as its worksheet name,
    // which Excel requires unique — Unified Reporting's fixed EXPORT_SECTIONS
    // never collide, but widget titles here are user-edited and two widgets
    // can share a default or renamed title (CodeRabbit).
    const labels = uniqueExportLabels(raw.map(s => s.label));
    return raw.map((s, i) => ({ ...s, label: labels[i] }));
  };

  // Same slug rule as pptxFileName, just not tied to one extension.
  const dashboardFileSlug = (): string =>
    (name || 'dashboard').replace(/[^a-z0-9]+/gi, '-').replace(/^-+|-+$/g, '') || 'dashboard';

  // One Export dialog (flank#5061) replaces the three separate CSV/Excel/PPTX
  // buttons that used to call these directly — `widgetIds === null` means
  // "current view" (every widget on the page, same as before this ticket);
  // a non-null array is the dialog's "choose widgets" selection.
  //
  // PDF reuses the same window.print() + print-media-query mechanism as
  // Unified Reporting's own PDF export (`ExportDialog.tsx`/`ReportingV2Header.tsx`
  // — there's no separate PDF generator anywhere in this app, both are a
  // snapshot through the browser's print pipeline). Unlike Unified Reporting's
  // dialog, PDF here always captures the current view rather than a
  // per-widget selection: that needs hiding individual non-selected widgets
  // during print, which Unified Reporting does by toggling a CSS class on
  // each section's DOM node — this page has no equivalent per-widget hook,
  // and guessing one without a way to visually check real print output in
  // this session risks a worse bug (a widget silently missing from a client's
  // PDF) than the feature gap. The dialog disables "Choose widgets" for this
  // format accordingly (DashboardExportDialog.tsx).
  const handleExport = async (format: DashboardExportFormat, widgetIds: string[] | null) => {
    if (format === 'pdf') {
      // Same two-step close-then-print as Unified Reporting's own
      // ExportDialog.tsx, but a 0ms delay only waits for the state update to
      // commit — the MUI Dialog's exit transition keeps it (and its backdrop)
      // mounted and on screen past that, so print() could still snapshot it
      // mid-fade (CodeRabbit, flank#5065). 300ms covers the Dialog's default
      // leaving-transition. Closing explicitly rather than waiting for
      // DashboardExportDialog's own onExport-then-onClose order, since that
      // order runs window.print() before onClose ever fires.
      setExportDialogOpen(false);
      setTimeout(() => window.print(), 300);
      return;
    }
    const include = (id: string) => widgetIds === null || widgetIds.includes(id);
    if (format === 'pptx') {
      setExporting(true);
      try {
        const sections = pageWidgets
          .filter(w => include(w.id))
          .map(w => exportSnapshotsRef.current.get(w.id))
          .filter((s): s is WidgetExportSnapshot => s != null);
        const pptx = buildDashboardPptx(name || 'Dashboard', sections);
        await pptx.writeFile({ fileName: pptxFileName(name || 'dashboard') });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to export PowerPoint');
      } finally {
        setExporting(false);
      }
      return;
    }
    const sections = tabularExportSections().filter(s => include(s.key));
    if (format === 'csv') {
      const meta = [name || 'Dashboard', `Exported: ${new Date().toISOString().slice(0, 16).replace('T', ' ')}`];
      downloadFile(buildStackedCsv(sections, meta), `${dashboardFileSlug()}.csv`, 'text/csv');
    } else {
      downloadFile(buildSpreadsheetXml(sections), `${dashboardFileSlug()}.xls`, 'application/vnd.ms-excel');
    }
  };

  // Blocks every format in the dialog — loading/failed widgets.
  // tabularExportSections can't know yet whether there'll be any tabular
  // data, so this stays format-agnostic; the CSV/Excel-only "nothing tabular"
  // case below is separate, since PowerPoint can still export a chart-only
  // page these two formats can't (CodeRabbit).
  const exportDisabledReason: string =
    exportReadiness === 'loading'
      ? 'Waiting for every widget to finish loading first.'
      : exportReadiness === 'failed'
        ? 'Some widgets failed to load — export is unavailable until they succeed.'
        : '';
  const csvExcelDisabledReason: string =
    exportDisabledReason || tabularExportSections().length === 0
      ? exportDisabledReason || 'This page has no table or metric widgets to export.'
      : '';

  // ── pages ──
  const onAddPage = () => {
    const next = addPage(pages);
    setPages(next);
    setActivePageId(next[next.length - 1].id);
    persist(layout, undefined, undefined, undefined, undefined, undefined, undefined, undefined, next);
  };

  const onRenamePage = (pageId: string) => {
    const current = pages.find(p => p.id === pageId);
    setRenaming({ id: pageId, name: current?.name ?? '' });
  };

  const commitRenamePage = () => {
    if (!renaming) return;
    const next = renamePage(pages, renaming.id, renaming.name);
    setPages(next);
    setRenaming(null);
    persist(layout, undefined, undefined, undefined, undefined, undefined, undefined, undefined, next);
  };

  const onRemovePage = (pageId: string) => {
    // Widgets move to the neighbouring page rather than being deleted with it.
    const { pages: nextPages, widgets: nextWidgets, removed } = removePage(pages, layout, pageId);
    if (!removed) return;
    setPages(nextPages);
    setLayout(nextWidgets);
    if (activePageId === pageId) setActivePageId(nextPages[0].id);
    persist(nextWidgets, undefined, undefined, undefined, undefined, undefined, undefined, undefined, nextPages);
  };

  const onDragEnd = (result: DropResult) => {
    if (!result.destination || result.destination.index === result.source.index) return;
    const pageId = activePageId ?? pages[0]?.id;
    if (!pageId) return;
    // The grid renders one page, so these indices are page-relative.
    mutateLayout(prev => reorderWithinPage(prev, pageId, result.source.index, result.destination!.index));
  };

  const duplicateWidget = (widget: WidgetConfig) => {
    const copy: WidgetConfig = {
      ...widget,
      id: crypto.randomUUID(),
      title: `${widget.title} (copy)`,
    };
    mutateLayout(prev => {
      const next = [...prev];
      next.splice(prev.findIndex(w => w.id === widget.id) + 1, 0, copy);
      return next;
    });
  };

  const removeWidget = (id: string) => {
    mutateLayout(prev => prev.filter(w => w.id !== id));
  };

  const saveWidgetConfig = (id: string, patch: Partial<WidgetConfig>) => {
    setConfigWidget(null);
    mutateLayout(prev =>
      prev.map(w => {
        if (w.id !== id) return w;
        const merged = { ...w, ...patch };
        // flank#5097, CodeRabbit: ConfigPanel copies widgetFilters into its own
        // local draft on mount, before the dashboard-level strip effect (above)
        // can run — a denied DSP/Publisher override saved from that stale
        // draft would otherwise survive here even though it's already gone
        // from every other picker.
        if (!merged.widgetFilters) return merged;
        const [stripped] = stripDeniedFilterConditions(
          [merged as unknown as Record<string, unknown>],
          null,
          permissions,
        ).layout as unknown as WidgetConfig[];
        return stripped;
      }),
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !dashboard) {
    return (
      <Typography color='error' sx={{ p: 3 }}>
        {error}
      </Typography>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100%',
        // flank#5061: PDF export is window.print() (see handleExport) through
        // this page's own @media print rules — same per-element sx convention
        // already used elsewhere in this app (CreateCampaign's Summary pages,
        // Attribution), not a new global stylesheet.
        '@media print': { '@page': { margin: '10mm', size: 'landscape' } },
      }}
    >
      {/* ── Sticky header (flank#5060) ──
          Title/actions, the edit-mode toolbar and the filter bar used to be
          separate blocks that scrolled away with the page — once scrolled
          past, there was no way to see or change what a dashboard was
          filtered to without scrolling back up. One sticky group pinned to
          the top, same approach as Unified Reporting's own pinned header
          (#4626): `position: sticky` resolves against the window here too,
          the same way — this component renders inside AppLayout's `<main>`,
          which has no fixed height of its own, so nothing here needs its own
          scroll container; it must stay free of `overflow-y-auto`/`hidden`
          or sticky gets a scroll frame that never fires. Each child section
          keeps its own opaque background so content scrolling underneath
          can't show through a gap between sections. */}
      <Box sx={{ position: 'sticky', top: 0, zIndex: 11 }}>
        {/* ── Header ── */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            px: 3,
            py: 1.5,
            borderBottom: '1px solid',
            borderColor: 'divider',
            gap: 1.5,
            bgcolor: 'background.paper',
          }}
        >
          {/* Branding logo, when set */}
          {branding.logoUrl && (
            <Box
              component='img'
              src={branding.logoUrl}
              alt=''
              sx={{ height: 28, maxWidth: 120, objectFit: 'contain', mr: 1.5 }}
            />
          )}

          {/* Breadcrumb */}
          <Box sx={{ flexGrow: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
              <Typography
                variant='body2'
                sx={{
                  color: 'primary.main',
                  cursor: 'pointer',
                  '&:hover': { textDecoration: 'underline' },
                }}
                onClick={onBack}
              >
                {'< Dashboards'}
              </Typography>
              <Typography variant='body2' color='text.disabled'>
                /
              </Typography>
              <Typography variant='body2' sx={{ fontWeight: 600 }}>
                {name}
              </Typography>
              {mode === 'edit' && (
                <>
                  <Chip
                    label='Editing'
                    size='small'
                    sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600, bgcolor: '#F3F4F6' }}
                  />
                  <Typography variant='caption' color='text.secondary'>
                    {saving ? 'Saving…' : 'Autosaves on change'}
                  </Typography>
                </>
              )}
            </Box>
            {branding.headline && (
              <Typography
                variant='caption'
                sx={{
                  display: 'block',
                  mt: 0.25,
                  color: branding.accentColor ?? 'text.secondary',
                  fontWeight: 600,
                }}
              >
                {branding.headline}
              </Typography>
            )}
            {mode === 'view' && dashboard?.description && (
              <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mt: 0.25 }}>
                {dashboard.description}
              </Typography>
            )}
          </Box>

          {/* Actions — hidden on the printed/PDF page the same way the rest of
              this app hides non-printable controls (sx '@media print', e.g.
              CreateCampaign's Summary pages), not a hard-coded display:none
              that would also hide Refresh/Export from the screen. */}
          {mode === 'view' ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, '@media print': { display: 'none' } }}>
              <Button
                size='small'
                startIcon={<RefreshRounded sx={{ fontSize: 16 }} />}
                sx={{ textTransform: 'none', color: 'text.secondary' }}
                // Was persist(layout): a PUT that re-saved the dashboard and
                // changed nothing any widget effect depended on, so no widget
                // refetched. Bump the nonce the widget queries key off instead.
                onClick={() => setRefreshNonce(n => n + 1)}
              >
                Refresh
              </Button>
              {/* One Export button + dialog (flank#5061) replaces the previous
                  three separate Export PPTX/CSV/Excel buttons — format and
                  scope ("Current view" vs "Choose widgets") are picked in the
                  dialog instead of by which button was clicked. */}
              <Button
                size='small'
                disabled={exporting}
                startIcon={<FileDownloadOutlined sx={{ fontSize: 16 }} />}
                sx={{ textTransform: 'none', color: 'text.secondary' }}
                onClick={() => setExportDialogOpen(true)}
              >
                {exporting ? 'Exporting…' : 'Export'}
              </Button>
              {/* Print next to Export, as in Unified Reporting's header
                  (ReportingV2Header.tsx) — same window.print() call, no
                  dialog, relying on the same print-media rules above. */}
              <Button
                size='small'
                startIcon={<PrintOutlined sx={{ fontSize: 16 }} />}
                sx={{ textTransform: 'none', color: 'text.secondary' }}
                onClick={() => window.print()}
              >
                Print
              </Button>
              {/* Sharing is deliberately inert until row-level tenancy lands: the
                reporting query layer's applyTenancy is a documented no-op
                (#3699/B0), so any link minted today would expose every client's
                data regardless of who opened it. Disabled-with-reason rather
                than removed, so the affordance stays visible and the blocker is
                legible to anyone demoing this. */}
              <MuiTooltip title='Sharing needs client-scoped access, which is not enforced yet (#3699). Until then a link would expose all clients’ data.'>
                <span>
                  {/* aria-disabled rather than `disabled`: a truly disabled button
                    leaves the tab order, so a keyboard-only user can never focus
                    it and therefore never sees the tooltip explaining why it's
                    off. This stays focusable and inert. */}
                  <Button
                    size='small'
                    aria-disabled
                    onClick={event => event.preventDefault()}
                    startIcon={<ShareRounded sx={{ fontSize: 16 }} />}
                    sx={{
                      textTransform: 'none',
                      color: 'text.disabled',
                      cursor: 'default',
                      '&:hover': { bgcolor: 'transparent' },
                    }}
                  >
                    Share
                  </Button>
                </span>
              </MuiTooltip>
              <Button
                variant='contained'
                disableElevation
                size='small'
                startIcon={<EditOutlined sx={{ fontSize: 16 }} />}
                onClick={() => setMode('edit')}
                sx={{ textTransform: 'none' }}
              >
                Edit
              </Button>
              <IconButton size='small' onClick={e => setMoreAnchor(e.currentTarget)}>
                <MoreHorizRounded fontSize='small' />
              </IconButton>
            </Box>
          ) : (
            // No explicit Save: the header already says "Autosaves on change", and
            // offering both invites the question of what Save does differently.
            // Done persists and leaves edit mode.
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Button
                variant='contained'
                disableElevation
                size='small'
                disabled={saving}
                onClick={handleDone}
                sx={{ textTransform: 'none' }}
              >
                Done
              </Button>
              <IconButton size='small' onClick={e => setMoreAnchor(e.currentTarget)}>
                <MoreHorizRounded fontSize='small' />
              </IconButton>
            </Box>
          )}
        </Box>

        {/* ── Toolbar (edit mode only) ── */}
        {mode === 'edit' && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              px: 3,
              py: 1.5,
              borderBottom: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
            }}
          >
            <Button
              variant='contained'
              disableElevation
              size='small'
              startIcon={<AddRounded />}
              onClick={() => setPickerOpen(true)}
              sx={{ textTransform: 'none' }}
            >
              Add Widget
            </Button>
            <Chip
              label={
                <>
                  <Typography component='span' variant='caption' color='text.secondary'>
                    Client filter:{' '}
                  </Typography>
                  <Typography component='span' variant='caption' sx={{ fontWeight: 600, color: 'primary.main' }}>
                    All clients
                  </Typography>
                </>
              }
              size='small'
              variant='outlined'
              sx={{ borderRadius: 1.5, height: 28 }}
            />
          </Box>
        )}

        {/* ── Filter bar ── part of the sticky group, not the scrollable
            Canvas below — this is the piece flank#5060 actually asked to
            keep in reach while scrolling. */}
        <Box sx={{ px: 3, py: 2, bgcolor: 'background.paper', borderBottom: '1px solid', borderColor: 'divider' }}>
          <DashboardFilterBar
            filters={filters}
            dataThroughLabel={dataThroughLabel(filters.dateStart, filters.dateEnd, filters.dsps, freshnessMapForLabel)}
            filterBar={filterBar}
            onFilterBarChange={updateFilterBar}
            canManageTenant={canManageTenant}
            tenantDefaults={tenantDefaults}
            tenantError={tenantError}
            onSaveTenantDefault={saveTenantDefault}
            onResetTenantDefault={resetTenantDefault}
            views={views}
            activeViewId={activeViewId}
            customDimensions={tenantFields.dimensions}
            onChange={updateFilters}
            onSaveView={saveView}
            onSelectView={selectView}
            onResetView={resetView}
            onDeleteView={deleteView}
          />

          {error && (
            <Typography color='error' variant='body2' sx={{ mt: 1 }}>
              {error}
            </Typography>
          )}
        </Box>
      </Box>

      <Snackbar
        open={entityNotice}
        autoHideDuration={8000}
        onClose={() => setEntityNotice(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity='info' onClose={() => setEntityNotice(false)} sx={{ width: '100%' }}>
          {ENTITY_FILTERS_NOTICE}
        </Alert>
      </Snackbar>
      <Snackbar
        open={productFilterNotice}
        autoHideDuration={8000}
        onClose={() => setProductFilterNotice(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity='info' onClose={() => setProductFilterNotice(false)} sx={{ width: '100%' }}>
          {PRODUCT_FILTER_NOTICE}
        </Alert>
      </Snackbar>
      <Snackbar
        open={deniedFieldsNotice}
        autoHideDuration={8000}
        onClose={() => setDeniedFieldsNotice(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity='info' onClose={() => setDeniedFieldsNotice(false)} sx={{ width: '100%' }}>
          {DENIED_FIELDS_NOTICE}
        </Alert>
      </Snackbar>
      <Snackbar
        open={productDimensionNotice}
        autoHideDuration={8000}
        onClose={() => setProductDimensionNotice(false)}
        // Distinct from the filter notice above (CodeRabbit) — a dashboard
        // with both a saved Product filter and a Product-grouped widget
        // would otherwise open two Snackbars stacked at the same spot.
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity='info' onClose={() => setProductDimensionNotice(false)} sx={{ width: '100%' }}>
          {PRODUCT_DIMENSION_NOTICE}
        </Alert>
      </Snackbar>
      <Snackbar
        open={deniedFilterNotice}
        autoHideDuration={8000}
        onClose={() => setDeniedFilterNotice(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity='info' onClose={() => setDeniedFilterNotice(false)} sx={{ width: '100%' }}>
          {DENIED_FILTER_NOTICE}
        </Alert>
      </Snackbar>

      {/* ── Canvas ── */}
      <Box sx={{ flexGrow: 1, p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* ── Pages ──
            One tab strip per dashboard. Hidden when there's a single page and
            nothing to edit, so a simple dashboard stays simple. */}
        {(pages.length > 1 || mode === 'edit') && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={currentPageId ?? false}
              onChange={(_, v) => setActivePageId(v)}
              variant='scrollable'
              scrollButtons='auto'
              sx={{ minHeight: 40 }}
            >
              {pages.map(page => (
                <Tab key={page.id} value={page.id} label={page.name} sx={{ minHeight: 40, textTransform: 'none' }} />
              ))}
            </Tabs>
            {mode === 'edit' && currentPageId && (
              <>
                <IconButton
                  size='small'
                  aria-label='Rename page'
                  onClick={() => onRenamePage(currentPageId)}
                  sx={{ flexShrink: 0 }}
                >
                  <EditOutlined sx={{ fontSize: 16 }} />
                </IconButton>
                <IconButton
                  size='small'
                  aria-label='Delete page'
                  onClick={() => onRemovePage(currentPageId)}
                  disabled={pages.length === 1}
                  sx={{ flexShrink: 0 }}
                >
                  <CloseRounded sx={{ fontSize: 16 }} />
                </IconButton>
                <Button size='small' onClick={onAddPage} sx={{ textTransform: 'none', flexShrink: 0 }}>
                  + Add page
                </Button>
              </>
            )}
          </Box>
        )}

        {pageWidgets.length === 0 ? (
          <Box
            sx={{
              flexGrow: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              py: 10,
            }}
          >
            {mode === 'edit' ? (
              <Typography variant='body2' color='text.secondary'>
                Click + Add Widget to get started.
              </Typography>
            ) : (
              // In view mode the Add Widget button lives in the edit toolbar, so
              // a freshly created dashboard was a dead end: an empty canvas with
              // no way forward unless you knew to press Edit first.
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                <Typography variant='body2' color='text.secondary'>
                  This dashboard has no widgets yet.
                </Typography>
                <Button
                  variant='contained'
                  startIcon={<AddRounded />}
                  onClick={() => {
                    setMode('edit');
                    setPickerOpen(true);
                  }}
                >
                  Add your first widget
                </Button>
              </Box>
            )}
          </Box>
        ) : (
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId='dashboard-canvas' direction='horizontal' isDropDisabled={mode !== 'edit'}>
              {dropProvided => (
                <Box
                  ref={dropProvided.innerRef}
                  {...dropProvided.droppableProps}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
                    gap: 2,
                    alignItems: 'start',
                  }}
                >
                  {pageWidgets.map((widget, index) => (
                    <Draggable key={widget.id} draggableId={widget.id} index={index} isDragDisabled={mode !== 'edit'}>
                      {(dragProvided, snapshot) => (
                        <Box
                          ref={dragProvided.innerRef}
                          {...dragProvided.draggableProps}
                          sx={{
                            // span is capped by the widest breakpoint (3 columns)
                            gridColumn: {
                              xs: 'span 1',
                              md: `span ${Math.min(widget.span ?? 1, 2)}`,
                              lg: `span ${widget.span ?? 1}`,
                            },
                            opacity: snapshot.isDragging ? 0.85 : 1,
                            ...dragProvided.draggableProps.style,
                          }}
                        >
                          <WidgetCard
                            widget={widget}
                            editing={mode === 'edit'}
                            filters={filters}
                            accent={branding.accentColor}
                            dragHandleProps={dragProvided.dragHandleProps ?? undefined}
                            onConfigure={() => setConfigWidget(widget)}
                            onDuplicate={() => duplicateWidget(widget)}
                            onResize={span => saveWidgetConfig(widget.id, { span })}
                            onResizeRows={rowSpan => saveWidgetConfig(widget.id, { rowSpan })}
                            onConversionTypeChange={type => saveWidgetConfig(widget.id, { conversionType: type })}
                            onRemove={() => removeWidget(widget.id)}
                            refreshNonce={refreshNonce}
                            tenantFields={tenantFields}
                            onExportReady={onWidgetExportReady}
                          />
                        </Box>
                      )}
                    </Draggable>
                  ))}
                  {dropProvided.placeholder}
                </Box>
              )}
            </Droppable>
          </DragDropContext>
        )}
      </Box>

      {/* ── Widget picker drawer ── */}
      <WidgetPickerDrawer
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onPick={type => {
          setPickerOpen(false);
          addWidget(type);
        }}
      />

      {/* ── More options menu ── */}
      <Menu anchorEl={moreAnchor} open={Boolean(moreAnchor)} onClose={() => setMoreAnchor(null)}>
        <MenuItem
          onClick={() => {
            setMoreAnchor(null);
            setRenameError(null);
            setRenamingDashboard(true);
          }}
          sx={{ fontSize: '0.875rem' }}
        >
          Rename…
        </MenuItem>
        {mode === 'view' && (
          <MenuItem
            onClick={() => {
              setMoreAnchor(null);
              saveCurrentFiltersAsStartingView();
            }}
            disabled={saving}
            sx={{ fontSize: '0.875rem' }}
          >
            Save as starting view
          </MenuItem>
        )}
        {canManageTenant && (
          <MenuItem
            onClick={() => {
              setMoreAnchor(null);
              setFieldsOpen(true);
            }}
            sx={{ fontSize: '0.875rem' }}
          >
            Custom fields…
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            setMoreAnchor(null);
            setBrandingOpen(true);
          }}
          sx={{ fontSize: '0.875rem' }}
        >
          Branding…
        </MenuItem>
        <MenuItem
          onClick={() => {
            setMoreAnchor(null);
            onBack();
          }}
          sx={{ color: 'error.main', fontSize: '0.875rem' }}
        >
          Back to dashboards
        </MenuItem>
      </Menu>

      {/* ── Rename dashboard ── */}
      <RenameDashboardDialog
        open={renamingDashboard}
        initialName={name}
        saving={savingDashboardName}
        error={renameError}
        onCancel={() => setRenamingDashboard(false)}
        onSave={renameDashboard}
      />

      {/* ── Rename page ── */}
      <Dialog open={!!renaming} onClose={() => setRenaming(null)} maxWidth='xs' fullWidth>
        <DialogTitle>Rename page</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            size='small'
            label='Page name'
            sx={{ mt: 1 }}
            value={renaming?.name ?? ''}
            onChange={e => setRenaming(prev => (prev ? { ...prev, name: e.target.value } : prev))}
            onKeyDown={e => {
              if (e.key === 'Enter') commitRenamePage();
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button color='inherit' onClick={() => setRenaming(null)}>
            Cancel
          </Button>
          <Button variant='contained' onClick={commitRenamePage} disabled={!renaming?.name.trim()}>
            Rename
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Export dialog (flank#5061) ── */}
      <DashboardExportDialog
        open={exportDialogOpen}
        onClose={() => setExportDialogOpen(false)}
        widgets={pageWidgets.map(w => ({
          id: w.id,
          // Same fallback as the export-snapshot title above — an untitled
          // widget still needs something tickable in "Choose widgets".
          title: w.title || WIDGET_TYPES.find(t => t.type === w.type)?.label || w.type,
        }))}
        exporting={exporting}
        disabledReason={exportDisabledReason}
        csvExcelDisabledReason={csvExcelDisabledReason}
        onExport={handleExport}
      />

      {/* ── Custom fields dialog ── */}
      <TenantFieldsDialog
        open={fieldsOpen}
        onClose={() => {
          setFieldsOpen(false);
          // One save when the dialog closes. Saving per change issued a PUT per
          // keystroke in "Rename fields", and concurrent PUTs are not ordered,
          // so a slow early request could overwrite the final value.
          persist(layout, undefined, undefined, undefined, undefined, undefined, undefined, tenantFields);
        }}
        fields={tenantFields}
        onChange={setTenantFields}
        // flank#5101: unlike `dimensionEntries` just above (the widget editor's
        // own Group-by picker), this list went straight through unfiltered —
        // DSP was offered as a custom-dimension bucket field to a viewer
        // without "Show DSP / Platform". Same filter, applied here too.
        dimensionOptions={Object.keys(TABLE_DIMENSIONS)
          .map(d => ({ value: TABLE_DIMENSIONS[d], label: d }))
          .filter(opt => permissions.canViewPlatform || opt.value !== 'dsp')}
        allowedMetricKeys={allowedMetricKeys}
      />

      {/* ── Branding dialog ── */}
      <BrandingDialog
        open={brandingOpen}
        branding={branding}
        onClose={() => setBrandingOpen(false)}
        onSave={next => {
          setBranding(next);
          setBrandingOpen(false);
          // Saving branding doesn't mean the starting filters — leave
          // `nextFilters` unset (CodeRabbit, flank#5065), same reasoning as
          // saveView/deleteView above.
          persist(layout, undefined, undefined, views, undefined, next);
        }}
      />

      {/* ── Config panel (right drawer) ── */}
      <Drawer
        anchor='right'
        open={Boolean(configWidget)}
        onClose={() => setConfigWidget(null)}
        slotProps={{ paper: { sx: { width: 340 } } }}
      >
        {configWidget && (
          <ConfigPanel
            widget={configWidget}
            onClose={() => setConfigWidget(null)}
            dashboardFilters={filters}
            filterBar={filterBar}
            tenantFields={tenantFields}
            onSave={patch => saveWidgetConfig(configWidget.id, patch)}
          />
        )}
      </Drawer>
    </Box>
  );
};

export default DashboardBuilder;
